import { makeObservable, observable, computed, action } from 'mobx';

import * as JSFUNC from "../../Library/JSFUNC.js";
import * as JSPHP from "../../CaptureExecLocalDatabaseMobx/JSPHP.js";
import * as CEC from "../../CaptureExecGeneral/CEC.js";

import DatabaseMobx from '../../CaptureExecLocalDatabaseMobx/DatabaseMobx.js';
import UserMobx from '../../CaptureExecLocalDatabaseMobx/UserMobx.js';

import CaptureExecMobx from '../CaptureExec/CaptureExecMobx.js';
import CapturesMobx from '../Captures/CapturesMobx.js';
import TeammateContractsMobx from '../TeammateContracts/TeammateContractsMobx.js';

class ContactsMobx {
  //========================================================================================
  //observable values
  //selecting contacts in floating box
  o_initialSelectedContactIDsArray = [];
  o_currentSelectedContactIDsArray = [];

  //left side divisions tree
  o_leftSideState = "treeWithSearch"; //"treeWithSearch", "advancedCompanySearch", "exportContactsToCsv", "selectParentDivision"
  o_searchFieldKey = "allFields";
  o_searchText = "";
  o_searchSelectedValuesComma = "";
  o_contactsBrowserSelectedTabDbName = "companies";
  o_contactsBrowserCompaniesPageNumber = 1;
  o_contactsBrowserPersonsPageNumber = 1;
  o_contactsBrowserSamEntitiesPageNumber = 1;
  o_samGovEntitySearchInputText = "";
  o_samGovEntitiesSearchResultsArrayOfObjs = [];
  o_samGovEntitiesSearchState = "idle"; //"idle", "loading", "loaded", "error"
  o_samGovEntitiesSearchErrorMessageOrUndefined = undefined;
  o_selectedSamGovEntityUEIOrUndefined = undefined;
  o_orgsExpandedCompanyIDsArray = [];
  o_leftSideNumOrgsDrawnLimit = 50;
  o_leftSideZoomedCompanyID = undefined; //undefined - show all orgs collapsed in tree (default view), single org id - this org has every division expanded all other orgs are filtered out completely

  o_advSearchCompanyFilterSelectedBusinessTypeIDsComma = "";
  o_advSearchCompanyFilterSelectedSBCertificationIDsComma = "";
  o_advSearchCompanyFilterSelectedCapabilityIDsComma = "";
  o_advSearchCompanyFilterSelectedNaicsCodeIDsComma = "";
  o_advSearchCompanyFilterSelectedContactCompanyExtraFieldID = -1;
  o_advSearchCompanyFilterContactCompanyExtraFieldSearchText = "";
  o_advSearchCaptureFilterSelectedDateFieldID = -1;
  o_advSearchCaptureFilterSelectedEarliestDate = JSFUNC.blank_date();
  o_advSearchNumResultsDrawnLimit = 50;
  o_advSearchGeneratingCsvOfMatchingCompaniesDataTF = false;

  //shared teammate and competitor analysis graph controls
  o_contactAnalysisYLogScaleTF = false;
  o_contactAnalysisGraphType = "line";
  o_contactAnalysisTimeBins = "yearly";
  o_contactAnalysisTeammateGraphValueType = "allocatedRevenueValue";

  //right side single open contact/new contact
  o_rightSideState = "empty"; //"empty", "viewEditSingleContact", "createNewContact", "savingNewContact", "advancedSearchCapturesAnalysis"
  o_openContactID = undefined;
  o_openContactIsPersonTF = undefined;
  o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = undefined;
  o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = undefined;
  o_openContactCompetitorAnalysisLoadState = "notLoaded"; //"notLoaded", "loading", "loaded", "error"
  o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = undefined;
  o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = undefined;
  o_openContactCompetitorAnalysisErrorMessageOrUndefined = undefined;
  o_openContactSubtabLoadingFromDatabaseMessageOrUndefined = undefined;
  o_openContactTeammatesRatingsSubmissionsArrayOfObjs = [];
  o_openContactPersonConversationsArrayOfObjs = [];
  o_openContactPersonConversationsFilterByCaptureNameText = "";
  o_openContactPersonConversationsFilterByDateMin = JSFUNC.blank_date();
  o_openContactPersonConversationsFilterByDateMax = JSFUNC.blank_date();
  o_openContactPersonConversationsFilterByNotesText = "";
  o_openContactLoadingDocsTF = undefined;
  o_openContactViewEditSingleContactSelectedTabDbName = "contactInfo"; //"contactInfo", "teammateAnalysis", "contactConversations", "viewDocs"
  o_newContactObj = undefined;
  o_advSearchTeamingCategory = undefined; //"theirAsOurWins", "theirAsOurActive", "theirAsOurLosses", "usAsTheirWins", "usAsTheirActive", "usAsTheirLosses"
  o_advSearchCaptureIDsArray = [];

  constructor() {
    makeObservable(this, {
      o_initialSelectedContactIDsArray: observable,
      o_currentSelectedContactIDsArray: observable,
      o_leftSideState: observable,
      o_searchFieldKey: observable,
      o_searchText: observable,
      o_searchSelectedValuesComma: observable,
      o_contactsBrowserSelectedTabDbName: observable,
      o_contactsBrowserCompaniesPageNumber: observable,
      o_contactsBrowserPersonsPageNumber: observable,
      o_contactsBrowserSamEntitiesPageNumber: observable,
      o_samGovEntitySearchInputText: observable,
      o_samGovEntitiesSearchResultsArrayOfObjs: observable,
      o_samGovEntitiesSearchState: observable,
      o_samGovEntitiesSearchErrorMessageOrUndefined: observable,
      o_selectedSamGovEntityUEIOrUndefined: observable,
      o_orgsExpandedCompanyIDsArray: observable,
      o_leftSideNumOrgsDrawnLimit: observable,
      o_leftSideZoomedCompanyID: observable,
      o_advSearchCompanyFilterSelectedBusinessTypeIDsComma: observable,
      o_advSearchCompanyFilterSelectedSBCertificationIDsComma: observable,
      o_advSearchCompanyFilterSelectedCapabilityIDsComma: observable,
      o_advSearchCompanyFilterSelectedNaicsCodeIDsComma: observable,
      o_advSearchCompanyFilterSelectedContactCompanyExtraFieldID: observable,
      o_advSearchCompanyFilterContactCompanyExtraFieldSearchText: observable,
      o_advSearchCaptureFilterSelectedDateFieldID: observable,
      o_advSearchCaptureFilterSelectedEarliestDate: observable,
      o_advSearchNumResultsDrawnLimit: observable,
      o_contactAnalysisYLogScaleTF: observable,
      o_contactAnalysisGraphType: observable,
      o_contactAnalysisTimeBins: observable,
      o_contactAnalysisTeammateGraphValueType: observable,
      o_rightSideState: observable,
      o_openContactID: observable,
      o_openContactIsPersonTF: observable,
      o_openContactTeammateRatingsLoadedCompanyIDOrUndefined: observable,
      o_openContactTeammateRatingsLoadingCompanyIDOrUndefined: observable,
      o_openContactCompetitorAnalysisLoadState: observable,
      o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined: observable,
      o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined: observable,
      o_openContactCompetitorAnalysisErrorMessageOrUndefined: observable,
      o_openContactSubtabLoadingFromDatabaseMessageOrUndefined: observable,
      o_openContactTeammatesRatingsSubmissionsArrayOfObjs: observable,
      o_openContactPersonConversationsArrayOfObjs: observable,
      o_openContactPersonConversationsFilterByCaptureNameText: observable,
      o_openContactPersonConversationsFilterByDateMin: observable,
      o_openContactPersonConversationsFilterByDateMax: observable,
      o_openContactPersonConversationsFilterByNotesText: observable,
      o_openContactLoadingDocsTF: observable,
      o_openContactViewEditSingleContactSelectedTabDbName: observable,
      o_newContactObj: observable,
      o_advSearchTeamingCategory: observable,
      o_advSearchCaptureIDsArray: observable,

      c_contactCompaniesPrecomputedInfoObj: computed,
      c_allCompaniesArrayOfObjs: computed,
      c_allCompaniesSelectArrayOfObjs: computed,
      c_allPersonsArrayOfObjs: computed,
      c_allCompanyIDsArray: computed,
      c_allOrgsArrayOfObjs: computed,
      c_createdByGcssImportContactPersonsArrayOfObjs: computed,
      c_unassignedPersonsArrayOfObjs: computed,
      c_numUnassignedPersons: computed,
      c_companyFieldsArrayOfObjs: computed,
      c_personFieldsArrayOfObjs: computed,
      c_searchFieldsArrayOfObjs: computed,
      c_searchFieldsFieldTypeObj: computed,
      c_selectedSearchFieldObj: computed,
      c_selectedSearchValuesFieldTypeObjOrUndefined: computed,
      c_contactsBrowserSearchIsActiveTF: computed,
      c_selectMultiContactsFloatingBoxIsSplitScreenTF: computed,
      c_searchResultsCompaniesArrayOfObjs: computed,
      c_searchResultsPersonsArrayOfObjs: computed,
      c_selectedSamGovEntityObjOrUndefined: computed,
      c_samGovEntityFieldsArrayOfObjs: computed,
      c_samGovEntityContactGroupsArrayOfObjs: computed,
      c_samGovEntityFieldsMetadataArrayOfObjs: computed,
      c_samGovEntityContactCompanyDuplicateCandidatesArrayOfObjs: computed,
      c_contactsBrowserSelectedArrayOfObjs: computed,
      c_contactsBrowserPaginatedSelectedArrayOfObjs: computed,
      c_contactsBrowserItemsPerPage: computed,
      c_contactsBrowserCurrentPageNumber: computed,
      c_contactsBrowserTotalNumItems: computed,
      c_contactsBrowserTotalNumPages: computed,
      c_contactsBrowserCurrentPageFirstItemNumber: computed,
      c_contactsBrowserCurrentPageLastItemNumber: computed,
      c_contactsBrowserCanIncrementPageTF: computed,
      c_contactsBrowserCanDecrementPageTF: computed,
      c_contactsBrowserItemsPerPageFieldTypeObj: computed,
      c_openContactObj: computed,
      c_openContactName: computed,
      c_openContactFieldsArrayOfObjs: computed,
      c_openContactAllChildrenIDsArray: computed,
      c_openContactCompanyTeammateCaptureIDsArray: computed,
      c_openContactCompanyCompetitorCaptureIDsArray: computed,
      c_openContactCompanyTeammateArchivedCaptureIDsArray: computed,
      c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs: computed,
      c_openContactCompanyTeammateRatingsTimeGraphObj: computed,
      c_openContactCompanyCompetitorAnalysisCapturesArrayOfObjs: computed,
      c_openContactCompanyCompetitorAnalysisTimeGraphObj: computed,
      c_openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs: computed,
      c_openContactCompanyTeammatesRatingsCapturesNotReviewedArrayOfObjs: computed,
      c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs: computed,
      c_openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs: computed,
      c_openContactPersonNumFilteredConversations: computed,
      c_openContactCompanyFileFolderSystemMapOfMaps: computed,
      c_openContactCompanyFileFolderSystemNumFiles: computed,
      c_openContactPersonFileFolderSystemMapOfMaps: computed,
      c_openContactPersonFileFolderSystemNumFiles: computed,
      c_advSearchExpandedSelectedTeammatesArrayOfObjs: computed,
      c_advSearchEarliestDateFilteredCapturesArrayOfObjs: computed,
      c_advSearchEarliestDateFilteredCaptureIDsArray: computed,
      c_advSearchAllContactCompaniesArrayOfObjs: computed,
      c_advSearchBusinessTypeFilterIsActiveTF: computed,
      c_advSearchSBCertificationsFilterIsActiveTF: computed,
      c_advSearchCapabilitiesFilterIsActiveTF: computed,
      c_advSearchNaicsCodesFilterIsActiveTF: computed,
      c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF: computed,
      c_advSearchCaptureDateFilterIsActiveTF: computed,
      c_advSearchCompanyFilterSelectedBusinessTypeIDsArray: computed,
      c_advSearchCompanyFilterSelectedSBCertificationIDsArray: computed,
      c_advSearchCompanyFilterSelectedCapabilityIDsArray: computed,
      c_advSearchCompanyFilterSelectedNaicsCodeIDsArray: computed,
      c_advSearchCompanyFilterSelectedContactCompanyExtraFieldMapOrUndefined: computed,
      c_advSearchAnyFilterIsActiveTF: computed,
      c_advSearchFilteredContactCompaniesArrayOfObjs: computed,
      c_advSearchAnalysisCapturesArrayOfObjs: computed,
      c_advSearchAnalysisScatterDataObj: computed,

      a_initialize_initial_and_current_selected_contact_ids_arrays: action,
      a_initialize_contacts_system: action,
      a_set_left_side_state: action,
      a_set_right_side_state: action,
      a_set_contacts_browser_selected_tab_db_name: action,
      a_set_contacts_browser_current_page_number: action,
      a_increment_contacts_browser_current_page_number: action,
      a_decrement_contacts_browser_current_page_number: action,
      a_set_contacts_browser_items_per_page: action,
      a_set_search_field_key: action,
      a_set_search_text: action,
      a_set_search_selected_values_comma: action,
      a_set_sam_gov_entity_search_input_text: action,
      a_search_sam_gov_entities: action,
      a_set_selected_sam_gov_entity_uei: action,
      a_insert_sam_gov_entity_into_contacts: action,
      a_set_contact_analysis_y_log_scale_tf: action,
      a_set_contact_analysis_graph_type: action,
      a_set_contact_analysis_time_bins: action,
      a_set_contact_analysis_teammate_graph_value_type: action,
      a_expand_collapse_company: action,
      a_set_left_side_num_orgs_drawn_to_unlimited: action,
      a_set_left_side_zoomed_org_id_from_is_person_tf_and_contact_id: action,
      a_set_left_side_zoomed_org_id_from_company_id: action,
      a_set_left_side_zoomed_org_id_from_person_id: action,
      a_set_left_side_zoomed_org_id_from_org_id: action,
      a_open_contact: action,
      a_close_contact: action,
      a_load_open_contact_teammate_ratings: action,
      a_load_open_contact_competitor_analysis: action,
      a_set_open_contact_subtab_loading_from_database_message_or_undefined: action,
      a_set_open_contact_teammates_ratings_submissions_arrayOfObjs: action,
      a_contact_person_conversations_load_all_person_conversation_history: action,
      a_set_open_contact_person_conversations_arrayOfObjs: action,
      a_set_open_contact_person_conversations_filter_by_capture_name_text: action,
      a_set_open_contact_person_conversations_filter_by_date_min: action,
      a_set_open_contact_person_conversations_filter_by_date_max: action,
      a_set_open_contact_person_conversations_filter_by_notes_text: action,
      a_set_open_contact_loading_docs_tf: action,
      a_set_open_contact_view_edit_single_contact_selected_tab_db_name: action,
      a_set_selected_contact_ids_array: action,
      a_select_contact: action,
      a_remove_contact_from_selection: action,
      a_delete_contact: action,
      a_update_contact_field: action,
      a_update_contact_parent_company_field: action,
      a_insert_new_contact_from_contacts_system_creation_and_open_on_success: action,
      a_insert_new_contact_from_new_contact_obj: action,
      a_initialize_new_contact: action,
      a_update_new_contact_field_local: action,
      a_update_new_contact_parent_company_field_local: action,
      a_export_contacts_to_csv_for_download: action,
      a_open_contacts_advanced_search_left_side: action,
      a_contacts_advanced_search_set_company_filter_selected_business_type_ids_comma: action,
      a_contacts_advanced_search_set_company_filter_selected_sb_certifications_bm_set_aside_ids_comma: action,
      a_contacts_advanced_search_set_company_filter_selected_capability_ids_comma: action,
      a_contacts_advanced_search_set_company_filter_selected_naics_code_ids_comma: action,
      a_contacts_advanced_search_set_company_filter_selected_contact_company_extra_field_id: action,
      a_contacts_advanced_search_set_company_filter_contact_company_extra_field_search_text: action,
      a_contacts_advanced_search_set_capture_filter_selected_date_field_id: action,
      a_contacts_advanced_search_set_capture_filter_selected_earliest_date: action,
      a_contacts_advanced_search_set_num_results_drawn_to_initial_value: action,
      a_contacts_advanced_search_set_num_results_drawn_to_unlimited: action,
      a_contacts_advanced_search_set_generating_csv_of_matching_companies_data_tf: action,
      a_contacts_advanced_search_download_csv_of_matching_companies_data: action,
      a_contacts_advanced_search_open_captures_analysis: action
    });
  }


  //========================================================================================
  //contacts system computed values
  get c_contactCompaniesPrecomputedInfoObj() {
    const o_tbl_g_contacts_companies = DatabaseMobx.o_tbl_g_contacts_companies;
    const o_tbl_g_contacts_persons = DatabaseMobx.o_tbl_g_contacts_persons;

    const allCompanyIDsArray = Array.from(o_tbl_g_contacts_companies.keys());
    const allCompanyIDsSet = new Set(allCompanyIDsArray);
    const companyPrecomputedMap = new Map();
    const numOrgDivisionsByOrgIDMap = new Map();
    const numPersonsByOrgIDMap = new Map();

    for(let [contactCompanyID, contactCompanyMap] of o_tbl_g_contacts_companies) {
      const orgTopDivIDRaw = contactCompanyMap.get("org_topdiv_id");
      const legalName = contactCompanyMap.get("legal_name");
      const abbrName = contactCompanyMap.get("abbreviated_name");

      companyPrecomputedMap.set(contactCompanyID, {
        orgTopDivIDRaw: orgTopDivIDRaw,
        legalName: legalName,
        abbrName: abbrName,
        legalNameLower: JSFUNC.str2lower(legalName, "--Legal Name not a Contact Field--"),
        abbrNameLower: JSFUNC.str2lower(abbrName, "--Abbr Name not a Contact Field--"),
        abbrOrLegalNameBase: this.contact_company_abbr_name_or_legal_name_from_abbr_name_and_legal_name(abbrName, legalName)
      });
    }

    for(let [contactCompanyID, precomputedCompanyObj] of companyPrecomputedMap) {
      let orgTopDivID = precomputedCompanyObj.orgTopDivIDRaw;
      let abbrOrLegalName = precomputedCompanyObj.abbrOrLegalNameBase;
      let isTopLevelCompanyTF = (orgTopDivID <= 0);

      if(!isTopLevelCompanyTF && !allCompanyIDsSet.has(orgTopDivID)) {
        abbrOrLegalName += " {--Parent Company [ID: " + orgTopDivID + "] Does Not Exist--}";
        orgTopDivID = -1;
        isTopLevelCompanyTF = true;
      }

      const organizationID = (isTopLevelCompanyTF) ? (contactCompanyID) : (orgTopDivID);
      const organizationPrecomputedCompanyObj = companyPrecomputedMap.get(organizationID);

      let organizationAbbrOrLegalName = abbrOrLegalName;
      let divNameParenOrgName = JSFUNC.full_name_from_first_name_last_name(precomputedCompanyObj.abbrName, precomputedCompanyObj.legalName);
      if(!isTopLevelCompanyTF && (organizationPrecomputedCompanyObj !== undefined)) {
        organizationAbbrOrLegalName = organizationPrecomputedCompanyObj.abbrOrLegalNameBase;
        divNameParenOrgName += " (" + JSFUNC.full_name_from_first_name_last_name(organizationPrecomputedCompanyObj.abbrName, organizationPrecomputedCompanyObj.legalName) + ")";
      }

      precomputedCompanyObj.orgTopDivID = orgTopDivID;
      precomputedCompanyObj.isTopLevelCompanyTF = isTopLevelCompanyTF;
      precomputedCompanyObj.organizationID = organizationID;
      precomputedCompanyObj.organizationAbbrOrLegalName = organizationAbbrOrLegalName;
      precomputedCompanyObj.divNameParenOrgNameLower = divNameParenOrgName.toLowerCase();
      precomputedCompanyObj.abbrOrLegalName = abbrOrLegalName;

      if(isTopLevelCompanyTF) {
        numOrgDivisionsByOrgIDMap.set(contactCompanyID, 0);
        numPersonsByOrgIDMap.set(contactCompanyID, 0);
      }
      else {
        numOrgDivisionsByOrgIDMap.set(organizationID, (numOrgDivisionsByOrgIDMap.get(organizationID) || 0) + 1);
      }
    }

    for(let contactPersonMap of o_tbl_g_contacts_persons.values()) {
      const contactCompanyID = contactPersonMap.get("contact_company_id");
      const precomputedCompanyObj = companyPrecomputedMap.get(contactCompanyID);
      if(precomputedCompanyObj !== undefined) {
        const organizationID = precomputedCompanyObj.organizationID;
        numPersonsByOrgIDMap.set(organizationID, (numPersonsByOrgIDMap.get(organizationID) || 0) + 1);
      }
    }

    for(let [contactCompanyID, precomputedCompanyObj] of companyPrecomputedMap) {
      if(precomputedCompanyObj.isTopLevelCompanyTF) {
        precomputedCompanyObj.numOrgDivisions = numOrgDivisionsByOrgIDMap.get(contactCompanyID) || 0;
        precomputedCompanyObj.numPersons = numPersonsByOrgIDMap.get(contactCompanyID) || 0;
      }
      else {
        precomputedCompanyObj.numOrgDivisions = -1;
        precomputedCompanyObj.numPersons = -1;
      }
    }

    return({
      allCompanyIDsArray: allCompanyIDsArray,
      companyPrecomputedMap: companyPrecomputedMap
    });
  }

  get c_allCompaniesArrayOfObjs() {
    const o_tbl_g_contacts_companies = DatabaseMobx.o_tbl_g_contacts_companies;
    const c_contactCompaniesPrecomputedInfoObj = this.c_contactCompaniesPrecomputedInfoObj;

    const subdivsAndPersonsTF = true;

    var allCompaniesArrayOfObjs = [];
    for(let contactCompanyMap of o_tbl_g_contacts_companies.values()) {
      const contactCompanyID = contactCompanyMap.get("id");
      const precomputedCompanyObj = c_contactCompaniesPrecomputedInfoObj.companyPrecomputedMap.get(contactCompanyID);
      var contactCompanyObj = this.contact_company_obj_from_tbl_g_contacts_companies_row_map(contactCompanyMap, subdivsAndPersonsTF, contactCompanyID, precomputedCompanyObj);
      allCompaniesArrayOfObjs.push(contactCompanyObj);
    }

    //sort all contacts by name asc
    const contactNameField = this.contact_name_field(false);
    JSFUNC.sort_arrayOfObjs(allCompaniesArrayOfObjs, contactNameField, true);

    return(allCompaniesArrayOfObjs);
  }

  get c_allCompaniesSelectArrayOfObjs() {
    const o_tbl_g_contacts_companies = DatabaseMobx.o_tbl_g_contacts_companies;

    var allCompaniesSelectArrayOfObjs = [];
    for(let contactCompanyMap of o_tbl_g_contacts_companies.values()) {
      allCompaniesSelectArrayOfObjs.push({
        id: contactCompanyMap.get("id"),
        abbrOrLegalName: this.contact_company_abbr_name_or_legal_name_from_abbr_name_and_legal_name(contactCompanyMap.get("abbreviated_name"), contactCompanyMap.get("legal_name"))
      });
    }

    JSFUNC.sort_arrayOfObjs(allCompaniesSelectArrayOfObjs, "abbrOrLegalName", true);

    return(allCompaniesSelectArrayOfObjs);
  }

  get c_allPersonsArrayOfObjs() {
    const o_tbl_g_contacts_persons = DatabaseMobx.o_tbl_g_contacts_persons;
    const c_contactCompaniesPrecomputedInfoObj = this.c_contactCompaniesPrecomputedInfoObj;

    var allPersonsArrayOfObjs = [];
    for(let contactPersonMap of o_tbl_g_contacts_persons.values()) {
      var contactPersonObj = this.contact_person_obj_from_tbl_g_contacts_persons_row_map(contactPersonMap, contactPersonMap.get("id"), c_contactCompaniesPrecomputedInfoObj);
      allPersonsArrayOfObjs.push(contactPersonObj);
    }

    //sort all contacts by lastname asc
    JSFUNC.sort_arrayOfObjs(allPersonsArrayOfObjs, "sortName", true);

    return(allPersonsArrayOfObjs);
  }

  get c_allCompanyIDsArray() {
    return(this.c_contactCompaniesPrecomputedInfoObj.allCompanyIDsArray);
  }

  get c_allOrgsArrayOfObjs() { //top division of each organization has the field org_topdiv_id set as -1, all subdivisions point to their parent company using this field to reference the id number
    const c_allCompaniesArrayOfObjs = this.c_allCompaniesArrayOfObjs;

    const filterFunction = (i_contactCompanyObj) => { return(i_contactCompanyObj.org_topdiv_id === -1); };
    const sortFunction = JSFUNC.sort_by_asc("divNameParenOrgNameLower"); //list the organizations alphabetically
    return(c_allCompaniesArrayOfObjs.filter(filterFunction).sort(sortFunction));
  }

  get c_createdByGcssImportContactPersonsArrayOfObjs() { //all persons with either no contact company assigned, or the id assigned does not exist
    const c_allPersonsArrayOfObjs = this.c_allPersonsArrayOfObjs;

    const filterFunction = (i_personObj) => { return(i_personObj.contact_company_id === -2); }; //-2 parent companyID for a person means it is a GCSS Import
    const sortFunction = JSFUNC.sort_by_asc("sortName");
    return(c_allPersonsArrayOfObjs.filter(filterFunction).sort(sortFunction));
  }

  get c_unassignedPersonsArrayOfObjs() { //all persons with either no contact company assigned, or the id assigned does not exist
    const c_allPersonsArrayOfObjs = this.c_allPersonsArrayOfObjs;
    const c_allCompanyIDsArray = this.c_allCompanyIDsArray;
    
    const filterFunction = (i_personObj) => { return(!JSFUNC.in_array(i_personObj.contact_company_id, c_allCompanyIDsArray) && (i_personObj.contact_company_id !== -2)); }; //-2 parent companyID for a person means it is a GCSS Import
    const sortFunction = JSFUNC.sort_by_asc("sortName");
    return(c_allPersonsArrayOfObjs.filter(filterFunction).sort(sortFunction));
  }

  get c_numUnassignedPersons() {
    return(this.c_unassignedPersonsArrayOfObjs.length);
  }

  get c_companyFieldsArrayOfObjs() {
    return(DatabaseMobx.c_contactCompanyFieldsArrayOfObjs);
  }

  get c_personFieldsArrayOfObjs() {
    return(DatabaseMobx.c_contactPersonFieldsArrayOfObjs);
  }

  get c_searchFieldsArrayOfObjs() {
    const searchFieldsArrayOfObjs = [
      {key:"allFields", displayName:"<All Fields>", appliesToPersonTF:true, appliesToCompanyTF:true, fieldTypeObj:DatabaseMobx.c_genericTextFieldTypeObj},
      {key:"name", displayName:"Name", appliesToPersonTF:true, appliesToCompanyTF:true, fieldTypeObj:DatabaseMobx.c_genericTextFieldTypeObj}
    ];
    const excludedDbNamesArray = ["id", "tree_id"];
    for(let companyFieldObj of this.c_companyFieldsArrayOfObjs) {
      if(!JSFUNC.in_array(companyFieldObj.db_name, excludedDbNamesArray)) {
        searchFieldsArrayOfObjs.push({key:"company|" + companyFieldObj.db_name, displayName:"Company - " + companyFieldObj.display_name, appliesToPersonTF:false, appliesToCompanyTF:true, db_name:companyFieldObj.db_name, fieldTypeObj:companyFieldObj.fieldTypeObj});
      }
    }
    for(let personFieldObj of this.c_personFieldsArrayOfObjs) {
      if(!JSFUNC.in_array(personFieldObj.db_name, excludedDbNamesArray)) {
        searchFieldsArrayOfObjs.push({key:"person|" + personFieldObj.db_name, displayName:"Person - " + personFieldObj.display_name, appliesToPersonTF:true, appliesToCompanyTF:false, db_name:personFieldObj.db_name, fieldTypeObj:personFieldObj.fieldTypeObj});
      }
    }
    return(searchFieldsArrayOfObjs);
  }

  get c_searchFieldsFieldTypeObj() {
    const c_searchFieldsArrayOfObjs = this.c_searchFieldsArrayOfObjs;
    const valueArray = JSFUNC.get_column_vector_from_arrayOfObjs(c_searchFieldsArrayOfObjs, "key");
    const displayArray = JSFUNC.get_column_vector_from_arrayOfObjs(c_searchFieldsArrayOfObjs, "displayName");
    const selectWithSearchDataObj = DatabaseMobx.create_sws_data_obj_from_value_array_and_display_array("Contact Field", valueArray, true, displayArray, {hasSearchTF:true});
    return(DatabaseMobx.create_field_type_obj("select", selectWithSearchDataObj));
  }

  get c_selectedSearchFieldObj() {
    return(JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(this.c_searchFieldsArrayOfObjs, "key", this.o_searchFieldKey));
  }

  get c_selectedSearchValuesFieldTypeObjOrUndefined() {
    const c_selectedSearchFieldObj = this.c_selectedSearchFieldObj;
    if((c_selectedSearchFieldObj === undefined) || !c_selectedSearchFieldObj.fieldTypeObj.selectTF) {
      return(undefined);
    }
    return(DatabaseMobx.create_new_copy_field_type_obj_from_old_field_type_obj_and_field_input_type_and_sws_options_obj(c_selectedSearchFieldObj.fieldTypeObj, "select", {isMultiSelectTF:true, hasSearchTF:true, hasClearSelectionTF:true}));
  }

  get c_contactsBrowserSearchIsActiveTF() {
    const c_selectedSearchValuesFieldTypeObjOrUndefined = this.c_selectedSearchValuesFieldTypeObjOrUndefined;
    return((c_selectedSearchValuesFieldTypeObjOrUndefined === undefined) ? (this.o_searchText.length > 0) : (this.o_searchSelectedValuesComma.length > 0));
  }


  get c_selectMultiContactsFloatingBoxIsSplitScreenTF() {
    return(!CaptureExecMobx.c_isMobileOrTabletTF);
  }


  get c_searchResultsCompaniesArrayOfObjs() {
    const o_searchFieldKey = this.o_searchFieldKey;
    const o_searchText = this.o_searchText;
    const o_searchSelectedValuesComma = this.o_searchSelectedValuesComma;
    const c_allCompaniesArrayOfObjs = this.c_allCompaniesArrayOfObjs;
    const c_selectedSearchFieldObj = this.c_selectedSearchFieldObj;

    if(c_selectedSearchFieldObj === undefined || !c_selectedSearchFieldObj.appliesToCompanyTF) {
      return([]);
    }

    if(o_searchFieldKey === "allFields") {
      return(this.contacts_arrayOfObjs_matching_text_in_contact_fields(c_allCompaniesArrayOfObjs, this.c_companyFieldsArrayOfObjs, o_searchText));
    }

    if(o_searchFieldKey === "name") {
      if(o_searchText.length === 0) {
        return([]);
      }
      const searchTextLower = o_searchText.toLowerCase();
      return(c_allCompaniesArrayOfObjs.filter((i_companyObj) => ((i_companyObj.legalNameLower.indexOf(searchTextLower) !== -1) || (i_companyObj.abbrNameLower.indexOf(searchTextLower) !== -1))));
    }

    return(this.filtered_contacts_arrayOfObjs_from_search_field_and_values(c_allCompaniesArrayOfObjs, c_selectedSearchFieldObj, o_searchText, o_searchSelectedValuesComma));
  }

  get c_searchResultsPersonsArrayOfObjs() {
    const o_searchFieldKey = this.o_searchFieldKey;
    const o_searchText = this.o_searchText;
    const o_searchSelectedValuesComma = this.o_searchSelectedValuesComma;
    const c_allPersonsArrayOfObjs = this.c_allPersonsArrayOfObjs;
    const c_selectedSearchFieldObj = this.c_selectedSearchFieldObj;

    if(c_selectedSearchFieldObj === undefined || !c_selectedSearchFieldObj.appliesToPersonTF) {
      return([]);
    }

    if(o_searchFieldKey === "allFields") {
      return(this.contacts_arrayOfObjs_matching_text_in_contact_fields(c_allPersonsArrayOfObjs, this.c_personFieldsArrayOfObjs, o_searchText));
    }

    if(o_searchFieldKey === "name") {
      if(o_searchText.length === 0) {
        return([]);
      }
      const searchTextLower = o_searchText.toLowerCase();
      return(c_allPersonsArrayOfObjs.filter((i_personObj) => ((JSFUNC.str2lower(i_personObj.first_name, "").indexOf(searchTextLower) !== -1) || (JSFUNC.str2lower(i_personObj.last_name, "").indexOf(searchTextLower) !== -1) || (i_personObj.firstNameSpaceLastNameLower.indexOf(searchTextLower) !== -1))));
    }

    return(this.filtered_contacts_arrayOfObjs_from_search_field_and_values(c_allPersonsArrayOfObjs, c_selectedSearchFieldObj, o_searchText, o_searchSelectedValuesComma));
  }

  get c_selectedSamGovEntityObjOrUndefined() {
    const o_selectedSamGovEntityUEIOrUndefined = this.o_selectedSamGovEntityUEIOrUndefined;
    if(o_selectedSamGovEntityUEIOrUndefined === undefined) {
      return(undefined);
    }
    for(let samGovEntityObj of this.o_samGovEntitiesSearchResultsArrayOfObjs) {
      if(this.sam_gov_entity_field_value_from_key(samGovEntityObj, "uei") === o_selectedSamGovEntityUEIOrUndefined) {
        return(samGovEntityObj);
      }
    }
    return(undefined);
  }

  get c_samGovEntityFieldsArrayOfObjs() {
    const c_samGovEntityFieldsMetadataArrayOfObjs = this.c_samGovEntityFieldsMetadataArrayOfObjs;
    const c_selectedSamGovEntityObjOrUndefined = this.c_selectedSamGovEntityObjOrUndefined;
    const c_selectMultiBitMasterSetAsidesFieldTypeObj = DatabaseMobx.c_selectMultiBitMasterSetAsidesFieldTypeObj;
    const c_selectMultiBitMasterNaicsCodesCombinedNamesFieldTypeObj = DatabaseMobx.c_selectMultiBitMasterNaicsCodesCombinedNamesFieldTypeObj;
    if(c_selectedSamGovEntityObjOrUndefined === undefined) {
      return(c_samGovEntityFieldsMetadataArrayOfObjs);
    }

    const businessTypeFieldObjOrUndefined = c_samGovEntityFieldsMetadataArrayOfObjs.find((i_fieldObj) => i_fieldObj.fieldKey === "businessTypes");
    const primaryNaicsFieldObjOrUndefined = c_samGovEntityFieldsMetadataArrayOfObjs.find((i_fieldObj) => i_fieldObj.fieldKey === "primaryNaicsCode");
    const otherNaicsFieldObjOrUndefined = c_samGovEntityFieldsMetadataArrayOfObjs.find((i_fieldObj) => i_fieldObj.fieldKey === "otherNaicsCodes");
    if((businessTypeFieldObjOrUndefined === undefined) && (primaryNaicsFieldObjOrUndefined === undefined)) {
      return(c_samGovEntityFieldsMetadataArrayOfObjs);
    }

    const businessTypeTranslationObj = ((businessTypeFieldObjOrUndefined === undefined)
      ? (undefined)
      : (this.sam_entity_business_type_codes_translation_obj_from_value(c_selectedSamGovEntityObjOrUndefined[businessTypeFieldObjOrUndefined.dbName])));
    const setAsideValueMaskOrUndefined = ((businessTypeTranslationObj !== undefined) && (businessTypeTranslationObj.setAsideIDsComma !== "")
      ? (DatabaseMobx.value_mask_from_value_raw_and_field_type_obj(businessTypeTranslationObj.setAsideIDsComma, c_selectMultiBitMasterSetAsidesFieldTypeObj))
      : (undefined));
    const naicsCodesTranslationObj = ((primaryNaicsFieldObjOrUndefined === undefined)
      ? (undefined)
      : (this.sam_entity_naics_codes_translation_obj_from_primary_and_other_values(
        c_selectedSamGovEntityObjOrUndefined[primaryNaicsFieldObjOrUndefined.dbName],
        ((otherNaicsFieldObjOrUndefined === undefined) ? (undefined) : (c_selectedSamGovEntityObjOrUndefined[otherNaicsFieldObjOrUndefined.dbName]))
      )));
    const naicsValueMaskOrUndefined = ((naicsCodesTranslationObj !== undefined) && (naicsCodesTranslationObj.naicsCodeIDsComma !== "")
      ? (DatabaseMobx.value_mask_from_value_raw_and_field_type_obj(naicsCodesTranslationObj.naicsCodeIDsComma, c_selectMultiBitMasterNaicsCodesCombinedNamesFieldTypeObj))
      : (undefined));
    const primaryNaicsRaw = ((primaryNaicsFieldObjOrUndefined === undefined) ? (undefined) : (c_selectedSamGovEntityObjOrUndefined[primaryNaicsFieldObjOrUndefined.dbName]));
    const otherNaicsRaw = ((otherNaicsFieldObjOrUndefined === undefined) ? (undefined) : (c_selectedSamGovEntityObjOrUndefined[otherNaicsFieldObjOrUndefined.dbName]));
    const primaryNaicsDisplay = JSFUNC.text_or_number_is_filled_out_tf(primaryNaicsRaw) ? (String(primaryNaicsRaw)) : ("—");
    const otherNaicsDisplay = JSFUNC.text_or_number_is_filled_out_tf(otherNaicsRaw) ? (String(otherNaicsRaw)) : ("—");
    const naicsDisplayValue = "Primary NAICS: " + primaryNaicsDisplay + "\nOther NAICS: " + otherNaicsDisplay;

    return(c_samGovEntityFieldsMetadataArrayOfObjs.map((i_fieldObj) => {
      const fieldObjWithDisplayValue = {...i_fieldObj, samGovEntityDisplayValue:c_selectedSamGovEntityObjOrUndefined[i_fieldObj.dbName]};
      if(i_fieldObj.fieldKey === "businessTypes" && businessTypeTranslationObj !== undefined) {
        return({
          ...fieldObjWithDisplayValue,
          isBusinessTypeFieldTF:true,
          businessTypeTranslationObj:businessTypeTranslationObj,
          setAsideValueMaskOrUndefined:setAsideValueMaskOrUndefined,
          unmappedBusinessTypeCodesArray:businessTypeTranslationObj.unmappedBusinessTypeCodesArray
        });
      }
      if((i_fieldObj.fieldKey === "primaryNaicsCode") && (naicsCodesTranslationObj !== undefined)) {
        return({
          ...fieldObjWithDisplayValue,
          isNaicsCodesFieldTF:true,
          samGovEntityDisplayValue:naicsDisplayValue,
          naicsValueMaskOrUndefined:naicsValueMaskOrUndefined,
          unmappedNaicsCodesArray:naicsCodesTranslationObj.unmappedNaicsCodesArray
        });
      }
      return(fieldObjWithDisplayValue);
    }));
  }

  get c_samGovEntityContactGroupsArrayOfObjs() {
    const c_selectedSamGovEntityObjOrUndefined = this.c_selectedSamGovEntityObjOrUndefined;
    if(c_selectedSamGovEntityObjOrUndefined === undefined) {
      return([]);
    }

    const c_samGovEntityFieldsMetadataArrayOfObjs = this.c_samGovEntityFieldsMetadataArrayOfObjs;
    const contactGroupsArrayOfObjs = [];
    for(let contactNumber = 1; contactNumber <= 6; contactNumber++) {
      const contactFieldsArrayOfObjs = c_samGovEntityFieldsMetadataArrayOfObjs.filter((i_fieldObj) => i_fieldObj.contactNumber === contactNumber);
      const contactHasDataTF = contactFieldsArrayOfObjs.some((i_fieldObj) =>
        JSFUNC.text_or_number_is_filled_out_tf(c_selectedSamGovEntityObjOrUndefined[i_fieldObj.dbName])
      );
      if(!contactHasDataTF) {
        continue;
      }

      const contactNamePartsArray = ["first_name", "middle_name", "last_name"].map((i_personFieldDbName) => {
        const nameFieldObjOrUndefined = contactFieldsArrayOfObjs.find((i_fieldObj) => i_fieldObj.personDbNameOrUndefined === i_personFieldDbName);
        return((nameFieldObjOrUndefined === undefined) ? ("") : (String(JSFUNC.prop_value(c_selectedSamGovEntityObjOrUndefined[nameFieldObjOrUndefined.dbName], ""))));
      }).filter((i_namePart) => i_namePart !== "");
      const contactPersonTypeMapOrUndefined = DatabaseMobx.o_tbl_bit_master_contact_person_types.get(contactNumber);
      const contactPersonTypeNameOrUndefined = ((contactPersonTypeMapOrUndefined === undefined) ? (undefined) : (contactPersonTypeMapOrUndefined.get("name")));

      contactGroupsArrayOfObjs.push({
        contactNumber:contactNumber,
        contactName:((contactNamePartsArray.length > 0) ? (contactNamePartsArray.join(" ")) : ("Contact #" + contactNumber)),
        contactPersonTypeNameOrUndefined:contactPersonTypeNameOrUndefined,
        fieldsArrayOfObjs:contactFieldsArrayOfObjs
      });
    }
    return(contactGroupsArrayOfObjs);
  }

  get c_samGovEntityFieldsMetadataArrayOfObjs() {
    return(DatabaseMobx.sam_gov_entity_fields_arrayOfObjs_from_hardcoded_contact_fields());
  }

  sam_gov_entity_field_value_from_key(i_samGovEntityObj, i_fieldKey) {
    const c_samGovEntityFieldsArrayOfObjs = this.c_samGovEntityFieldsMetadataArrayOfObjs;
    if(i_samGovEntityObj === undefined) {
      return(undefined);
    }
    const fieldObjOrUndefined = c_samGovEntityFieldsArrayOfObjs.find((i_fieldObj) => i_fieldObj.fieldKey === i_fieldKey);
    return((fieldObjOrUndefined === undefined) ? (undefined) : (i_samGovEntityObj[fieldObjOrUndefined.dbName]));
  }

  sam_entity_business_type_codes_translation_obj_from_value(i_businessTypeCodesRaw) {
    const businessTypeCodesRaw = ((i_businessTypeCodesRaw === undefined) || (i_businessTypeCodesRaw === null)) ? ("") : (String(i_businessTypeCodesRaw));
    const businessTypeCodesArray = businessTypeCodesRaw
      .split("~")
      .map((i_businessTypeCode) => i_businessTypeCode.trim())
      .filter((i_businessTypeCode) => i_businessTypeCode !== "");
    const setAsideRowsArray = Array.from(DatabaseMobx.c_tbl_bit_master_set_asides.values());
    const setAsideRowByBusinessTypeCodeMap = new Map();
    const setAsideIDsSet = new Set();

    for(let setAsideRowMap of setAsideRowsArray) {
      const businessTypeCodeRaw = setAsideRowMap.get("sam_entity_business_type_code");
      const businessTypeCode = ((businessTypeCodeRaw === undefined) || (businessTypeCodeRaw === null)) ? ("") : (String(businessTypeCodeRaw).trim());
      if((businessTypeCode !== "") && !setAsideRowByBusinessTypeCodeMap.has(businessTypeCode)) {
        setAsideRowByBusinessTypeCodeMap.set(businessTypeCode, setAsideRowMap);
      }
      setAsideIDsSet.add(JSFUNC.str2int(setAsideRowMap.get("id"), 0));
    }

    const setAsideIDsArray = [];
    const unmappedBusinessTypeCodesArray = [];
    for(let businessTypeCode of businessTypeCodesArray) {
      const matchingSetAsideRowMap = setAsideRowByBusinessTypeCodeMap.get(businessTypeCode);
      if(matchingSetAsideRowMap === undefined) {
        unmappedBusinessTypeCodesArray.push(businessTypeCode);
        continue;
      }

      const hiddenDuplicateOfRowIDOr0 = JSFUNC.str2int(matchingSetAsideRowMap.get("is_hidden_duplicate_of_row_id_or_0"), 0);
      const setAsideID = ((hiddenDuplicateOfRowIDOr0 > 0) ? (hiddenDuplicateOfRowIDOr0) : (JSFUNC.str2int(matchingSetAsideRowMap.get("id"), 0)));
      if((setAsideID <= 0) || !setAsideIDsSet.has(setAsideID)) {
        unmappedBusinessTypeCodesArray.push(businessTypeCode);
        continue;
      }
      setAsideIDsArray.push(setAsideID);
    }

    return({
      setAsideIDsComma:setAsideIDsArray.join(","),
      unmappedBusinessTypeCodesArray:unmappedBusinessTypeCodesArray
    });
  }

  sam_entity_naics_codes_translation_obj_from_primary_and_other_values(i_primaryNaicsCodeRaw, i_otherNaicsCodesRaw) {
    const primaryNaicsCode = ((i_primaryNaicsCodeRaw === undefined) || (i_primaryNaicsCodeRaw === null)) ? ("") : (String(i_primaryNaicsCodeRaw).trim());
    const otherNaicsCodesRaw = ((i_otherNaicsCodesRaw === undefined) || (i_otherNaicsCodesRaw === null)) ? ("") : (String(i_otherNaicsCodesRaw));
    const otherNaicsCodeMatchesArray = otherNaicsCodesRaw.match(/(?:^|[^\d])\d{6}(?!\d)/g) || [];
    const naicsCodesArray = [];
    if(primaryNaicsCode !== "") {
      naicsCodesArray.push(primaryNaicsCode);
    }
    for(let otherNaicsCodeMatch of otherNaicsCodeMatchesArray) {
      const otherNaicsCodeMatchArray = otherNaicsCodeMatch.match(/\d{6}/);
      if(otherNaicsCodeMatchArray !== null) {
        naicsCodesArray.push(otherNaicsCodeMatchArray[0]);
      }
    }

    const uniqueNaicsCodesArray = Array.from(new Set(naicsCodesArray));
    const naicsRowByCodeMap = new Map();
    for(let naicsRowMap of DatabaseMobx.o_tbl_bit_master_naics_codes.values()) {
      const naicsCode = JSFUNC.num2str(naicsRowMap.get("code"));
      if((naicsCode !== undefined) && !naicsRowByCodeMap.has(naicsCode)) {
        naicsRowByCodeMap.set(naicsCode, naicsRowMap.get("id"));
      }
    }

    const naicsIDsArray = [];
    const naicsIDsSet = new Set();
    const unmappedNaicsCodesArray = [];
    for(let naicsCode of uniqueNaicsCodesArray) {
      const naicsIDOrUndefined = naicsRowByCodeMap.get(naicsCode);
      const naicsID = JSFUNC.str2int(naicsIDOrUndefined, 0);
      if(naicsID <= 0) {
        unmappedNaicsCodesArray.push(naicsCode);
        continue;
      }
      if(!naicsIDsSet.has(naicsID)) {
        naicsIDsSet.add(naicsID);
        naicsIDsArray.push(naicsID);
      }
    }

    return({
      naicsCodeIDsComma:naicsIDsArray.join(","),
      unmappedNaicsCodesArray:unmappedNaicsCodesArray
    });
  }

  sam_gov_entity_contact_persons_arrayOfObjs_from_entity_obj(i_samGovEntityObj) {
    const c_samGovEntityFieldsArrayOfObjs = this.c_samGovEntityFieldsMetadataArrayOfObjs;
    const samGovEntityContactPersonsArrayOfObjs = [];
    for(let contactNumber = 1; contactNumber <= 6; contactNumber++) {
      const contactPersonObj = {};
      let contactPersonHasNameOrTitleTF = false;
      for(let contactFieldObj of c_samGovEntityFieldsArrayOfObjs) {
        if((contactFieldObj.contactNumber === contactNumber) && (contactFieldObj.personDbNameOrUndefined !== undefined)) {
          const fieldValue = JSFUNC.prop_value(i_samGovEntityObj[contactFieldObj.dbName], "");
          contactPersonObj[contactFieldObj.personDbNameOrUndefined] = fieldValue;
          if(contactFieldObj.personNameOrTitleTF && JSFUNC.text_or_number_is_filled_out_tf(fieldValue)) {
            contactPersonHasNameOrTitleTF = true;
          }
        }
      }
      if(contactPersonHasNameOrTitleTF) {
        contactPersonObj.bm_contact_person_type_ids_comma = String(contactNumber);
        samGovEntityContactPersonsArrayOfObjs.push(contactPersonObj);
      }
    }
    return(samGovEntityContactPersonsArrayOfObjs);
  }

  get c_samGovEntityContactCompanyDuplicateCandidatesArrayOfObjs() {
    const c_selectedSamGovEntityObjOrUndefined = this.c_selectedSamGovEntityObjOrUndefined;
    const c_allCompaniesArrayOfObjs = this.c_allCompaniesArrayOfObjs;
    if(c_selectedSamGovEntityObjOrUndefined === undefined) {
      return([]);
    }

    const samEntityNameLower = this.sam_entity_normalized_company_name_string(this.sam_gov_entity_field_value_from_key(c_selectedSamGovEntityObjOrUndefined, "entityName"));
    if(samEntityNameLower === "") {
      return([]);
    }

    const duplicateCandidatesArrayOfObjs = [];
    for(let companyObj of c_allCompaniesArrayOfObjs) {
      const legalNameSimilarity = this.sam_entity_company_name_similarity0to1(samEntityNameLower, companyObj.legal_name);
      const abbreviatedNameSimilarity = this.sam_entity_company_name_similarity0to1(samEntityNameLower, companyObj.abbreviated_name);
      const similarity0to1 = Math.max(legalNameSimilarity, abbreviatedNameSimilarity);
      if(similarity0to1 >= 0.75) {
        duplicateCandidatesArrayOfObjs.push({
          id: companyObj.id,
          contactName: companyObj.abbrOrLegalName,
          legalName: companyObj.legal_name,
          abbreviatedName: companyObj.abbreviated_name,
          similarity0to1: similarity0to1
        });
      }
    }

    duplicateCandidatesArrayOfObjs.sort((i_candidate1Obj, i_candidate2Obj) => (i_candidate2Obj.similarity0to1 - i_candidate1Obj.similarity0to1));
    return(duplicateCandidatesArrayOfObjs);
  }

  sam_entity_normalized_company_name_string(i_companyName) {
    if(!JSFUNC.is_string(i_companyName)) {
      return("");
    }
    return(i_companyName.toLowerCase().replace(/[^a-z0-9]/g, ""));
  }

  sam_entity_company_name_similarity0to1(i_normalizedSamEntityName, i_contactCompanyName) {
    const normalizedContactCompanyName = this.sam_entity_normalized_company_name_string(i_contactCompanyName);
    const maxNameLength = Math.max(i_normalizedSamEntityName.length, normalizedContactCompanyName.length);
    if(maxNameLength === 0) {
      return(0);
    }

    var previousDistanceRow = [];
    for(let columnIndex = 0; columnIndex <= normalizedContactCompanyName.length; columnIndex++) {
      previousDistanceRow.push(columnIndex);
    }

    for(let rowIndex = 1; rowIndex <= i_normalizedSamEntityName.length; rowIndex++) {
      var currentDistanceRow = [rowIndex];
      for(let columnIndex = 1; columnIndex <= normalizedContactCompanyName.length; columnIndex++) {
        const substitutionCost = ((i_normalizedSamEntityName.charAt(rowIndex - 1) === normalizedContactCompanyName.charAt(columnIndex - 1)) ? (0) : (1));
        currentDistanceRow[columnIndex] = Math.min(
          currentDistanceRow[columnIndex - 1] + 1,
          previousDistanceRow[columnIndex] + 1,
          previousDistanceRow[columnIndex - 1] + substitutionCost
        );
      }
      previousDistanceRow = currentDistanceRow;
    }

    return(1 - (previousDistanceRow[normalizedContactCompanyName.length] / maxNameLength));
  }

  get c_contactsBrowserSelectedArrayOfObjs() {
    if(this.o_contactsBrowserSelectedTabDbName === "samEntities") {
      return(this.o_samGovEntitiesSearchResultsArrayOfObjs);
    }

    const searchIsActiveTF = this.c_contactsBrowserSearchIsActiveTF;
    if(searchIsActiveTF) {
      return([...this.c_searchResultsCompaniesArrayOfObjs, ...this.c_searchResultsPersonsArrayOfObjs]);
    }
    if(this.o_contactsBrowserSelectedTabDbName === "persons") {
      return(this.c_allPersonsArrayOfObjs);
    }
    return(this.c_allOrgsArrayOfObjs);
  }

  get c_contactsBrowserPaginatedSelectedArrayOfObjs() {
    const c_contactsBrowserItemsPerPage = this.c_contactsBrowserItemsPerPage;
    const firstArrayIndex = ((this.c_contactsBrowserCurrentPageNumber - 1) * c_contactsBrowserItemsPerPage);
    return(this.c_contactsBrowserSelectedArrayOfObjs.slice(firstArrayIndex, firstArrayIndex + c_contactsBrowserItemsPerPage));
  }

  get c_contactsBrowserItemsPerPage() {
    const c_contactsBrowserItemsPerPageFieldTypeObj = this.c_contactsBrowserItemsPerPageFieldTypeObj;
    const c_userContactsNumContactsPerPage = UserMobx.c_userContactsNumContactsPerPage;
    const valueArray = c_contactsBrowserItemsPerPageFieldTypeObj.selectWithSearchDataObj.valueArray;
    return((JSFUNC.in_array(c_userContactsNumContactsPerPage, valueArray)) ? (c_userContactsNumContactsPerPage) : (25));
  }

  get c_contactsBrowserCurrentPageNumber() {
    if(this.o_contactsBrowserSelectedTabDbName === "samEntities") {
      return(this.o_contactsBrowserSamEntitiesPageNumber);
    }
    if(this.c_contactsBrowserSearchIsActiveTF) {
      return(this.o_contactsBrowserCompaniesPageNumber);
    }
    return((this.o_contactsBrowserSelectedTabDbName === "persons") ? (this.o_contactsBrowserPersonsPageNumber) : (this.o_contactsBrowserCompaniesPageNumber));
  }

  get c_contactsBrowserTotalNumItems() {
    return(this.c_contactsBrowserSelectedArrayOfObjs.length);
  }

  get c_contactsBrowserTotalNumPages() {
    return(Math.max(1, Math.ceil(this.c_contactsBrowserTotalNumItems / this.c_contactsBrowserItemsPerPage)));
  }

  get c_contactsBrowserCurrentPageFirstItemNumber() {
    if(this.c_contactsBrowserTotalNumItems === 0) {
      return(0);
    }
    return(((this.c_contactsBrowserCurrentPageNumber - 1) * this.c_contactsBrowserItemsPerPage) + 1);
  }

  get c_contactsBrowserCurrentPageLastItemNumber() {
    return(Math.min((this.c_contactsBrowserCurrentPageNumber * this.c_contactsBrowserItemsPerPage), this.c_contactsBrowserTotalNumItems));
  }

  get c_contactsBrowserCanIncrementPageTF() {
    return(this.c_contactsBrowserCurrentPageNumber < this.c_contactsBrowserTotalNumPages);
  }

  get c_contactsBrowserCanDecrementPageTF() {
    return(this.c_contactsBrowserCurrentPageNumber > 1);
  }

  get c_contactsBrowserItemsPerPageFieldTypeObj() {
    const valueArray = [10, 15, 20, 25, 50, 100, 500];
    var displayArray = [];
    for(let i = 0; i < valueArray.length; i++) {
      displayArray.push(valueArray[i] + " per page");
    }
    const selectWithSearchDataObj = DatabaseMobx.create_sws_data_obj_from_value_array_and_display_array("Contacts Per Page", valueArray, false, displayArray);
    return(DatabaseMobx.create_field_type_obj("select", selectWithSearchDataObj));
  }

  filtered_contacts_arrayOfObjs_from_search_field_and_values(i_contactsArrayOfObjs, i_searchFieldObj, i_searchText, i_searchSelectedValuesComma) {
    const fieldTypeObj = i_searchFieldObj.fieldTypeObj;
    const fieldDbName = i_searchFieldObj.db_name;
    if(fieldTypeObj.selectTF) {
      const selectedValuesArray = ((fieldTypeObj.selectWithSearchDataObj.valuesAreStringsTF) ? (JSFUNC.convert_comma_list_to_array(i_searchSelectedValuesComma)) : (JSFUNC.convert_comma_list_to_int_array(i_searchSelectedValuesComma)));
      if(selectedValuesArray.length === 0) {
        return([]);
      }
      return(i_contactsArrayOfObjs.filter((i_contactObj) => {
        const contactValueRaw = i_contactObj[fieldDbName];
        if(fieldTypeObj.selectWithSearchDataObj.isMultiSelectTF) {
          const contactValuesArray = ((fieldTypeObj.selectWithSearchDataObj.valuesAreStringsTF) ? (JSFUNC.convert_comma_list_to_array(contactValueRaw)) : (JSFUNC.convert_comma_list_to_int_array(contactValueRaw)));
          return(selectedValuesArray.some((i_selectedValue) => JSFUNC.in_array(i_selectedValue, contactValuesArray)));
        }
        return(JSFUNC.in_array(contactValueRaw, selectedValuesArray));
      }));
    }

    if(i_searchText.length === 0) {
      return([]);
    }
    const searchTextLower = i_searchText.toLowerCase();
    return(i_contactsArrayOfObjs.filter((i_contactObj) => (JSFUNC.str2lower(i_contactObj[fieldDbName], "").indexOf(searchTextLower) !== -1)));
  }

  contacts_arrayOfObjs_matching_text_in_contact_fields(i_contactsArrayOfObjs, i_contactFieldsArrayOfObjs, i_searchText) {
    if(i_searchText.length === 0) {
      return([]);
    }

    const searchTextLower = i_searchText.toLowerCase();
    const excludedDbNamesArray = ["id", "tree_id"];
    return(i_contactsArrayOfObjs.filter((i_contactObj) => {
      const contactName = this.contact_name_plaintext_from_contact_obj(i_contactObj);
      if((contactName !== undefined) && (contactName !== null) && (String(contactName).toLowerCase().indexOf(searchTextLower) !== -1)) {
        return(true);
      }

      for(let contactFieldObj of i_contactFieldsArrayOfObjs) {
        if(JSFUNC.in_array(contactFieldObj.db_name, excludedDbNamesArray)) {
          continue;
        }

        const contactFieldValue = i_contactObj[contactFieldObj.db_name];
        if((contactFieldValue === undefined) || (contactFieldValue === null)) {
          continue;
        }

        if(String(contactFieldValue).toLowerCase().indexOf(searchTextLower) !== -1) {
          return(true);
        }

        if(contactFieldObj.fieldTypeObj.selectTF) {
          const contactFieldValuePlainText = DatabaseMobx.value_mask_plaintext_from_value_raw_and_field_type_obj(contactFieldValue, contactFieldObj.fieldTypeObj);
          if(String(contactFieldValuePlainText).toLowerCase().indexOf(searchTextLower) !== -1) {
            return(true);
          }
        }
      }

      return(false);
    }));
  }

  get c_openContactObj() {
    const o_openContactID = this.o_openContactID;
    const o_openContactIsPersonTF = this.o_openContactIsPersonTF;

    if((o_openContactID > 0) && (o_openContactIsPersonTF !== undefined)) {
      return(this.contact_company_or_person_obj_from_id(o_openContactIsPersonTF, o_openContactID));
    }
    return(undefined);
  }

  get c_openContactName() {
    const c_openContactObj = this.c_openContactObj;
    if(c_openContactObj === undefined) {
      return(undefined);
    }
    return(this.contact_name_plaintext_from_contact_obj(c_openContactObj));
  }

  get c_openContactFieldsArrayOfObjs() {
    const c_openContactObj = this.c_openContactObj;
    if(c_openContactObj === undefined) {
      return(undefined);
    }
    return(this.contact_fields_arrayOfObjs(c_openContactObj.isPersonTF));
  }

  get c_openContactAllChildrenIDsArray() {
    const c_openContactObj = this.c_openContactObj;

    if(c_openContactObj === undefined) {
      return(undefined);
    }

    var openContactSelfAndAllChildrenIDsArray = [c_openContactObj.id]; //start with the open contactID, add all children in same org to this list

    //only compute children if this is a company
    if(!c_openContactObj.isPersonTF) {
      const openContactTreeID = c_openContactObj.tree_id;
      const allCompaniesInOldOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(c_openContactObj.organizationID);
      for(let companyInOrgObj of allCompaniesInOldOrgArrayOfObjs) {
        if(JSFUNC.tree_id_is_child_of_parent_tree_id_tf(openContactTreeID, companyInOrgObj.tree_id)) {
          openContactSelfAndAllChildrenIDsArray.push(companyInOrgObj.id);
        }
      }
    }

    return(openContactSelfAndAllChildrenIDsArray);
  }

  get c_openContactCompanyTeammateCaptureIDsArray() {
    const c_openContactObj = this.c_openContactObj;
    const o_tbl_c_teammates = DatabaseMobx.o_tbl_c_teammates;

    var openContactCompanyTeammateCaptureIDsArray = [];
    if((c_openContactObj !== undefined) && !c_openContactObj.isPersonTF) {
      var uniqueCaptureIDsArray = [];
      for(let teammateMap of o_tbl_c_teammates.values()) {
        var teammateContactCompanyID = teammateMap.get("contact_company_id");
        if(teammateContactCompanyID === c_openContactObj.id) {
          var teammateCaptureID = teammateMap.get("capture_id");
          if(!JSFUNC.in_array(teammateCaptureID, uniqueCaptureIDsArray)) {
            uniqueCaptureIDsArray.push(teammateCaptureID);
            openContactCompanyTeammateCaptureIDsArray.push(teammateCaptureID);
          }
        }
      }
    }
    return(openContactCompanyTeammateCaptureIDsArray);
  }

  get c_openContactCompanyCompetitorCaptureIDsArray() {
    const c_openContactObj = this.c_openContactObj;
    const o_openContactFilteredTblCCompetitorsMapOfMaps = DatabaseMobx.o_openContactFilteredTblCCompetitorsMapOfMaps;

    var openContactCompanyCompetitorCaptureIDsArray = [];
    if((c_openContactObj !== undefined) && !c_openContactObj.isPersonTF) {
      for(let competitorMap of o_openContactFilteredTblCCompetitorsMapOfMaps.values()) {
        const competitorCaptureID = competitorMap.get("capture_id");
        if(!JSFUNC.in_array(competitorCaptureID, openContactCompanyCompetitorCaptureIDsArray)) {
          openContactCompanyCompetitorCaptureIDsArray.push(competitorCaptureID);
        }
      }
    }
    return(openContactCompanyCompetitorCaptureIDsArray);
  }

  get c_openContactCompanyTeammateArchivedCaptureIDsArray() {
    const c_openContactCompanyTeammateCaptureIDsArray = this.c_openContactCompanyTeammateCaptureIDsArray;
    const c_archivedCaptureIDsArray = DatabaseMobx.c_archivedCaptureIDsArray;

    var openContactCompanyTeammateArchivedCaptureIDsArray = [];
    for(let captureID of c_openContactCompanyTeammateCaptureIDsArray) {
      if(JSFUNC.in_array(captureID, c_archivedCaptureIDsArray)) {
        openContactCompanyTeammateArchivedCaptureIDsArray.push(captureID);
      }
    }
    return(openContactCompanyTeammateArchivedCaptureIDsArray);
  }

  get c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs() {
    const o_openContactTeammatesRatingsSubmissionsArrayOfObjs = this.o_openContactTeammatesRatingsSubmissionsArrayOfObjs;
    const c_openContactObj = this.c_openContactObj;

    const o_tbl_captures = DatabaseMobx.o_tbl_captures;
    const o_tbl_c_teammates = DatabaseMobx.o_tbl_c_teammates;
    const c_adminSortedExpandedStagesArrayOfObjs = DatabaseMobx.c_adminSortedExpandedStagesArrayOfObjs;
    const c_activeStageIDsArray = DatabaseMobx.c_activeStageIDsArray;
    const c_closedWonStageIDsArray = DatabaseMobx.c_closedWonStageIDsArray;
    const c_closedLostStageIDsArray = DatabaseMobx.c_closedLostStageIDsArray;
    const c_fieldMapOfStage = DatabaseMobx.c_fieldMapOfStage;
    const c_fieldMapOfContractOverallValue = DatabaseMobx.c_fieldMapOfContractOverallValue;
    const c_fieldMapOfAddedDate = DatabaseMobx.c_fieldMapOfAddedDate;
    const c_fieldMapOfAwardDate = DatabaseMobx.c_fieldMapOfAwardDate;
    const c_genericDateFieldTypeObj = DatabaseMobx.c_genericDateFieldTypeObj;

    if((c_openContactObj === undefined) || c_openContactObj.isPersonTF) {
      return([]);
    }

    var openContactCompanyTeammatesRatingsCapturesArrayOfObjs = [];
    var captureIDsAlreadyAddedArray = [];
    for(let teammateMap of o_tbl_c_teammates.values()) {
      if(teammateMap.get("contact_company_id") !== c_openContactObj.id) {
        continue;
      }

      const captureID = teammateMap.get("capture_id");
      if(JSFUNC.in_array(captureID, captureIDsAlreadyAddedArray)) {
        continue;
      }
      captureIDsAlreadyAddedArray.push(captureID);

      const captureMap = o_tbl_captures.get(captureID);
      if(captureMap === undefined) {
        continue;
      }

      const captureNamePlainText = DatabaseMobx.capture_name_plaintext_from_capture_map(captureMap);
      const captureStageID = DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, c_fieldMapOfStage);
      const captureStageObjOrUndefined = JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(c_adminSortedExpandedStagesArrayOfObjs, "id", captureStageID);
      const captureStageName = ((captureStageObjOrUndefined === undefined) ? ("--Stage Not Found--") : (captureStageObjOrUndefined.name));
      const captureStageColor = ((captureStageObjOrUndefined === undefined) ? ("999999") : (captureStageObjOrUndefined.color));
      const captureStageIsWonTypeTF = JSFUNC.in_array(captureStageID, c_closedWonStageIDsArray);
      const captureStageIsLostTypeTF = JSFUNC.in_array(captureStageID, c_closedLostStageIDsArray);
      const captureStageIsActiveTypeTF = JSFUNC.in_array(captureStageID, c_activeStageIDsArray);

      const contractOverallValueRaw = DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, c_fieldMapOfContractOverallValue);
      const contractOverallValueMask = DatabaseMobx.value_mask_plaintext_from_value_raw_and_field_type_obj(contractOverallValueRaw, c_fieldMapOfContractOverallValue.get("fieldTypeObj"));
      const contractOverallValueNumeric = Number(contractOverallValueRaw);
      const contractOverallValueIsNumericTF = (contractOverallValueRaw !== undefined) && (contractOverallValueRaw !== null) && (contractOverallValueRaw !== "") && Number.isFinite(contractOverallValueNumeric);

      var captureGraphDateFieldMap = undefined;
      if(captureStageIsWonTypeTF || captureStageIsLostTypeTF) {
        captureGraphDateFieldMap = c_fieldMapOfAwardDate;
      }
      else if(captureStageIsActiveTypeTF) {
        captureGraphDateFieldMap = c_fieldMapOfAddedDate;
      }
      const captureGraphDateRaw = ((captureGraphDateFieldMap === undefined) ? (undefined) : (DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, captureGraphDateFieldMap)));
      const captureGraphDate = ((JSFUNC.date_is_filled_out_tf(captureGraphDateRaw)) ? (JSFUNC.convert_mysqldate_or_mysqldatetimeutc_to_mysqldatelocal(captureGraphDateRaw)) : (""));
      const captureGraphDateFieldDisplayName = ((captureGraphDateFieldMap === undefined) ? ("") : (captureGraphDateFieldMap.get("display_name")));

      const teammateObj = TeammateContractsMobx.create_combined_teammate_obj_from_tbl_c_teammates_row_map(teammateMap);
      const allocatedRevenueValueNumeric = teammateObj.allocationOfCOV;
      const allocatedRevenueValueIsNumericTF = contractOverallValueIsNumericTF && Number.isFinite(allocatedRevenueValueNumeric);
      const allocatedRevenueValueMask = ((allocatedRevenueValueIsNumericTF) ? (DatabaseMobx.value_mask_plaintext_from_value_raw_and_field_type_obj(allocatedRevenueValueNumeric, c_fieldMapOfContractOverallValue.get("fieldTypeObj"))) : (""));
      var teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined = undefined;
      for(let teammatesRatingSubmissionObj of o_openContactTeammatesRatingsSubmissionsArrayOfObjs) {
        if(teammatesRatingSubmissionObj.capture_id === captureID) {
          teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined = JSFUNC.copy_obj(teammatesRatingSubmissionObj);
          teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined.reviewedByUserNameMask = DatabaseMobx.user_name_mask_from_user_id(teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined.reviewed_by_user_id);
          teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined.reviewDate = DatabaseMobx.value_mask_from_value_raw_and_field_type_obj(JSFUNC.convert_mysqldate_or_mysqldatetimeutc_to_mysqldatelocal(teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined.datetime_utc), c_genericDateFieldTypeObj);
          break;
        }
      }

      openContactCompanyTeammatesRatingsCapturesArrayOfObjs.push({
        captureID: captureID,
        captureNamePlainText: captureNamePlainText,
        captureNameLowerCase: captureNamePlainText.toLowerCase(),
        captureStageID: captureStageID,
        captureStageName: captureStageName,
        captureStageColor: captureStageColor,
        captureStageIsWonTypeTF: captureStageIsWonTypeTF,
        captureStageIsLostTypeTF: captureStageIsLostTypeTF,
        captureStageIsActiveTypeTF: captureStageIsActiveTypeTF,
        captureGraphDateFieldDisplayName: captureGraphDateFieldDisplayName,
        captureGraphDate: captureGraphDate,
        contractOverallValueRaw: contractOverallValueRaw,
        contractOverallValueMask: contractOverallValueMask,
        contractOverallValueNumeric: contractOverallValueNumeric,
        contractOverallValueIsNumericTF: contractOverallValueIsNumericTF,
        allocatedRevenueValueNumeric: allocatedRevenueValueNumeric,
        allocatedRevenueValueMask: allocatedRevenueValueMask,
        allocatedRevenueValueIsNumericTF: allocatedRevenueValueIsNumericTF,
        teammateObj: teammateObj,
        reviewedTF: (teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined !== undefined),
        teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined: teammatesRatingsQuestionnaireSubmissionRowObjOrUndefined
      });
    }

    JSFUNC.sort_arrayOfObjs(openContactCompanyTeammatesRatingsCapturesArrayOfObjs, ["captureGraphDate", "captureNameLowerCase"], [false, true]);
    return(openContactCompanyTeammatesRatingsCapturesArrayOfObjs);
  }

  get c_openContactCompanyTeammateRatingsTimeGraphObj() {
    const c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs = this.c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs;
    return(this.contact_capture_analysis_time_graph_obj_from_capture_arrayOfObjs(c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs, this.o_contactAnalysisTeammateGraphValueType));
  }

  get c_openContactCompanyCompetitorAnalysisCapturesArrayOfObjs() {
    const c_openContactObj = this.c_openContactObj;
    const o_openContactFilteredTblCCompetitorsMapOfMaps = DatabaseMobx.o_openContactFilteredTblCCompetitorsMapOfMaps;
    const o_tbl_captures = DatabaseMobx.o_tbl_captures;
    const c_adminSortedExpandedStagesArrayOfObjs = DatabaseMobx.c_adminSortedExpandedStagesArrayOfObjs;
    const c_activeStageIDsArray = DatabaseMobx.c_activeStageIDsArray;
    const c_closedWonStageIDsArray = DatabaseMobx.c_closedWonStageIDsArray;
    const c_closedLostStageIDsArray = DatabaseMobx.c_closedLostStageIDsArray;
    const c_fieldMapOfStage = DatabaseMobx.c_fieldMapOfStage;
    const c_fieldMapOfContractOverallValue = DatabaseMobx.c_fieldMapOfContractOverallValue;
    const c_fieldMapOfAddedDate = DatabaseMobx.c_fieldMapOfAddedDate;
    const c_fieldMapOfAwardDate = DatabaseMobx.c_fieldMapOfAwardDate;
    const c_fieldMapOfIncumbentCompanies = DatabaseMobx.c_fieldMapOfIncumbentCompanies;

    if((c_openContactObj === undefined) || c_openContactObj.isPersonTF) {
      return([]);
    }

    var openContactCompanyCompetitorAnalysisCapturesArrayOfObjs = [];
    for(let competitorMap of o_openContactFilteredTblCCompetitorsMapOfMaps.values()) {
      const captureID = competitorMap.get("capture_id");
      const captureMap = o_tbl_captures.get(captureID);
      if(captureMap === undefined) {
        continue;
      }

      const captureNamePlainText = DatabaseMobx.capture_name_plaintext_from_capture_map(captureMap);
      const captureStageID = DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, c_fieldMapOfStage);
      const captureStageObjOrUndefined = JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(c_adminSortedExpandedStagesArrayOfObjs, "id", captureStageID);
      const captureStageIsWonTypeTF = JSFUNC.in_array(captureStageID, c_closedWonStageIDsArray);
      const captureStageIsLostTypeTF = JSFUNC.in_array(captureStageID, c_closedLostStageIDsArray);
      const captureStageIsActiveTypeTF = JSFUNC.in_array(captureStageID, c_activeStageIDsArray);
      const contractOverallValueRaw = DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, c_fieldMapOfContractOverallValue);
      const contractOverallValueNumeric = Number(contractOverallValueRaw);
      const contractOverallValueMask = DatabaseMobx.value_mask_plaintext_from_value_raw_and_field_type_obj(contractOverallValueRaw, c_fieldMapOfContractOverallValue.get("fieldTypeObj"));
      const contractOverallValueIsNumericTF = (contractOverallValueRaw !== undefined) && (contractOverallValueRaw !== null) && (contractOverallValueRaw !== "") && Number.isFinite(contractOverallValueNumeric);

      var captureGraphDateFieldMap = undefined;
      if(captureStageIsWonTypeTF || captureStageIsLostTypeTF) {
        captureGraphDateFieldMap = c_fieldMapOfAwardDate;
      }
      else if(captureStageIsActiveTypeTF) {
        captureGraphDateFieldMap = c_fieldMapOfAddedDate;
      }
      const captureGraphDateRaw = ((captureGraphDateFieldMap === undefined) ? (undefined) : (DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, captureGraphDateFieldMap)));
      const captureGraphDate = ((JSFUNC.date_is_filled_out_tf(captureGraphDateRaw)) ? (JSFUNC.convert_mysqldate_or_mysqldatetimeutc_to_mysqldatelocal(captureGraphDateRaw)) : (""));
      const captureGraphDateFieldDisplayName = ((captureGraphDateFieldMap === undefined) ? ("") : (captureGraphDateFieldMap.get("display_name")));
      const incumbentContactCompanyIDsArray = JSFUNC.convert_comma_list_to_int_array(DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, c_fieldMapOfIncumbentCompanies));

      openContactCompanyCompetitorAnalysisCapturesArrayOfObjs.push({
        captureID: captureID,
        captureNamePlainText: captureNamePlainText,
        captureNameLowerCase: captureNamePlainText.toLowerCase(),
        captureStageName: ((captureStageObjOrUndefined === undefined) ? ("--Stage Not Found--") : (captureStageObjOrUndefined.name)),
        captureStageColor: ((captureStageObjOrUndefined === undefined) ? ("999999") : (captureStageObjOrUndefined.color)),
        captureStageIsWonTypeTF: captureStageIsWonTypeTF,
        captureStageIsLostTypeTF: captureStageIsLostTypeTF,
        captureStageIsActiveTypeTF: captureStageIsActiveTypeTF,
        captureGraphDateFieldDisplayName: captureGraphDateFieldDisplayName,
        captureGraphDate: captureGraphDate,
        contractOverallValueMask: contractOverallValueMask,
        contractOverallValueNumeric: contractOverallValueNumeric,
        contractOverallValueIsNumericTF: contractOverallValueIsNumericTF,
        competitorObj: JSFUNC.obj_from_map(competitorMap),
        incumbentTF: JSFUNC.in_array(c_openContactObj.id, incumbentContactCompanyIDsArray)
      });
    }

    JSFUNC.sort_arrayOfObjs(openContactCompanyCompetitorAnalysisCapturesArrayOfObjs, ["captureGraphDate", "captureNameLowerCase"], [false, true]);
    return(openContactCompanyCompetitorAnalysisCapturesArrayOfObjs);
  }

  get c_openContactCompanyCompetitorAnalysisTimeGraphObj() {
    const c_openContactCompanyCompetitorAnalysisCapturesArrayOfObjs = this.c_openContactCompanyCompetitorAnalysisCapturesArrayOfObjs;
    return(this.contact_capture_analysis_time_graph_obj_from_capture_arrayOfObjs(c_openContactCompanyCompetitorAnalysisCapturesArrayOfObjs));
  }

  contact_capture_analysis_time_graph_obj_from_capture_arrayOfObjs(i_captureAnalysisArrayOfObjs, i_valueType="contractOverallValue") {
    const todayDate = JSFUNC.now_dateYmd();
    const categoriesArrayOfObjs = [
      {label:"Active", color:"FFCC80", idDateValueArrayOfObjs:[]},
      {label:"Won", color:"4CAF50", idDateValueArrayOfObjs:[]},
      {label:"Lost", color:"EF5350", idDateValueArrayOfObjs:[]}
    ];

    var startDate = undefined;
    var endDate = undefined;
    for(let captureAnalysisObj of i_captureAnalysisArrayOfObjs) {
      var categoryObjOrUndefined = undefined;
      if(captureAnalysisObj.captureStageIsWonTypeTF) {
        categoryObjOrUndefined = categoriesArrayOfObjs[1];
      }
      else if(captureAnalysisObj.captureStageIsLostTypeTF) {
        categoryObjOrUndefined = categoriesArrayOfObjs[2];
      }
      else if(captureAnalysisObj.captureStageIsActiveTypeTF) {
        categoryObjOrUndefined = categoriesArrayOfObjs[0];
      }

      const captureAnalysisValueNumeric = ((i_valueType === "allocatedRevenueValue") ? (captureAnalysisObj.allocatedRevenueValueNumeric) : (captureAnalysisObj.contractOverallValueNumeric));
      const captureAnalysisValueIsNumericTF = ((i_valueType === "allocatedRevenueValue") ? (captureAnalysisObj.allocatedRevenueValueIsNumericTF) : (captureAnalysisObj.contractOverallValueIsNumericTF));
      if((categoryObjOrUndefined === undefined) || !JSFUNC.date_is_filled_out_tf(captureAnalysisObj.captureGraphDate) || !captureAnalysisValueIsNumericTF) {
        continue;
      }

      categoryObjOrUndefined.idDateValueArrayOfObjs.push({
        id: captureAnalysisObj.captureID,
        date: captureAnalysisObj.captureGraphDate,
        value: captureAnalysisValueNumeric
      });
      startDate = ((startDate === undefined) || (captureAnalysisObj.captureGraphDate < startDate)) ? (captureAnalysisObj.captureGraphDate) : (startDate);
      endDate = ((endDate === undefined) || (captureAnalysisObj.captureGraphDate > endDate)) ? (captureAnalysisObj.captureGraphDate) : (endDate);
    }

    if((startDate === undefined) || (endDate === undefined)) {
      startDate = todayDate;
      endDate = JSFUNC.date_add_days(todayDate, 1);
    }
    else {
      startDate = JSFUNC.date_add_days(startDate, -1);
      endDate = JSFUNC.date_add_days(endDate, 1);
    }

    return({
      categoriesArrayOfObjs: categoriesArrayOfObjs,
      startDate: startDate,
      endDate: endDate
    });
  }

  get c_openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs() {
    var openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs = [];
    for(let teammatesRatingsCaptureObj of this.c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs) {
      if(teammatesRatingsCaptureObj.reviewedTF) {
        openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs.push(teammatesRatingsCaptureObj);
      }
    }
    return(openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs);
  }

  get c_openContactCompanyTeammatesRatingsCapturesNotReviewedArrayOfObjs() {
    var openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs = [];
    for(let teammatesRatingsCaptureObj of this.c_openContactCompanyTeammatesRatingsCapturesArrayOfObjs) {
      if(!teammatesRatingsCaptureObj.reviewedTF) {
        openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs.push(teammatesRatingsCaptureObj);
      }
    }
    return(openContactCompanyTeammatesRatingsCapturesReviewedArrayOfObjs);
  }

  get c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs() {
    const o_openContactPersonConversationsArrayOfObjs = this.o_openContactPersonConversationsArrayOfObjs;
    const o_tbl_captures = DatabaseMobx.o_tbl_captures;
    const c_genericDateYmdFromRawDateTimeUtcFieldTypeObj = DatabaseMobx.c_genericDateYmdFromRawDateTimeUtcFieldTypeObj;
    const c_genericDateTimeNaturalFieldTypeObj = DatabaseMobx.c_genericDateTimeNaturalFieldTypeObj;

    //loop through all conversations for this contact person loaded from database, compute extra fields to create an 'expanded' conversation obj
    var openContactPersonExpandedConversationsArrayOfObjs = [];
    for(let conversationObj of o_openContactPersonConversationsArrayOfObjs) {
      var expandedConversationObj = JSFUNC.copy_obj(conversationObj);

      expandedConversationObj.dateTimeLocalValueMaskSortIfoObj = DatabaseMobx.value_mask_sort_ifo_obj_from_value_raw_and_field_type_obj(conversationObj.datetime_utc, c_genericDateTimeNaturalFieldTypeObj);
      expandedConversationObj.dateYmdLocalValueMaskSortIfoObj = DatabaseMobx.value_mask_sort_ifo_obj_from_value_raw_and_field_type_obj(conversationObj.datetime_utc, c_genericDateYmdFromRawDateTimeUtcFieldTypeObj);

      openContactPersonExpandedConversationsArrayOfObjs.push(expandedConversationObj);
    }

    //sort all conversations mixed across many captures by date desc
    JSFUNC.sort_arrayOfObjs(openContactPersonExpandedConversationsArrayOfObjs, "datetime_utc", false);

    //get all unique captureIDs for each conversation (only include captureIDs that exist as real captures in o_tbl_captures)
    var allUniqueCaptureIDsArray = [];
    for(let expandedConversationObj of openContactPersonExpandedConversationsArrayOfObjs) {
      if(!JSFUNC.in_array(expandedConversationObj.capture_id, allUniqueCaptureIDsArray)) { //only add unique captureIDs to the list
        if(o_tbl_captures.has(expandedConversationObj.capture_id)) { //only include captureIDs that exist and are currently loaded into the system (archived captures thus excluded)
          allUniqueCaptureIDsArray.push(expandedConversationObj.capture_id);
        }
      }
    }

    //for each captureID, create a filtered list of matching conversations (already in desc date order)
    var openContactPersonExpandedConversationsPerCaptureArrayOfObjs = [];
    for(let captureID of allUniqueCaptureIDsArray) {
      //get capture info
      var captureNamePlainText = DatabaseMobx.capture_name_plaintext_from_capture_id(captureID);
      var captureNamePlainTextLowercase = captureNamePlainText.toLowerCase();

      //get all conversations for this single captureID
      var mostRecentDateTimeUtc = undefined;
      var captureExpandedConversationsArrayOfObjs = [];
      for(let expandedConversationObj of openContactPersonExpandedConversationsArrayOfObjs) {
        if(expandedConversationObj.capture_id === captureID) {
          if(mostRecentDateTimeUtc === undefined) { //record the datetime of the first conversation record, since all convos are sorted by date desc, the most recent will be first
            mostRecentDateTimeUtc = expandedConversationObj.datetime_utc;
          }
          
          captureExpandedConversationsArrayOfObjs.push(expandedConversationObj);
        }
      }

      //if there are 0 records, put 0000-00-00 00:00:00 as the most recent datetime for sorting the captures
      if(mostRecentDateTimeUtc === undefined) {
        mostRecentDateTimeUtc = JSFUNC.blank_datetime();
      }

      openContactPersonExpandedConversationsPerCaptureArrayOfObjs.push({
        captureID: captureID,
        captureNamePlainText: captureNamePlainText,
        captureNamePlainTextLowercase: captureNamePlainTextLowercase,
        mostRecentDateTimeUtc: mostRecentDateTimeUtc,
        captureExpandedConversationsArrayOfObjs: captureExpandedConversationsArrayOfObjs
      });
    }

    return(openContactPersonExpandedConversationsPerCaptureArrayOfObjs);
  }

  get c_openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs() {
    const o_openContactPersonConversationsFilterByCaptureNameText = this.o_openContactPersonConversationsFilterByCaptureNameText;
    const o_openContactPersonConversationsFilterByDateMin = this.o_openContactPersonConversationsFilterByDateMin;
    const o_openContactPersonConversationsFilterByDateMax = this.o_openContactPersonConversationsFilterByDateMax;
    const o_openContactPersonConversationsFilterByNotesText = this.o_openContactPersonConversationsFilterByNotesText;
    const c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs = this.c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs;

    const filterByCaptureNameIsFilledOutTF = JSFUNC.string_is_filled_out_tf(o_openContactPersonConversationsFilterByCaptureNameText);
    const filterByDateMinIsFilledOutTF = JSFUNC.date_is_filled_out_tf(o_openContactPersonConversationsFilterByDateMin);
    const filterByDateMaxIsFilledOutTF = JSFUNC.date_is_filled_out_tf(o_openContactPersonConversationsFilterByDateMax);
    const filterByNotesIsFilledOutTF = JSFUNC.string_is_filled_out_tf(o_openContactPersonConversationsFilterByNotesText);

    //if none of the filters are currently filled out (most common state), return the entire set of captures/conversations unfiltered
    if(!filterByCaptureNameIsFilledOutTF && !filterByDateMinIsFilledOutTF && !filterByDateMaxIsFilledOutTF && !filterByNotesIsFilledOutTF) {
      return(c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs);
    }

    //convert the input search terms to lowercase before looping through the captures/conversations
    var filterByCaptureNameLowercase = "";
    if(filterByCaptureNameIsFilledOutTF) {
      filterByCaptureNameLowercase = o_openContactPersonConversationsFilterByCaptureNameText.toLowerCase();
    }

    var filterByNotesLowercase = "";
    if(filterByNotesIsFilledOutTF) {
      filterByNotesLowercase = o_openContactPersonConversationsFilterByNotesText.toLowerCase();
    }

    var openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs = [];
    for(let expandedConversationsPerCaptureObj of c_openContactPersonExpandedConversationsPerCaptureArrayOfObjs) {
      var includeCaptureTF = true;

      //determine if the capture name filter includes this capture or not
      if(includeCaptureTF && filterByCaptureNameIsFilledOutTF) {
        if(!JSFUNC.input_lowercase_string_contains_lowercase_search_term_string_tf(expandedConversationsPerCaptureObj.captureNamePlainTextLowercase, filterByCaptureNameLowercase)) {
          includeCaptureTF = false;
        }
      }

      //if this capture is included, use the other filters to filter the conversations by date and notes, if 0 conversations remain in a capture, remove the whole capture obj from the filtered list
      if(includeCaptureTF) {
        var captureFilteredExpandedConversationsArrayOfObjs = [];
        for(let expandedConversationObj of expandedConversationsPerCaptureObj.captureExpandedConversationsArrayOfObjs) {
          var includeConversationTF = true;

          if(includeConversationTF && filterByDateMinIsFilledOutTF) { //conversation date min filter
            if(!(expandedConversationObj.dateYmdLocalValueMaskSortIfoObj.valueMaskPlainText >= o_openContactPersonConversationsFilterByDateMin)) {
              includeConversationTF = false;
            }
          }

          if(includeConversationTF && filterByDateMaxIsFilledOutTF) { //conversation date max filter
            if(!(expandedConversationObj.dateYmdLocalValueMaskSortIfoObj.valueMaskPlainText <= o_openContactPersonConversationsFilterByDateMax)) {
              includeConversationTF = false;
            }
          }

          if(includeConversationTF && filterByNotesIsFilledOutTF) { //conversation notes filter
            if(!JSFUNC.input_string_converted_to_lowercase_contains_lowercase_search_term_string_tf(expandedConversationObj.notes, filterByNotesLowercase)) {
              includeConversationTF = false;
            }
          }

          if(includeConversationTF) {
            captureFilteredExpandedConversationsArrayOfObjs.push(expandedConversationObj);
          }
        }

        //if the single capture has at least 1 conversation after filtering for dates/notes, include the capture with those filtered conversations
        if(captureFilteredExpandedConversationsArrayOfObjs.length > 0) {
          openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs.push({
            captureID: expandedConversationsPerCaptureObj.captureID,
            captureNamePlainText: expandedConversationsPerCaptureObj.captureNamePlainText,
            captureNamePlainTextLowercase: expandedConversationsPerCaptureObj.captureNamePlainTextLowercase,
            mostRecentDateTimeUtc: expandedConversationsPerCaptureObj.mostRecentDateTimeUtc,
            captureExpandedConversationsArrayOfObjs: captureFilteredExpandedConversationsArrayOfObjs
          });
        }
      }
    }
    return(openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs);
  }

  get c_openContactPersonNumFilteredConversations() {
    const c_openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs = this.c_openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs;

    var numFilteredConversations = 0;
    for(let expandedConversationsPerCaptureObj of c_openContactPersonFilteredExpandedConversationsPerCaptureArrayOfObjs) {
      numFilteredConversations += expandedConversationsPerCaptureObj.captureExpandedConversationsArrayOfObjs.length;
    }
    return(numFilteredConversations);
  }


  get c_openContactCompanyFileFolderSystemMapOfMaps() {
    if((this.c_openContactObj === undefined) || (this.o_openContactIsPersonTF !== false)) {
      return(new Map());
    }
    return(JSFUNC.filtered_mapOfMaps_from_matching_field_value(DatabaseMobx.o_tbl_g_contacts_companies_filefoldersystem, "contact_company_id", this.o_openContactID));
  }

  get c_openContactCompanyFileFolderSystemNumFiles() {
    const filesMapOfMaps = JSFUNC.filtered_mapOfMaps_from_matching_field_value(this.c_openContactCompanyFileFolderSystemMapOfMaps, "folder0_file1", 1);
    return(filesMapOfMaps.size);
  }

  get c_openContactPersonFileFolderSystemMapOfMaps() {
    if((this.c_openContactObj === undefined) || (this.o_openContactIsPersonTF === false)) {
      return(new Map());
    }
    return(JSFUNC.filtered_mapOfMaps_from_matching_field_value(DatabaseMobx.o_tbl_g_contacts_persons_filefoldersystem, "contact_person_id", this.o_openContactID));
  }

  get c_openContactPersonFileFolderSystemNumFiles() {
    const filesMapOfMaps = JSFUNC.filtered_mapOfMaps_from_matching_field_value(this.c_openContactPersonFileFolderSystemMapOfMaps, "folder0_file1", 1);
    return(filesMapOfMaps.size);
  }



  get c_advSearchExpandedSelectedTeammatesArrayOfObjs() {
    var advSearchExpandedSelectedTeammatesArrayOfObjs = [];
    for(let teammateMap of DatabaseMobx.o_tbl_c_teammates.values()) {
      if(DatabaseMobx.o_tbl_captures.has(teammateMap.get("capture_id"))) {
        if(teammateMap.get("selected_01") === 1) { //only include teammtes that have been selected for the team on each capture
          var combinedTeammateObj = TeammateContractsMobx.create_combined_teammate_obj_from_tbl_c_teammates_row_map(teammateMap);

          //add extra fields to the tbl_c_teammates record based on the captureID
          var captureStageIsActiveTypeTF = false;
          var captureStageIsWonTypeTF = false;
          var captureStageIsLostTypeTF = false;
          var captureStageIsNoBidOrCancelledTypeTF = false;
          var captureMap = DatabaseMobx.o_tbl_captures.get(combinedTeammateObj.capture_id);
          if(captureMap !== undefined) {
            var stageID = DatabaseMobx.capture_value_raw_or_undefined_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfStage);
            var matchingStageObjOrUndefined = JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(DatabaseMobx.c_adminSortedExpandedStagesArrayOfObjs, "id", stageID);
            if(matchingStageObjOrUndefined !== undefined) {
              captureStageIsActiveTypeTF = (!(matchingStageObjOrUndefined.isClosedStageTF)); //"not closed" catches all invalid stages as active as well
              captureStageIsWonTypeTF = matchingStageObjOrUndefined.isClosedWonStageTF;
              captureStageIsLostTypeTF = matchingStageObjOrUndefined.isClosedLostStageTF;
              captureStageIsNoBidOrCancelledTypeTF = matchingStageObjOrUndefined.isClosedNotSubmittedStageTF;
            }
          }

          combinedTeammateObj.captureTypeIsPrimeTF = DatabaseMobx.capture_type_is_prime_tf_from_capture_map(captureMap);
          combinedTeammateObj.captureStageIsActiveTypeTF = captureStageIsActiveTypeTF;
          combinedTeammateObj.captureStageIsWonTypeTF = captureStageIsWonTypeTF;
          combinedTeammateObj.captureStageIsLostTypeTF = captureStageIsLostTypeTF;
          combinedTeammateObj.captureStageIsNoBidOrCancelledTypeTF = captureStageIsNoBidOrCancelledTypeTF;

          advSearchExpandedSelectedTeammatesArrayOfObjs.push(combinedTeammateObj);
        }
      }
    }

    JSFUNC.sort_arrayOfObjs(advSearchExpandedSelectedTeammatesArrayOfObjs, "allocation_percent", false);

    return(advSearchExpandedSelectedTeammatesArrayOfObjs);
  }

  get c_advSearchEarliestDateFilteredCapturesArrayOfObjs() {
    const c_tbl_captures_fields = DatabaseMobx.c_tbl_captures_fields;

    //filter the captures if the date filter is filled out
    var filteredCapturesMapOfMaps = new Map();
    if(this.c_advSearchCaptureDateFilterIsActiveTF) {
      const filtersArrayOfObjs = [
        {capture_field_id:this.o_advSearchCaptureFilterSelectedDateFieldID, operator:"gte", value:this.o_advSearchCaptureFilterSelectedEarliestDate}
      ];
      const expandedFiltersArrayOfObjs = CapturesMobx.create_expanded_filters_arrayOfObjs_from_filters_arrayOfObjs_and_expanded_captures_fields_mapOfMaps(filtersArrayOfObjs, c_tbl_captures_fields);
      filteredCapturesMapOfMaps = CapturesMobx.get_filtered_captures_mapOfMaps_from_captures_mapOfMaps_and_expanded_filters_arrayOfObjs(DatabaseMobx.o_tbl_captures, expandedFiltersArrayOfObjs);
    }
    else { //use all captures in system
      filteredCapturesMapOfMaps = DatabaseMobx.o_tbl_captures;
    }

    var advSearchAllSubCaptureIDsAndStageIDsArrayOfObjs = [];
    for(let [captureID, captureMap] of filteredCapturesMapOfMaps) {
      var captureTypeIsPrimeTF = DatabaseMobx.capture_type_is_prime_tf_from_capture_map(captureMap);

      var stageID = captureMap.get("stage_id");
      var stageIsClosedWonTF = JSFUNC.in_array(stageID, DatabaseMobx.c_closedWonStageIDsArray);
      var stageIsActiveTF = JSFUNC.in_array(stageID, DatabaseMobx.c_activeStageIDsArray);
      var stageIsClosedLostTF = JSFUNC.in_array(stageID, DatabaseMobx.c_closedLostStageIDsArray);

      var primeContactCompanyID = captureMap.get("prime_contact_company_id");

      advSearchAllSubCaptureIDsAndStageIDsArrayOfObjs.push({
        captureID: captureID,
        captureMap: captureMap,
        captureTypeIsPrimeTF: captureTypeIsPrimeTF,
        stageIsClosedWonTF: stageIsClosedWonTF,
        stageIsActiveTF: stageIsActiveTF,
        stageIsClosedLostTF: stageIsClosedLostTF,
        primeContactCompanyID: primeContactCompanyID
      });
    }
    return(advSearchAllSubCaptureIDsAndStageIDsArrayOfObjs);
  }

  get c_advSearchEarliestDateFilteredCaptureIDsArray() {
    return(JSFUNC.get_column_vector_from_arrayOfObjs(this.c_advSearchEarliestDateFilteredCapturesArrayOfObjs, "captureID"));
  }

  get c_advSearchAllContactCompaniesArrayOfObjs() {
    //loop through each contact company, counting the captures they were teammates on in separate categories
    var allContactCompaniesArrayOfObjs = [];
    for(let companyObj of this.c_allCompaniesArrayOfObjs) {
      var wonCaptureIDsWePrimeTheySubArray = [];
      var activeCaptureIDsWePrimeTheySubArray = [];
      var lostCaptureIDsWePrimeTheySubArray = [];
      var wonCaptureIDsTheyPrimeWeSubArray = [];
      var activeCaptureIDsTheyPrimeWeSubArray = [];
      var lostCaptureIDsTheyPrimeWeSubArray = [];

      //prime capture types where contact was teammate
      for(let expandedTeammateObj of this.c_advSearchExpandedSelectedTeammatesArrayOfObjs) {
        if(expandedTeammateObj.contact_company_id === companyObj.id) { //find all teammate records that use this contact company
          if(expandedTeammateObj.captureTypeIsPrimeTF) { //prime capture type
            if(JSFUNC.in_array(expandedTeammateObj.capture_id, this.c_advSearchEarliestDateFilteredCaptureIDsArray)) { //in a capture that matches the earliest date filter
              if(expandedTeammateObj.captureStageIsWonTypeTF) { //won stage
                wonCaptureIDsWePrimeTheySubArray.push(expandedTeammateObj.capture_id);
              }
              else if(expandedTeammateObj.captureStageIsActiveTypeTF) {
                activeCaptureIDsWePrimeTheySubArray.push(expandedTeammateObj.capture_id);
              }
              else if(expandedTeammateObj.captureStageIsLostTypeTF) {
                lostCaptureIDsWePrimeTheySubArray.push(expandedTeammateObj.capture_id);
              }
            }
          }
        }
      }

      //sub capture types where contact was prime
      for(let captureObj of this.c_advSearchEarliestDateFilteredCapturesArrayOfObjs) {
        if(!captureObj.captureTypeIsPrimeTF) { //sub capture type
          if(companyObj.id === captureObj.primeContactCompanyID) {
            if(captureObj.stageIsClosedWonTF) {
              wonCaptureIDsTheyPrimeWeSubArray.push(captureObj.captureID);
            }
            if(captureObj.stageIsActiveTF) {
              activeCaptureIDsTheyPrimeWeSubArray.push(captureObj.captureID);
            }
            if(captureObj.stageIsClosedLostTF) {
              lostCaptureIDsTheyPrimeWeSubArray.push(captureObj.captureID);
            }
          }
        }
      }

      //copy all fields in the contact company obj
      var advSearchFilterContactCompanyObj = JSFUNC.copy_obj(companyObj);

      //convert some field comma list to arrays for easier filter comparisons
      advSearchFilterContactCompanyObj.sbCertificationsBmSetAsideIDsArray = JSFUNC.convert_comma_list_to_int_array(advSearchFilterContactCompanyObj.sb_certifications_bm_set_aside_ids_comma);
      advSearchFilterContactCompanyObj.capabilityIDsArray = JSFUNC.convert_comma_list_to_int_array(advSearchFilterContactCompanyObj.capability_ids_comma);
      advSearchFilterContactCompanyObj.naicsCodeIDsArray = JSFUNC.convert_comma_list_to_int_array(advSearchFilterContactCompanyObj.naics_code_ids_comma);

      advSearchFilterContactCompanyObj.wonCaptureIDsWePrimeTheySubArray = wonCaptureIDsWePrimeTheySubArray;
      advSearchFilterContactCompanyObj.activeCaptureIDsWePrimeTheySubArray = activeCaptureIDsWePrimeTheySubArray;
      advSearchFilterContactCompanyObj.lostCaptureIDsWePrimeTheySubArray = lostCaptureIDsWePrimeTheySubArray;
      advSearchFilterContactCompanyObj.wonCaptureIDsTheyPrimeWeSubArray = wonCaptureIDsTheyPrimeWeSubArray;
      advSearchFilterContactCompanyObj.activeCaptureIDsTheyPrimeWeSubArray = activeCaptureIDsTheyPrimeWeSubArray;
      advSearchFilterContactCompanyObj.lostCaptureIDsTheyPrimeWeSubArray = lostCaptureIDsTheyPrimeWeSubArray;

      advSearchFilterContactCompanyObj.numWonCapturesWePrimeTheySub = wonCaptureIDsWePrimeTheySubArray.length;
      advSearchFilterContactCompanyObj.numActiveCapturesWePrimeTheySub = activeCaptureIDsWePrimeTheySubArray.length;
      advSearchFilterContactCompanyObj.numLostCapturesWePrimeTheySub = lostCaptureIDsWePrimeTheySubArray.length;
      advSearchFilterContactCompanyObj.numWonCapturesTheyPrimeWeSub = wonCaptureIDsTheyPrimeWeSubArray.length;
      advSearchFilterContactCompanyObj.numActiveCapturesTheyPrimeWeSub = activeCaptureIDsTheyPrimeWeSubArray.length;
      advSearchFilterContactCompanyObj.numLostCapturesTheyPrimeWeSub = lostCaptureIDsTheyPrimeWeSubArray.length;

      advSearchFilterContactCompanyObj.numWonCapturesTotal = (advSearchFilterContactCompanyObj.numWonCapturesWePrimeTheySub + advSearchFilterContactCompanyObj.numWonCapturesTheyPrimeWeSub);
      advSearchFilterContactCompanyObj.numActiveCapturesTotal = (advSearchFilterContactCompanyObj.numActiveCapturesWePrimeTheySub + advSearchFilterContactCompanyObj.numActiveCapturesTheyPrimeWeSub);
      advSearchFilterContactCompanyObj.numLostCapturesTotal = (advSearchFilterContactCompanyObj.numLostCapturesWePrimeTheySub + advSearchFilterContactCompanyObj.numLostCapturesTheyPrimeWeSub);

      allContactCompaniesArrayOfObjs.push(advSearchFilterContactCompanyObj);
    }

    const sortFieldNamesArray = ["numWonCapturesTotal", "numActiveCapturesTotal", "numLostCapturesTotal", "name"];
    const sortDirectionsTFArray = [false, false, false, true];
    JSFUNC.sort_arrayOfObjs(allContactCompaniesArrayOfObjs, sortFieldNamesArray, sortDirectionsTFArray);

    return(allContactCompaniesArrayOfObjs);
  }

  get c_advSearchBusinessTypeFilterIsActiveTF() { return(JSFUNC.selectmulti_is_filled_out_tf(this.o_advSearchCompanyFilterSelectedBusinessTypeIDsComma)); }
  get c_advSearchSBCertificationsFilterIsActiveTF() { return(JSFUNC.selectmulti_is_filled_out_tf(this.o_advSearchCompanyFilterSelectedSBCertificationIDsComma)); }
  get c_advSearchCapabilitiesFilterIsActiveTF() { return(JSFUNC.selectmulti_is_filled_out_tf(this.o_advSearchCompanyFilterSelectedCapabilityIDsComma)); }
  get c_advSearchNaicsCodesFilterIsActiveTF() { return(JSFUNC.selectmulti_is_filled_out_tf(this.o_advSearchCompanyFilterSelectedNaicsCodeIDsComma)); }
  get c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF() { return(JSFUNC.select_int_is_filled_out_tf(this.o_advSearchCompanyFilterSelectedContactCompanyExtraFieldID)) }

  get c_advSearchCaptureDateFilterIsActiveTF() {
    return(JSFUNC.select_int_is_filled_out_tf(this.o_advSearchCaptureFilterSelectedDateFieldID) && JSFUNC.date_is_filled_out_tf(this.o_advSearchCaptureFilterSelectedEarliestDate));
  }

  get c_advSearchCompanyFilterSelectedBusinessTypeIDsArray() { return(JSFUNC.convert_comma_list_to_int_array(this.o_advSearchCompanyFilterSelectedBusinessTypeIDsComma)); }
  get c_advSearchCompanyFilterSelectedSBCertificationIDsArray() { return(JSFUNC.convert_comma_list_to_int_array(this.o_advSearchCompanyFilterSelectedSBCertificationIDsComma)); }
  get c_advSearchCompanyFilterSelectedCapabilityIDsArray() { return(JSFUNC.convert_comma_list_to_int_array(this.o_advSearchCompanyFilterSelectedCapabilityIDsComma)); }
  get c_advSearchCompanyFilterSelectedNaicsCodeIDsArray() { return(JSFUNC.convert_comma_list_to_int_array(this.o_advSearchCompanyFilterSelectedNaicsCodeIDsComma)); }

  get c_advSearchCompanyFilterSelectedContactCompanyExtraFieldMapOrUndefined() {
    if(this.c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF) {
      return(DatabaseMobx.o_tbl_a_contacts_companies_extra_fields.get(this.o_advSearchCompanyFilterSelectedContactCompanyExtraFieldID));
    }
    return(undefined);
  }

  get c_advSearchAnyFilterIsActiveTF() {
    return(this.c_advSearchBusinessTypeFilterIsActiveTF || this.c_advSearchSBCertificationsFilterIsActiveTF || this.c_advSearchCapabilitiesFilterIsActiveTF || this.c_advSearchNaicsCodesFilterIsActiveTF || this.c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF || this.c_advSearchCaptureDateFilterIsActiveTF);
  }

  get c_advSearchFilteredContactCompaniesArrayOfObjs() {
    if(!this.c_advSearchAnyFilterIsActiveTF) {
      return([]);
    }

    //get lowercase contact company extra fields selected field and search term if that filter is currently applied
    var selectedContactCompanyExtraFieldIsValidTF = false;
    var selectedContactCompanyExtraFieldDbName = undefined;
    var contactCompanyExtraFieldSearchTextLowercase = "";
    var contactCompanyExtraFieldSearchTextIsNotBlankTF = false;
    if(this.c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF) {
      if(JSFUNC.text_or_number_is_filled_out_tf(this.o_advSearchCompanyFilterContactCompanyExtraFieldSearchText)) {
        if(this.c_advSearchCompanyFilterSelectedContactCompanyExtraFieldMapOrUndefined !== undefined) { //if a valid contact company extra field was selected to be the filter
          selectedContactCompanyExtraFieldIsValidTF = true;
          selectedContactCompanyExtraFieldDbName = this.c_advSearchCompanyFilterSelectedContactCompanyExtraFieldMapOrUndefined.get("db_name");
          contactCompanyExtraFieldSearchTextLowercase = this.o_advSearchCompanyFilterContactCompanyExtraFieldSearchText.toLowerCase();
          contactCompanyExtraFieldSearchTextIsNotBlankTF = true;
        }
      }
    }

    var advSearchFilteredContactCompaniesArrayOfObjs = [];
    for(let advSearchFilterContactCompanyObj of this.c_advSearchAllContactCompaniesArrayOfObjs) {
      var contactMatchesAllActiveFiltersTF = true;

      if(contactMatchesAllActiveFiltersTF && this.c_advSearchBusinessTypeFilterIsActiveTF) {
        contactMatchesAllActiveFiltersTF = JSFUNC.in_array(advSearchFilterContactCompanyObj.business_type_id, this.c_advSearchCompanyFilterSelectedBusinessTypeIDsArray);
      }

      if(contactMatchesAllActiveFiltersTF && this.c_advSearchSBCertificationsFilterIsActiveTF) {
        contactMatchesAllActiveFiltersTF = JSFUNC.any_of_array1_is_in_array2(advSearchFilterContactCompanyObj.sbCertificationsBmSetAsideIDsArray, this.c_advSearchCompanyFilterSelectedSBCertificationIDsArray);
      }

      if(contactMatchesAllActiveFiltersTF && this.c_advSearchCapabilitiesFilterIsActiveTF) {
        contactMatchesAllActiveFiltersTF = JSFUNC.any_of_array1_is_in_array2(advSearchFilterContactCompanyObj.capabilityIDsArray, this.c_advSearchCompanyFilterSelectedCapabilityIDsArray);
      }

      if(contactMatchesAllActiveFiltersTF && this.c_advSearchNaicsCodesFilterIsActiveTF) {
        contactMatchesAllActiveFiltersTF = JSFUNC.any_of_array1_is_in_array2(advSearchFilterContactCompanyObj.naicsCodeIDsArray, this.c_advSearchCompanyFilterSelectedNaicsCodeIDsArray);
      }

      if(contactMatchesAllActiveFiltersTF && this.c_advSearchContactCompanyExtraFieldSearchTextFilterIsActiveTF) {
        contactMatchesAllActiveFiltersTF = false;
        if(selectedContactCompanyExtraFieldIsValidTF) {
          if(contactCompanyExtraFieldSearchTextIsNotBlankTF) {
            var contactCompanyExtraFieldValue = advSearchFilterContactCompanyObj[selectedContactCompanyExtraFieldDbName];
            if(JSFUNC.is_string(contactCompanyExtraFieldValue)) {
              contactMatchesAllActiveFiltersTF = JSFUNC.input_string_converted_to_lowercase_contains_lowercase_search_term_string_tf(contactCompanyExtraFieldValue, contactCompanyExtraFieldSearchTextLowercase);
            }
          }
        }
      }

      if(contactMatchesAllActiveFiltersTF) {
        advSearchFilteredContactCompaniesArrayOfObjs.push(advSearchFilterContactCompanyObj);
      }
    }
    return(advSearchFilteredContactCompaniesArrayOfObjs);
  }

  get c_advSearchAnalysisCapturesArrayOfObjs() {
    var advSearchAnalysisCapturesArrayOfObjs = [];
    if(this.o_openContactID > 0) {
      const todayJsDateObj = new Date();
      const averageNumDaysPerMonth = (365.25 / 12);

      for(let captureID of this.o_advSearchCaptureIDsArray) {
        var captureMap = DatabaseMobx.o_tbl_captures.get(captureID);
        if(captureMap !== undefined) {
          var captureTypeIsPrimeTF = DatabaseMobx.capture_type_is_prime_tf_from_capture_map(captureMap);

          var captureName = DatabaseMobx.capture_name_plaintext_from_capture_map(captureMap);

          var awardDateValueMaskSortIfoCanEditObj = DatabaseMobx.value_mask_sort_ifo_canedit_obj_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfAwardDate);
          var numMonthsTodayToAwardDate = undefined;
          if(awardDateValueMaskSortIfoCanEditObj.isFilledOutTF) {
            var awardDateJsDateObj = JSFUNC.convert_mysqldate_to_jsdateobj(awardDateValueMaskSortIfoCanEditObj.valueRaw);
            var numDaysTodayToAwardDate = JSFUNC.num_days_from_jsDateObj1_to_jsDateObj2(todayJsDateObj, awardDateJsDateObj);
            numMonthsTodayToAwardDate = (numDaysTodayToAwardDate / averageNumDaysPerMonth);
          }

          var covValueMaskSortIfoCanEditObj = DatabaseMobx.value_mask_sort_ifo_canedit_obj_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfContractOverallValue);
          var covValueRaw = 0;
          if(covValueMaskSortIfoCanEditObj.isFilledOutTF) {
            covValueRaw = covValueMaskSortIfoCanEditObj.valueRaw;
          }

          var stageValueMaskSortIfoCanEditObj = DatabaseMobx.value_mask_sort_ifo_canedit_obj_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfStage);

          var expandedTeammateObj = {};
          var ourDivisionMinus2TeammateObj = {};
          var ourPrimeSubTeammateDivisionValueMaskSortIfoCanEditObj = undefined;
          var ourPrimeSubTeammateAllocationValueMaskSortIfoCanEditObj = undefined;
          var ourAllocationMoneyShort = undefined;
          if(captureTypeIsPrimeTF) { //we are prime, get teammate record as a sub
            expandedTeammateObj = JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(this.c_advSearchExpandedSelectedTeammatesArrayOfObjs, ["capture_id", "contact_company_id"], [captureID, this.o_openContactID]);
          }
          else {
            ourDivisionMinus2TeammateObj = JSFUNC.get_first_obj_from_arrayOfObjs_matching_field_value(this.c_advSearchExpandedSelectedTeammatesArrayOfObjs, ["capture_id", "contact_company_id"], [captureID, -2]);
            if(ourDivisionMinus2TeammateObj === undefined) {
              ourDivisionMinus2TeammateObj = {};
            }
            ourPrimeSubTeammateDivisionValueMaskSortIfoCanEditObj = DatabaseMobx.value_mask_sort_ifo_canedit_obj_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfOurPrimeSubTeammateDivisionID);
            ourPrimeSubTeammateAllocationValueMaskSortIfoCanEditObj = DatabaseMobx.value_mask_sort_ifo_canedit_obj_from_capture_map_and_expanded_capture_field_map(captureMap, DatabaseMobx.c_fieldMapOfOurPrimeSubTeammateAllocation);
            ourAllocationMoneyShort = JSFUNC.money_short(covValueRaw * (ourPrimeSubTeammateAllocationValueMaskSortIfoCanEditObj.valueRaw / 100));
          }

          advSearchAnalysisCapturesArrayOfObjs.push({
            expandedTeammateObj: expandedTeammateObj,
            ourDivisionMinus2TeammateObj: ourDivisionMinus2TeammateObj,
            ourPrimeSubTeammateDivisionValueMaskSortIfoCanEditObj: ourPrimeSubTeammateDivisionValueMaskSortIfoCanEditObj,
            ourPrimeSubTeammateAllocationValueMaskSortIfoCanEditObj: ourPrimeSubTeammateAllocationValueMaskSortIfoCanEditObj,
            ourAllocationMoneyShort: ourAllocationMoneyShort,
            captureID: captureID,
            captureMap: captureMap,
            captureName: captureName,
            captureTypeIsPrimeTF: captureTypeIsPrimeTF,
            awardDateIsFilledOutTF: awardDateValueMaskSortIfoCanEditObj.isFilledOutTF,
            awardDateValueRaw: awardDateValueMaskSortIfoCanEditObj.valueRaw,
            awardDateValueMask: awardDateValueMaskSortIfoCanEditObj.valueMask,
            awardDateValueMaskPlainText: awardDateValueMaskSortIfoCanEditObj.valueMaskPlainText,
            awardDateValueSort: awardDateValueMaskSortIfoCanEditObj.valueSort,
            numMonthsTodayToAwardDate: numMonthsTodayToAwardDate,
            covValueRaw: covValueRaw,
            covValueMask: covValueMaskSortIfoCanEditObj.valueMask,
            covValueMaskPlainText: covValueMaskSortIfoCanEditObj.valueMaskPlainText,
            stageValueMask: stageValueMaskSortIfoCanEditObj.valueMask
          });
        }
      }

      JSFUNC.sort_arrayOfObjs(advSearchAnalysisCapturesArrayOfObjs, "awardDateValueSort", false);
    }
    return(advSearchAnalysisCapturesArrayOfObjs);
  }

  get c_advSearchAnalysisScatterDataObj() {
    const captureIDFieldDisplayName = DatabaseMobx.c_fieldMapOfCaptureID.get("display_name");
    const awardDateFieldDisplayName = DatabaseMobx.c_fieldMapOfAwardDate.get("display_name");

    var maxXValue = -999999999999;
    var xyPointsArrayOfObjs = [];
    for(let advSearchAnalysisCaptureObj of this.c_advSearchAnalysisCapturesArrayOfObjs) {
      if(advSearchAnalysisCaptureObj.awardDateIsFilledOutTF) {
        xyPointsArrayOfObjs.push({
          x: advSearchAnalysisCaptureObj.numMonthsTodayToAwardDate,
          y: advSearchAnalysisCaptureObj.covValueRaw,
          title: captureIDFieldDisplayName + ": " + advSearchAnalysisCaptureObj.captureID + "\n" + advSearchAnalysisCaptureObj.captureName + "\n" + advSearchAnalysisCaptureObj.covValueMaskPlainText + "\n" + awardDateFieldDisplayName + ": " + advSearchAnalysisCaptureObj.awardDateValueMaskPlainText,
          clickReturnValue: advSearchAnalysisCaptureObj.captureID
        });

        if(advSearchAnalysisCaptureObj.numMonthsTodayToAwardDate > maxXValue) {
          maxXValue = advSearchAnalysisCaptureObj.numMonthsTodayToAwardDate;
        }
      }
    }

    var color = "aed";
    var label = "Active";
    if(JSFUNC.in_array(this.o_advSearchTeamingCategory, ["theirAsOurWins", "usAsTheirWins"])) {
      color = "66f666";
      label = "Wins";
    }
    else if(JSFUNC.in_array(this.o_advSearchTeamingCategory, ["theirAsOurLosses", "usAsTheirLosses"])) {
      color = "e77";
      label = "Losses";
    }

    return({
      xyPointsArrayOfObjs: xyPointsArrayOfObjs,
      label: label,
      color: color,
      maxXValue: maxXValue
    });
  }









  //========================================================================================
  //actions
  a_initialize_initial_and_current_selected_contact_ids_arrays(i_selectMode, i_selectedContactIDOrContactIDsComma) {
    const [isSelectingPersonsTFU, isMultiSelectTFU] = this.is_person_and_is_multiselect_tfu_from_select_mode(i_selectMode);
    var initialSelectedContactIDsArray = undefined;
    if(isMultiSelectTFU) { //multiselect input expects initial value to be a string comma list of contactIDs
      initialSelectedContactIDsArray = JSFUNC.convert_comma_list_to_int_array(i_selectedContactIDOrContactIDsComma);
    }
    else { //single select input expects initial value to be a single contactID as an int, -1 if nothing is selected, or undefined meaning nothing is selected
      if(JSFUNC.select_int_is_filled_out_tf(i_selectedContactIDOrContactIDsComma)) {
        initialSelectedContactIDsArray = [i_selectedContactIDOrContactIDsComma]; //create an array of the 1 selected contactID
      }
      else {
        initialSelectedContactIDsArray = [];
      }
    }

    //set the initial and current selected arrays to this initial value array
    this.o_initialSelectedContactIDsArray = initialSelectedContactIDsArray;
    this.o_currentSelectedContactIDsArray = initialSelectedContactIDsArray;
  }

  a_initialize_contacts_system() {
    this.o_leftSideState = "treeWithSearch";
    this.o_searchText = "";
    this.o_orgsExpandedCompanyIDsArray = [];
    this.o_leftSideNumOrgsDrawnLimit = 50;
    this.o_leftSideZoomedCompanyID = undefined;
    this.o_advSearchCompanyFilterSelectedBusinessTypeIDsComma = ""; //"", "1,2,3" (testing)
    this.o_advSearchCaptureFilterSelectedDateFieldID = DatabaseMobx.c_fieldMapOfAwardDate.get("id");
    this.o_advSearchCaptureFilterSelectedEarliestDate = JSFUNC.blank_date();
    this.o_advSearchNumResultsDrawnLimit = 50;

    this.o_rightSideState = "empty"; //"empty", "viewEditSingleContact", "advancedSearchCapturesAnalysis"
    this.o_openContactID = undefined; //undefined, 2
    this.o_openContactIsPersonTF = undefined; //undefined, false
    this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = undefined;
    this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisLoadState = "notLoaded";
    this.o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = undefined;
    DatabaseMobx.a_set_open_contact_filtered_tbl_c_competitors_mapOfMaps(new Map());
    this.o_openContactSubtabLoadingFromDatabaseMessageOrUndefined = undefined;
    this.o_openContactTeammatesRatingsSubmissionsArrayOfObjs = [];
    this.o_openContactPersonConversationsArrayOfObjs = [];
    this.o_openContactPersonConversationsFilterByCaptureNameText = "";
    this.o_openContactPersonConversationsFilterByDateMin = JSFUNC.blank_date();
    this.o_openContactPersonConversationsFilterByDateMax = JSFUNC.blank_date();
    this.o_openContactPersonConversationsFilterByNotesText = "";
    this.o_openContactLoadingDocsTF = false;
    this.o_openContactViewEditSingleContactSelectedTabDbName = "contactInfo";
    this.o_advSearchTeamingCategory = undefined; //undefined, "usAsTheirActive"
    this.o_advSearchCaptureIDsArray = []; //[], [1], [1,2,3,4]

    //reset variable in CaptureExecMobx for which field is currently being edited
    CaptureExecMobx.o_itemEditingContactFieldUniqueString = undefined;
  }

  a_set_left_side_state(i_newStateString) {
    this.o_leftSideState = i_newStateString;
  }

  a_set_right_side_state(i_newStateString) {
    this.o_rightSideState = i_newStateString;
  }

  a_set_contacts_browser_selected_tab_db_name(i_selectedTabDbName) {
    if((i_selectedTabDbName === "companies") || (i_selectedTabDbName === "persons") || (i_selectedTabDbName === "samEntities")) {
      this.o_contactsBrowserSelectedTabDbName = i_selectedTabDbName;
      this.a_set_contacts_browser_current_page_number(1);
    }
  }

  a_set_contacts_browser_current_page_number(i_pageNumber) {
    const boundedPageNumber = Math.min(Math.max(1, i_pageNumber), this.c_contactsBrowserTotalNumPages);
    if(this.o_contactsBrowserSelectedTabDbName === "samEntities") {
      this.o_contactsBrowserSamEntitiesPageNumber = boundedPageNumber;
    }
    else if(this.c_contactsBrowserSearchIsActiveTF) {
      this.o_contactsBrowserCompaniesPageNumber = boundedPageNumber;
      this.o_contactsBrowserPersonsPageNumber = boundedPageNumber;
    }
    else if(this.o_contactsBrowserSelectedTabDbName === "persons") {
      this.o_contactsBrowserPersonsPageNumber = boundedPageNumber;
    }
    else {
      this.o_contactsBrowserCompaniesPageNumber = boundedPageNumber;
    }
  }

  a_increment_contacts_browser_current_page_number() {
    if(this.c_contactsBrowserCanIncrementPageTF) {
      this.a_set_contacts_browser_current_page_number(this.c_contactsBrowserCurrentPageNumber + 1);
    }
  }

  a_decrement_contacts_browser_current_page_number() {
    if(this.c_contactsBrowserCanDecrementPageTF) {
      this.a_set_contacts_browser_current_page_number(this.c_contactsBrowserCurrentPageNumber - 1);
    }
  }

  a_set_contacts_browser_items_per_page(i_itemsPerPage) {
    const c_contactsBrowserItemsPerPageFieldTypeObj = this.c_contactsBrowserItemsPerPageFieldTypeObj;
    const valueArray = c_contactsBrowserItemsPerPageFieldTypeObj.selectWithSearchDataObj.valueArray;
    if(JSFUNC.in_array(i_itemsPerPage, valueArray)) {
      UserMobx.a_update_logged_in_user_per_email_field("contacts_num_contacts_per_page", i_itemsPerPage, "i");
      this.o_contactsBrowserCompaniesPageNumber = 1;
      this.o_contactsBrowserPersonsPageNumber = 1;
      this.o_contactsBrowserSamEntitiesPageNumber = 1;
    }
  }

  a_set_search_field_key(i_searchFieldKey) {
    this.o_searchFieldKey = i_searchFieldKey;
    this.o_searchText = "";
    this.o_searchSelectedValuesComma = "";
    this.o_contactsBrowserCompaniesPageNumber = 1;
    this.o_contactsBrowserPersonsPageNumber = 1;
  }

  a_set_search_text(i_changedValue) {
    this.o_searchText = i_changedValue;
    this.o_contactsBrowserCompaniesPageNumber = 1;
    this.o_contactsBrowserPersonsPageNumber = 1;
  }

  a_set_search_selected_values_comma(i_selectedValuesComma) {
    this.o_searchSelectedValuesComma = i_selectedValuesComma;
    this.o_contactsBrowserCompaniesPageNumber = 1;
    this.o_contactsBrowserPersonsPageNumber = 1;
  }

  a_set_sam_gov_entity_search_input_text(i_newText) {
    this.o_samGovEntitySearchInputText = i_newText;
    this.o_samGovEntitiesSearchState = "idle";
    this.o_samGovEntitiesSearchErrorMessageOrUndefined = undefined;
    this.o_samGovEntitiesSearchResultsArrayOfObjs = [];
    this.o_selectedSamGovEntityUEIOrUndefined = undefined;
    this.o_contactsBrowserSamEntitiesPageNumber = 1;
  }

  a_set_selected_sam_gov_entity_uei(i_samGovEntityUEIOrUndefined) {
    this.o_selectedSamGovEntityUEIOrUndefined = i_samGovEntityUEIOrUndefined;
  }

  a_search_sam_gov_entities(i_columnDbNamesComma) {
    const o_samGovEntitySearchInputText = this.o_samGovEntitySearchInputText;
    if(!JSFUNC.string_is_filled_out_tf(o_samGovEntitySearchInputText.trim())) {
      this.o_samGovEntitiesSearchResultsArrayOfObjs = [];
      this.o_samGovEntitiesSearchState = "idle";
      this.o_selectedSamGovEntityUEIOrUndefined = undefined;
      return;
    }
    if(this.o_samGovEntitiesSearchState === "loading") {
      return;
    }

    const searchTerm = o_samGovEntitySearchInputText.trim();
    this.o_samGovEntitiesSearchState = "loading";
    this.o_samGovEntitiesSearchErrorMessageOrUndefined = undefined;
    this.o_samGovEntitiesSearchResultsArrayOfObjs = [];
    this.o_selectedSamGovEntityUEIOrUndefined = undefined;
    this.o_contactsBrowserSamEntitiesPageNumber = 1;

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_search_sam_gov_entities", ["i_searchTerm"], [searchTerm]);
    const C_CallPhpScript = new JSPHP.ClassCallPhpScript("searchSamGovEntitiesByName", jsDescription);
    C_CallPhpScript.add_post_var("i_searchTerm", searchTerm);
    C_CallPhpScript.add_post_var("i_columnDbNamesComma", i_columnDbNamesComma);
    C_CallPhpScript.add_return_vars("ceCompressedSamGovEntitiesMatrix");

    const functionOnSuccess = (i_parseResponse) => {
      if(this.o_samGovEntitySearchInputText.trim() !== searchTerm) {
        return;
      }
      const samGovEntitiesArrayOfObjs = DatabaseMobx.uncompress_ce_compressed_data_table_arrayOfObjs(i_parseResponse.ceCompressedSamGovEntitiesMatrix);
      if(!JSFUNC.is_array(samGovEntitiesArrayOfObjs)) {
        JSPHP.record_z_error(jsDescription, "SAM.gov Entities search returned an invalid data table.");
        this.o_samGovEntitiesSearchState = "error";
        this.o_samGovEntitiesSearchErrorMessageOrUndefined = "SAM.gov Entities could not be loaded. Please try again.";
        return;
      }
      this.o_samGovEntitiesSearchResultsArrayOfObjs = samGovEntitiesArrayOfObjs;
      this.o_samGovEntitiesSearchState = "loaded";
    }
    C_CallPhpScript.add_function("onSuccess", functionOnSuccess);

    const functionOnError = () => {
      if(this.o_samGovEntitySearchInputText.trim() === searchTerm) {
        this.o_samGovEntitiesSearchState = "error";
        this.o_samGovEntitiesSearchErrorMessageOrUndefined = "SAM.gov Entities could not be loaded. Please try again.";
      }
    }
    C_CallPhpScript.add_function("onError", functionOnError);

    C_CallPhpScript.execute();
  }

  a_set_contact_analysis_y_log_scale_tf(i_newValueTF) {
    if(typeof i_newValueTF === "boolean") {
      this.o_contactAnalysisYLogScaleTF = i_newValueTF;
    }
  }

  a_set_contact_analysis_graph_type(i_graphType) {
    if((i_graphType === "line") || (i_graphType === "bar")) {
      this.o_contactAnalysisGraphType = i_graphType;
    }
  }

  a_set_contact_analysis_time_bins(i_timeBins) {
    if(JSFUNC.in_array(i_timeBins, ["yearly", "quarterly", "monthly"])) {
      this.o_contactAnalysisTimeBins = i_timeBins;
    }
  }

  a_set_contact_analysis_teammate_graph_value_type(i_valueType) {
    if(JSFUNC.in_array(i_valueType, ["allocatedRevenueValue", "contractOverallValue"])) {
      this.o_contactAnalysisTeammateGraphValueType = i_valueType;
    }
  }

  a_expand_collapse_company(i_companyID) {
    if(JSFUNC.in_array(i_companyID, this.o_orgsExpandedCompanyIDsArray)) { //the org is currently expanded, remove the id to collapse it
      //find all subdivisions below the company being collapsed and collapse all of their id numbers as well
      var companyIDsToRemoveArray = [i_companyID]; //initialize
      var companyObj = this.contact_company_or_person_obj_from_id(false, i_companyID);
      var allOrgDivisionsArrayOfObjs = undefined;
      if(companyObj.org_topdiv_id === -1) { //if this is a top org division, find all subdivision ids and remove them all
        allOrgDivisionsArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(i_companyID);
        var allOrgDivisionIDsArray = JSFUNC.get_column_vector_from_arrayOfObjs(allOrgDivisionsArrayOfObjs, "id");
        companyIDsToRemoveArray = JSFUNC.merge_unique(companyIDsToRemoveArray, allOrgDivisionIDsArray); //add the top org id to be removed with all of the subdivision ids
      }
      else { //a division was collapsed, only collapse that division and all of its subdivisions based on tree_id
        var collapsingCompanyTreeID = companyObj.tree_id;
        var collapsingCompanyTreeIDLength = collapsingCompanyTreeID.length;
        allOrgDivisionsArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(companyObj.org_topdiv_id);
        for(let orgDivisionObj of allOrgDivisionsArrayOfObjs) {
          var orgDivisionTreeID = orgDivisionObj.tree_id;
          if(orgDivisionTreeID.substring(0, collapsingCompanyTreeIDLength) === collapsingCompanyTreeID) {
            companyIDsToRemoveArray.push(orgDivisionObj.id);
          }
        }
      }
      this.o_orgsExpandedCompanyIDsArray = JSFUNC.remove_all_values_from_array(companyIDsToRemoveArray, this.o_orgsExpandedCompanyIDsArray);
    }
    else {
      this.o_orgsExpandedCompanyIDsArray.push(i_companyID);
    }
  }

  a_set_left_side_num_orgs_drawn_to_unlimited() {
    this.o_leftSideNumOrgsDrawnLimit = JSFUNC.sort_max_mysqli_int();
  }

  a_set_left_side_zoomed_org_id_from_is_person_tf_and_contact_id(i_isPersonTF, i_contactID) {
    if(i_isPersonTF) {
      this.a_set_left_side_zoomed_org_id_from_person_id(i_contactID);
    }
    else {
      this.a_set_left_side_zoomed_org_id_from_company_id(i_contactID);
    }
  }

  a_set_left_side_zoomed_org_id_from_company_id(i_contactCompanyID) {
    //find the contact company obj from the input id
    var contactCompanyObj = undefined;
    for(let tempContactCompanyObj of this.c_allCompaniesArrayOfObjs) {
      if(tempContactCompanyObj.id === i_contactCompanyID) {
        contactCompanyObj = tempContactCompanyObj;
        break;
      }
    }

    //if the company is found, us its organizationID to set the zoomed org
    if(contactCompanyObj !== undefined) {
      this.a_set_left_side_zoomed_org_id_from_org_id(contactCompanyObj.organizationID);
    }
  }

  a_set_left_side_zoomed_org_id_from_person_id(i_contactPersonID) {
    //find the contact company obj from the input id
    var contactPersonObj = undefined;
    for(let tempContactPersonObj of this.c_allPersonsArrayOfObjs) {
      if(tempContactPersonObj.id === i_contactPersonID) {
        contactPersonObj = tempContactPersonObj;
        break;
      }
    }

    //if the company is found, us its organizationID to set the zoomed org
    if(contactPersonObj !== undefined) {
      this.a_set_left_side_zoomed_org_id_from_org_id(contactPersonObj.organizationID);
    }
  }

  a_set_left_side_zoomed_org_id_from_org_id(i_orgCompanyID) {
    this.o_leftSideZoomedCompanyID = i_orgCompanyID;

    const allDivisionsInOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(i_orgCompanyID);
    var zoomedOrgsExpandedCompanyIDsArray = JSFUNC.get_column_vector_from_arrayOfObjs(allDivisionsInOrgArrayOfObjs, "id");
    zoomedOrgsExpandedCompanyIDsArray.push(i_orgCompanyID); //add the top org itself as expanded
    this.o_orgsExpandedCompanyIDsArray = zoomedOrgsExpandedCompanyIDsArray;
  }

  a_open_contact(i_isPersonTF, i_contactID) {
    if(!((this.o_openContactIsPersonTF === i_isPersonTF) && (this.o_openContactID === i_contactID))) { //no need to reopen the contact if it's already open
      //set contacts system flags to open contact on right side
      this.o_rightSideState = "viewEditSingleContact";
      this.o_openContactIsPersonTF = i_isPersonTF;
      this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = undefined;
      this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = undefined;
      this.o_openContactCompetitorAnalysisLoadState = "notLoaded";
      this.o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = undefined;
      this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = undefined;
      this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = undefined;
      DatabaseMobx.a_set_open_contact_filtered_tbl_c_competitors_mapOfMaps(new Map());
      this.o_openContactSubtabLoadingFromDatabaseMessageOrUndefined = undefined;
      this.o_openContactTeammatesRatingsSubmissionsArrayOfObjs = [];
      this.o_openContactPersonConversationsArrayOfObjs = [];
      this.o_openContactPersonConversationsFilterByCaptureNameText = "";
      this.o_openContactPersonConversationsFilterByDateMin = JSFUNC.blank_date();
      this.o_openContactPersonConversationsFilterByDateMax = JSFUNC.blank_date();
      this.o_openContactPersonConversationsFilterByNotesText = "";
      this.o_openContactLoadingDocsTF = true;
      this.o_openContactViewEditSingleContactSelectedTabDbName = "contactInfo";
      this.o_openContactID = i_contactID;
      if(!i_isPersonTF) {
        this.a_load_open_contact_competitor_analysis();
      }

      //load only the documents for this contact from the server
      const loadContactDocsDatabaseApiString = ((i_isPersonTF) ? ("openContactPersonLoadFilteredDocs") : ("openContactCompanyLoadFilteredDocs"));
      const contactDocsFfsTblName = ((i_isPersonTF) ? ("tbl_g_contacts_persons_filefoldersystem") : ("tbl_g_contacts_companies_filefoldersystem"));
      const loadContactDocsDatabaseApiInputString = ((i_isPersonTF) ? ("i_contactPersonID") : ("i_contactCompanyID"));

      const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_open_contact", ["i_isPersonTF", "i_contactID"], [i_isPersonTF, i_contactID]);
      const C_CallPhpScript = new JSPHP.ClassCallPhpScript(loadContactDocsDatabaseApiString, jsDescription);
      C_CallPhpScript.add_post_var(loadContactDocsDatabaseApiInputString, i_contactID);
      C_CallPhpScript.add_return_vars("docsMatrix");

      const functionOnSuccess = (i_parseResponse) => {
        const clearOldMapDataFirstTF = true;
        DatabaseMobx.a_insert_or_update_local_data_map(contactDocsFfsTblName, i_parseResponse.docsMatrix, clearOldMapDataFirstTF, jsDescription);
      }
      C_CallPhpScript.add_function("onSuccess", functionOnSuccess);

      const functionOnFinish = () => {
        this.a_set_open_contact_loading_docs_tf(false);
      }
      C_CallPhpScript.add_function("onFinish", functionOnFinish);

      C_CallPhpScript.execute();
    }
  }

  a_close_contact() {
    //if the right side was selecting the parent company for a contact, force the left side back to treeWithSearch when closing
    if(this.o_leftSideState === "selectParentDivision") {
      this.o_leftSideState = "treeWithSearch";
    }

    this.o_rightSideState = "empty";
    this.o_openContactIsPersonTF = undefined;
    this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = undefined;
    this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisLoadState = "notLoaded";
    this.o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = undefined;
    this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = undefined;
    DatabaseMobx.a_set_open_contact_filtered_tbl_c_competitors_mapOfMaps(new Map());
    this.o_openContactSubtabLoadingFromDatabaseMessageOrUndefined = undefined;
    this.o_openContactTeammatesRatingsSubmissionsArrayOfObjs = [];
    this.o_openContactPersonConversationsArrayOfObjs = [];
    this.o_openContactPersonConversationsFilterByCaptureNameText = "";
    this.o_openContactPersonConversationsFilterByDateMin = JSFUNC.blank_date();
    this.o_openContactPersonConversationsFilterByDateMax = JSFUNC.blank_date();
    this.o_openContactPersonConversationsFilterByNotesText = "";
    this.o_openContactLoadingDocsTF = undefined;
    this.o_openContactViewEditSingleContactSelectedTabDbName = "contactInfo";
    this.o_openContactID = undefined;
  }

  a_load_open_contact_teammate_ratings(i_forceReloadTF=false) {
    const o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined;
    const o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined;
    const c_openContactObj = this.c_openContactObj;
    const c_teammatesRatingsQuestionsArrayOfObjs = DatabaseMobx.c_teammatesRatingsQuestionsArrayOfObjs;
    if((c_openContactObj === undefined) || c_openContactObj.isPersonTF) {
      return;
    }

    const contactCompanyID = c_openContactObj.id;
    if((o_openContactTeammateRatingsLoadedCompanyIDOrUndefined === contactCompanyID) && !i_forceReloadTF) {
      return;
    }
    if(o_openContactTeammateRatingsLoadingCompanyIDOrUndefined === contactCompanyID) {
      return;
    }

    if(i_forceReloadTF) {
      this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = undefined;
    }
    this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = contactCompanyID;
    this.a_set_open_contact_subtab_loading_from_database_message_or_undefined("Loading Teammate Analysis");

    var columnDbNamesToLoadArray = ["id", "capture_id", "reviewed_by_user_id", "datetime_utc"];
    for(let teammatesRatingsQuestionObj of c_teammatesRatingsQuestionsArrayOfObjs) {
      columnDbNamesToLoadArray.push("q" + teammatesRatingsQuestionObj.id + "a");
      columnDbNamesToLoadArray.push("q" + teammatesRatingsQuestionObj.id + "c");
    }
    const columnDbNamesToLoadComma = JSFUNC.convert_array_to_comma_list(columnDbNamesToLoadArray);

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_load_open_contact_teammate_ratings", ["i_contactCompanyID"], [contactCompanyID]);
    const C_CallPhpScript = new JSPHP.ClassCallPhpScript("loadTeammateRatingsFromContactCompanyID", jsDescription);

    C_CallPhpScript.add_post_var("i_contactCompanyID", contactCompanyID);
    C_CallPhpScript.add_post_var("i_columnDbNamesToLoadComma", columnDbNamesToLoadComma);
    C_CallPhpScript.add_return_vars("teammatesRatingsQuestionnaireSubmissionsMatrix");

    const functionOnSuccess = (i_parseResponse) => {
      const c_openContactObj = this.c_openContactObj;
      if((c_openContactObj !== undefined) && !c_openContactObj.isPersonTF && (c_openContactObj.id === contactCompanyID)) {
        this.a_set_open_contact_teammates_ratings_submissions_arrayOfObjs(i_parseResponse.teammatesRatingsQuestionnaireSubmissionsMatrix);
        this.o_openContactTeammateRatingsLoadedCompanyIDOrUndefined = contactCompanyID;
      }
    }
    C_CallPhpScript.add_function("onSuccess", functionOnSuccess);

    const functionOnFinish = () => {
      const o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined;
      if(o_openContactTeammateRatingsLoadingCompanyIDOrUndefined === contactCompanyID) {
        this.o_openContactTeammateRatingsLoadingCompanyIDOrUndefined = undefined;
        const c_openContactObj = this.c_openContactObj;
        if((c_openContactObj !== undefined) && (c_openContactObj.id === contactCompanyID)) {
          this.a_set_open_contact_subtab_loading_from_database_message_or_undefined(undefined);
        }
      }
    }
    C_CallPhpScript.add_function("onFinish", functionOnFinish);

    C_CallPhpScript.execute();
  }

  a_load_open_contact_competitor_analysis(i_forceReloadTF=false) {
    const o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = this.o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined;
    const o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined;
    const c_openContactObj = this.c_openContactObj;
    if((c_openContactObj === undefined) || c_openContactObj.isPersonTF) {
      return;
    }

    const contactCompanyID = c_openContactObj.id;
    if((o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined === contactCompanyID) && !i_forceReloadTF) {
      return;
    }
    if(o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined === contactCompanyID) {
      return;
    }

    this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = contactCompanyID;
    this.o_openContactCompetitorAnalysisLoadState = "loading";
    this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = undefined;
    DatabaseMobx.a_set_open_contact_filtered_tbl_c_competitors_mapOfMaps(new Map());

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_load_open_contact_competitor_analysis", ["i_contactCompanyID"], [contactCompanyID]);
    const C_CallPhpScript = new JSPHP.ClassCallPhpScript("loadCompetitorsFromContactCompanyID", jsDescription);
    C_CallPhpScript.add_post_var("i_contactCompanyID", contactCompanyID);
    C_CallPhpScript.add_return_vars("ceCompressedCompetitorsMatrix");

    const functionOnSuccess = (i_parseResponse) => {
      const c_openContactObj = this.c_openContactObj;
      if((c_openContactObj === undefined) || c_openContactObj.isPersonTF || (c_openContactObj.id !== contactCompanyID)) {
        return;
      }

      const competitorsArrayOfObjs = DatabaseMobx.uncompress_ce_compressed_data_table_arrayOfObjs(i_parseResponse.ceCompressedCompetitorsMatrix);
      if(!JSFUNC.is_array(competitorsArrayOfObjs)) {
        JSPHP.record_z_error(jsDescription, "The competitor analysis API returned an invalid competitor table.");
        this.o_openContactCompetitorAnalysisLoadState = "error";
        this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = "Competitor analysis could not be loaded. Please try again.";
        return;
      }

      const competitorsMapOfMaps = JSFUNC.mapOfMaps_from_arrayOfObjs(competitorsArrayOfObjs);
      if(competitorsMapOfMaps === undefined) {
        JSPHP.record_z_error(jsDescription, "The competitor analysis API returned rows without valid record IDs.");
        this.o_openContactCompetitorAnalysisLoadState = "error";
        this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = "Competitor analysis could not be loaded. Please try again.";
        return;
      }

      DatabaseMobx.a_set_open_contact_filtered_tbl_c_competitors_mapOfMaps(competitorsMapOfMaps);
      this.o_openContactCompetitorAnalysisLoadedCompanyIDOrUndefined = contactCompanyID;
      this.o_openContactCompetitorAnalysisLoadState = "loaded";
    }
    C_CallPhpScript.add_function("onSuccess", functionOnSuccess);

    const functionOnError = () => {
      const c_openContactObj = this.c_openContactObj;
      if((c_openContactObj !== undefined) && !c_openContactObj.isPersonTF && (c_openContactObj.id === contactCompanyID)) {
        this.o_openContactCompetitorAnalysisLoadState = "error";
        this.o_openContactCompetitorAnalysisErrorMessageOrUndefined = "Competitor analysis could not be loaded. Please try again.";
      }
    }
    C_CallPhpScript.add_function("onError", functionOnError);

    const functionOnFinish = () => {
      const o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined;
      if(o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined === contactCompanyID) {
        this.o_openContactCompetitorAnalysisLoadingCompanyIDOrUndefined = undefined;
      }
    }
    C_CallPhpScript.add_function("onFinish", functionOnFinish);

    C_CallPhpScript.execute();
  }

  a_set_open_contact_subtab_loading_from_database_message_or_undefined(i_newValueStringOrUndefined) {
    this.o_openContactSubtabLoadingFromDatabaseMessageOrUndefined = i_newValueStringOrUndefined;
  }

  a_set_open_contact_teammates_ratings_submissions_arrayOfObjs(i_arrayOfObjs) {
    this.o_openContactTeammatesRatingsSubmissionsArrayOfObjs = i_arrayOfObjs;
  }


  a_contact_person_conversations_load_all_person_conversation_history(i_contactPersonID) {
    const c_openContactObj = this.c_openContactObj;

    const contactPersonName = this.contact_name_plaintext_from_contact_obj(c_openContactObj);
    this.a_set_open_contact_subtab_loading_from_database_message_or_undefined("Loading all conversations for Contact Person '" + contactPersonName + "'");

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_contact_person_conversations_load_all_person_conversation_history", ["i_contactPersonID"], [i_contactPersonID]);
    const C_CallPhpScript = new JSPHP.ClassCallPhpScript("loadConversationsFromContactPersonID", jsDescription);

    C_CallPhpScript.add_post_var("i_contactPersonID", i_contactPersonID);

    C_CallPhpScript.add_return_vars("conversationsMatrix");

    const functionOnSuccess = (i_parseResponse) => {
      this.a_set_open_contact_person_conversations_arrayOfObjs(i_parseResponse.conversationsMatrix);
    }
    C_CallPhpScript.add_function("onSuccess", functionOnSuccess);

    const functionOnFinish = () => {
      this.a_set_open_contact_subtab_loading_from_database_message_or_undefined(undefined);
    }
    C_CallPhpScript.add_function("onFinish", functionOnFinish);

    C_CallPhpScript.execute();
  }

  a_set_open_contact_person_conversations_arrayOfObjs(i_arrayOfObjs) {
    this.o_openContactPersonConversationsArrayOfObjs = i_arrayOfObjs;
  }

  a_set_open_contact_person_conversations_filter_by_capture_name_text(i_newValueString) {
    this.o_openContactPersonConversationsFilterByCaptureNameText = i_newValueString;
  }

  a_set_open_contact_person_conversations_filter_by_date_min(i_newValueDateYmd) {
    this.o_openContactPersonConversationsFilterByDateMin = i_newValueDateYmd;
  }

  a_set_open_contact_person_conversations_filter_by_date_max(i_newValueDateYmd) {
    this.o_openContactPersonConversationsFilterByDateMax = i_newValueDateYmd;
  }

  a_set_open_contact_person_conversations_filter_by_notes_text(i_newValueString) {
    this.o_openContactPersonConversationsFilterByNotesText = i_newValueString;
  }


  a_set_open_contact_loading_docs_tf(i_newValueTF) {
    this.o_openContactLoadingDocsTF = i_newValueTF;
  }


  a_set_open_contact_view_edit_single_contact_selected_tab_db_name(i_newValue) {
    const c_openContactObj = this.c_openContactObj;

    this.o_openContactViewEditSingleContactSelectedTabDbName = i_newValue;

    //if opening the contact person conversations tab, load the conversations data
    if(i_newValue === "contactConversations") {
      this.a_contact_person_conversations_load_all_person_conversation_history(c_openContactObj.id);
    }
  }

  a_set_selected_contact_ids_array(i_newContactIDsArray) {
    this.o_currentSelectedContactIDsArray = i_newContactIDsArray;
  }

  a_select_contact(i_contactID, i_selectMode) { //toggle switch when clicking a select contact checkbox, unknown if the box is checked or not
    if(JSFUNC.in_array(i_contactID, this.o_currentSelectedContactIDsArray)) { //if the contactID has already been selected, remove all occurances of it to unselect it
      this.a_remove_contact_from_selection(i_contactID);
    }
    else {
      const [isSelectingPersonsTFU, isMultiSelectTFU] = this.is_person_and_is_multiselect_tfu_from_select_mode(i_selectMode);
      if(isMultiSelectTFU || this.o_currentSelectedContactIDsArray.length === 0) { //add the first selected contact, or add more if in multiselect mode
        this.o_currentSelectedContactIDsArray.push(i_contactID);
      }
      else { //do not allow adding a 2nd selected contact if this is a single select mode, instead overwrite the previous selection with the new one
        this.o_currentSelectedContactIDsArray = [i_contactID];
      }

      //also open the newly selected contact on the right side when selecting a contact
      this.a_open_contact(isSelectingPersonsTFU, i_contactID);
    }
  }

  a_remove_contact_from_selection(i_contactIdToRemove) {
    this.o_currentSelectedContactIDsArray = JSFUNC.remove_all_values_from_array(i_contactIdToRemove, this.o_currentSelectedContactIDsArray);
  }

  a_delete_contact(i_isPersonTF, i_contactID) {
    //if the contact being deleted is currently open being viewed, close it before deleting
    if(this.o_openContactIsPersonTF === i_isPersonTF && this.o_openContactID === i_contactID) {
      this.a_close_contact();
    }

    const tblName = this.contacts_tbl_name_from_is_person_tf(i_isPersonTF);

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_delete_contact", ["i_contactID", "i_isPersonTF"], [i_contactID, i_isPersonTF]);
    const C_CallPhpTblUID = new JSPHP.ClassCallPhpTblUID(jsDescription);

    C_CallPhpTblUID.add_delete(tblName, i_contactID); //no sort column for contacts (sorted by name), no need to resort after delete

    //deleting a contact company requires recalculating teammate counts/allocations for all captures that had this contact as a teammate
    if(!i_isPersonTF) {
      const functionOnSuccess = (i_parseResponse) => {
        JSPHP.recalculate_and_update_capture_teammate_counts_and_allocations_from_contact_company_id(i_contactID);
      }
      C_CallPhpTblUID.add_function("onSuccess", functionOnSuccess);
    }

    C_CallPhpTblUID.execute();
  }

  a_update_contact_field(i_isPersonTF, i_contactID, i_fieldDbName, i_newValue, i_fieldIdsb) {
    const updatedFieldIsBusinessTypeIDTF = (i_fieldDbName === "business_type_id");

    const tblName = this.contacts_tbl_name_from_is_person_tf(i_isPersonTF);

    var fieldNamesArray = [i_fieldDbName];
    var valuesArray = [i_newValue];
    var valuesIdsbArray = [i_fieldIdsb];

    //if setting the business size to large, erase any sb certifications selected back to ""
    if(updatedFieldIsBusinessTypeIDTF) {
      //look up the business type based on the i_newValue (business_type_id)
      const businessTypeMap = DatabaseMobx.tbl_row_map_from_id("tbl_a_business_types", i_newValue);
      if(businessTypeMap !== undefined) {
        if(businessTypeMap.get("is_sb_type_01") !== 1) {
          fieldNamesArray.push("sb_certifications_bm_set_aside_ids_comma");
          valuesArray.push("");
          valuesIdsbArray.push("s");
        }
      }
    }

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_update_contact_field", ["i_isPersonTF", "i_contactID", "i_fieldDbName", "i_newValue", "i_fieldIdsb"], [i_isPersonTF, i_contactID, i_fieldDbName, i_newValue, i_fieldIdsb]);
    const C_CallPhpTblUID = new JSPHP.ClassCallPhpTblUID(jsDescription);

    C_CallPhpTblUID.add_update(tblName, i_contactID, fieldNamesArray, valuesArray, valuesIdsbArray);

    //updating business type for a contact company requires recalculating teammate counts/allocations for all captures that have this contact as a teammate
    if(!i_isPersonTF && updatedFieldIsBusinessTypeIDTF) {
      const functionOnSuccess = (i_parseResponse) => {
        JSPHP.recalculate_and_update_capture_teammate_counts_and_allocations_from_contact_company_id(i_contactID);
      }
      C_CallPhpTblUID.add_function("onSuccess", functionOnSuccess);
    }

    C_CallPhpTblUID.execute();
  }

  a_update_contact_parent_company_field(i_newParentContactCompanyIDSelected) {
    if(this.c_openContactObj !== undefined) { //only perform this update if the openContactObj is defined to reference as the company/person being moved
      const isPersonTF = this.c_openContactObj.isPersonTF;
      const contactID = this.c_openContactObj.id;
      const tblName = this.contacts_tbl_name_from_is_person_tf(isPersonTF);

      const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_update_contact_parent_company_field", ["i_newParentContactCompanyIDSelected"], [i_newParentContactCompanyIDSelected]);
      const C_CallPhpTblUID = new JSPHP.ClassCallPhpTblUID(jsDescription);

      if(isPersonTF) { //changing a person's parent company
        C_CallPhpTblUID.add_update(tblName, contactID, "contact_company_id", i_newParentContactCompanyIDSelected, "i");
      }
      else { //changing a company's parent company (moves all children with them to the new location)
        const oldTreeIDLength = this.c_openContactObj.tree_id.length;
        var newOrgID = undefined;
        var newTreeID = undefined;
        if(i_newParentContactCompanyIDSelected <= 0) { //this company should be set as a top level company
          newOrgID = contactID;
          newTreeID = "";
          C_CallPhpTblUID.add_update(tblName, contactID, ["org_topdiv_id", "tree_id"], [-1, ""], ["i", "s"]);
        }
        else { //get info about parent company selected
          const newParentCompanyObj = this.contact_company_or_person_obj_from_id(false, i_newParentContactCompanyIDSelected);
          if(newParentCompanyObj.id > 0) { //if parent exists
            newOrgID = newParentCompanyObj.organizationID;
            const allCompaniesInNewOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(newOrgID);
            newTreeID = JSFUNC.get_new_child_tree_id_from_arrayOfObjs(allCompaniesInNewOrgArrayOfObjs, newParentCompanyObj.tree_id);
            C_CallPhpTblUID.add_update(tblName, contactID, ["org_topdiv_id", "tree_id"], [newOrgID, newTreeID], ["i", "s"]);
          }
        }

        if(newTreeID !== undefined) {
          for(let childID of this.c_openContactAllChildrenIDsArray) {
            var childContactCompanyObj = this.contact_company_or_person_obj_from_id(false, childID);
            if(childContactCompanyObj.id > 0) {
              var childOldTreeID = childContactCompanyObj.tree_id;
              var childNewTreeID = newTreeID + childOldTreeID.substring(oldTreeIDLength, childOldTreeID.length);
              C_CallPhpTblUID.add_update(tblName, childContactCompanyObj.id, ["org_topdiv_id", "tree_id"], [newOrgID, childNewTreeID], ["i", "s"]);
            }
          }
        }
      }

      C_CallPhpTblUID.execute();
    }
  }

  a_insert_new_contact_from_contacts_system_creation_and_open_on_success() {
    const o_newContactObj = this.o_newContactObj;

    this.a_set_right_side_state("savingNewContact");

    const functionOnSuccess = (i_newlyInsertedContactID) => {
      //open newly created contact, sets the right side state to viewing the newly created contact
      this.a_open_contact(o_newContactObj.isPersonTF, i_newlyInsertedContactID);

      //set left side to selecting the parent division if a contact person was created
      if(o_newContactObj.isPersonTF) {
        this.a_set_left_side_state("selectParentDivision");
      }
    }

    const functionOnError = () => {
      this.a_set_right_side_state("empty");
    }

    this.a_insert_new_contact_from_new_contact_obj(o_newContactObj, functionOnSuccess, functionOnError);
  }

  a_insert_new_contact_from_new_contact_obj(i_newContactObj, i_functionOnSuccess=undefined, i_functionOnError=undefined) {
    const c_teammatesRatingsQuestionsArrayOfObjs = DatabaseMobx.c_teammatesRatingsQuestionsArrayOfObjs;

    const isPersonTF = i_newContactObj.isPersonTF;

    const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_insert_new_contact_from_new_contact_obj", ["i_newContactObj"], [i_newContactObj]);
    const C_CallPhpTblUID = new JSPHP.ClassCallPhpTblUID(jsDescription);

    const tblName = this.contacts_tbl_name_from_is_person_tf(isPersonTF);
    const fieldsArrayOfObjs = this.contact_fields_arrayOfObjs(isPersonTF);

    var fieldDbNamesArray = [];
    var newValuesArray = [];
    var valuesIdsbArray = [];
    for(let contactExpandedFieldObj of fieldsArrayOfObjs) {
      fieldDbNamesArray.push(contactExpandedFieldObj.db_name);
      newValuesArray.push(i_newContactObj[contactExpandedFieldObj.db_name]);
      valuesIdsbArray.push(contactExpandedFieldObj.fieldTypeObj.idsb);
    }

    //insert initial values for the teammate ratings for contact companies
    if(!isPersonTF) {
      fieldDbNamesArray.push("tr_total");
      newValuesArray.push(-1); //initialize all rating values to -1 for N/A
      valuesIdsbArray.push("d");

      fieldDbNamesArray.push("tr_num_reviews");
      newValuesArray.push(0);
      valuesIdsbArray.push("i");

      for(let teammatesRatingsQuestionObj of c_teammatesRatingsQuestionsArrayOfObjs) {
        fieldDbNamesArray.push(teammatesRatingsQuestionObj.contactCompaniesRatingColumnDbName);
        newValuesArray.push(-1); //initialize all rating values to -1 for N/A
        valuesIdsbArray.push("d");
      }
    }

    C_CallPhpTblUID.add_insert(tblName, fieldDbNamesArray, newValuesArray, valuesIdsbArray);

    const functionOnSuccess = (i_parseResponse) => {
      const newlyInsertedContactID = i_parseResponse.outputObj.i0;
      if(JSFUNC.is_number(newlyInsertedContactID) && (newlyInsertedContactID > 0)) { //i0 > 0 is a successful insert, "0" is returned if the insert failed
        if(JSFUNC.is_function(i_functionOnSuccess)) {
          i_functionOnSuccess(newlyInsertedContactID);
        }
      }
      else { //insert failed
        if(JSFUNC.is_function(i_functionOnError)) {
          i_functionOnError();
        }
      }
    }
    C_CallPhpTblUID.add_function("onSuccess", functionOnSuccess);

    C_CallPhpTblUID.add_function("onError", i_functionOnError);

    C_CallPhpTblUID.execute();
  }

  a_insert_sam_gov_entity_into_contacts(i_samGovEntityObj, i_contactPersonsArrayOfObjs, i_functionOnSuccess, i_functionOnError) {
    const samGovEntityUEI = this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "uei");
    const samGovEntityName = this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityName");
    if((i_samGovEntityObj === undefined) || !JSFUNC.text_or_number_is_filled_out_tf(samGovEntityUEI) || !JSFUNC.text_or_number_is_filled_out_tf(samGovEntityName)) {
      if(JSFUNC.is_function(i_functionOnError)) {
        i_functionOnError("The SAM.gov Entity must include both an Entity Name and UEI Code.");
      }
      return;
    }

    const companyObj = this.create_initialized_contact_obj(false);
    companyObj.legal_name = samGovEntityName;
    companyObj.sam_unique_entity_id = samGovEntityUEI;
    companyObj.duns_number = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "dunsNumber"), "");
    companyObj.cage_code = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "cageCode"), "");
    companyObj.address1 = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityAddress1"), "");
    companyObj.address2 = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityAddress2"), "");
    companyObj.city = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityCity"), "");
    companyObj.state = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityState"), "");
    companyObj.zip = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityZip"), "");
    companyObj.zip2 = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityZipCodeP4"), "");
    companyObj.country = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityCountry"), "");
    companyObj.web_address = JSFUNC.prop_value(this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "entityUrl"), "");

    const samEntityBusinessTypeCodesRaw = this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "businessTypes");
    const samEntityBusinessTypeTranslationObj = this.sam_entity_business_type_codes_translation_obj_from_value(samEntityBusinessTypeCodesRaw);
    if(samEntityBusinessTypeTranslationObj.unmappedBusinessTypeCodesArray.length > 0) {
      const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_insert_sam_gov_entity_into_contacts", [], []);
      const errorMessage = "SAM.gov Entity Business Type code(s) '" + samEntityBusinessTypeTranslationObj.unmappedBusinessTypeCodesArray.join(", ") + "' from tbl_sam_entities.c32 value '" + String(JSFUNC.prop_value(samEntityBusinessTypeCodesRaw, "")) + "' were not found in tbl_bit_master_set_asides.sam_entity_business_type_code. Continuing the import with the mapped Set-Aside row ID(s) '" + samEntityBusinessTypeTranslationObj.setAsideIDsComma + "' only.";
      JSPHP.record_z_error(jsDescription, errorMessage);
    }
    companyObj.sb_certifications_bm_set_aside_ids_comma = samEntityBusinessTypeTranslationObj.setAsideIDsComma;

    const samPrimaryNaicsCodeRaw = this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "primaryNaicsCode");
    const samOtherNaicsCodesRaw = this.sam_gov_entity_field_value_from_key(i_samGovEntityObj, "otherNaicsCodes");
    const samEntityNaicsTranslationObj = this.sam_entity_naics_codes_translation_obj_from_primary_and_other_values(samPrimaryNaicsCodeRaw, samOtherNaicsCodesRaw);
    if(samEntityNaicsTranslationObj.unmappedNaicsCodesArray.length > 0) {
      const primaryNaicsCodeDisplay = ((samPrimaryNaicsCodeRaw === undefined) || (samPrimaryNaicsCodeRaw === null)) ? ("") : (String(samPrimaryNaicsCodeRaw));
      const otherNaicsCodesDisplay = ((samOtherNaicsCodesRaw === undefined) || (samOtherNaicsCodesRaw === null)) ? ("") : (String(samOtherNaicsCodesRaw));
      const jsDescription = JSFUNC.js_description_from_action("ContactsMobx", "a_insert_sam_gov_entity_into_contacts", [], []);
      const errorMessage = "SAM.gov Entity NAICS code(s) '" + samEntityNaicsTranslationObj.unmappedNaicsCodesArray.join(", ") + "' from tbl_sam_entities.c33 Primary NAICS value '" + primaryNaicsCodeDisplay + "' and c35 Other NAICS value '" + otherNaicsCodesDisplay + "' were not found in tbl_bit_master_naics_codes.code. Continuing the import with the mapped NAICS row ID(s) '" + samEntityNaicsTranslationObj.naicsCodeIDsComma + "' only.";
      JSPHP.record_z_error(jsDescription, errorMessage);
    }
    companyObj.naics_code_ids_comma = samEntityNaicsTranslationObj.naicsCodeIDsComma;

    const contactPersonsArrayOfObjs = this.sam_gov_contact_persons_arrayOfObjs_with_unique_names(i_contactPersonsArrayOfObjs);
    const functionOnCompanyInserted = (i_newCompanyID) => {
      const insertSamGovContactPersonAtIndex = (i_contactPersonIndex) => {
        if(i_contactPersonIndex >= contactPersonsArrayOfObjs.length) {
          if(JSFUNC.is_function(i_functionOnSuccess)) {
            i_functionOnSuccess(i_newCompanyID);
          }
          return;
        }

        const samGovContactPersonObj = contactPersonsArrayOfObjs[i_contactPersonIndex];
        const contactPersonObj = this.create_initialized_contact_obj(true);
        contactPersonObj.contact_company_id = i_newCompanyID;
        for(let hardcodedContactPersonFieldObj of DatabaseMobx.c_hardcodedContactPersonFieldsArrayOfObjs) {
          const fieldDbName = hardcodedContactPersonFieldObj.db_name;
          if((fieldDbName !== undefined) && (fieldDbName !== "id") && (fieldDbName !== "contact_company_id") && (samGovContactPersonObj[fieldDbName] !== undefined)) {
            contactPersonObj[fieldDbName] = JSFUNC.prop_value(samGovContactPersonObj[fieldDbName], "");
          }
        }

        const functionOnPersonInserted = () => {
          insertSamGovContactPersonAtIndex(i_contactPersonIndex + 1);
        }
        const functionOnPersonError = () => {
          if(JSFUNC.is_function(i_functionOnError)) {
            i_functionOnError("The company was saved, but one of its SAM.gov contact persons could not be saved. Check Contacts before retrying to avoid creating a duplicate company.", true);
          }
        }
        this.a_insert_new_contact_from_new_contact_obj(contactPersonObj, functionOnPersonInserted, functionOnPersonError);
      }

      insertSamGovContactPersonAtIndex(0);
    }
    this.a_insert_new_contact_from_new_contact_obj(companyObj, functionOnCompanyInserted, i_functionOnError);
  }

  sam_gov_contact_persons_arrayOfObjs_with_unique_names(i_contactPersonsArrayOfObjs) {
    const contactPersonsArrayOfObjs = JSFUNC.is_array(i_contactPersonsArrayOfObjs) ? (i_contactPersonsArrayOfObjs) : ([]);
    const uniqueContactPersonsArrayOfObjs = [];
    const contactPersonsByNameKeyMap = new Map();

    for(let contactPersonObj of contactPersonsArrayOfObjs) {
      const firstName = String(JSFUNC.prop_value(contactPersonObj.first_name, "") || "").trim().toLowerCase();
      const middleName = String(JSFUNC.prop_value(contactPersonObj.middle_name, "") || "").trim().toLowerCase();
      const lastName = String(JSFUNC.prop_value(contactPersonObj.last_name, "") || "").trim().toLowerCase();
      const personHasNameTF = (firstName !== "" || middleName !== "" || lastName !== "");
      const nameKey = JSON.stringify([firstName, middleName, lastName]);
      const existingContactPersonObj = personHasNameTF ? (contactPersonsByNameKeyMap.get(nameKey)) : (undefined);

      if(existingContactPersonObj === undefined) {
        const uniqueContactPersonObj = {...contactPersonObj};
        uniqueContactPersonsArrayOfObjs.push(uniqueContactPersonObj);
        if(personHasNameTF) {
          contactPersonsByNameKeyMap.set(nameKey, uniqueContactPersonObj);
        }
        continue;
      }

      const typeIDsString = [
        JSFUNC.prop_value(existingContactPersonObj.bm_contact_person_type_ids_comma, ""),
        JSFUNC.prop_value(contactPersonObj.bm_contact_person_type_ids_comma, "")
      ].filter((i_typeIDsString) => JSFUNC.text_or_number_is_filled_out_tf(i_typeIDsString)).join(",");
      const typeIDsSet = new Set(typeIDsString.split(",").map((i_typeID) => i_typeID.trim()).filter((i_typeID) => i_typeID !== ""));
      existingContactPersonObj.bm_contact_person_type_ids_comma = Array.from(typeIDsSet).join(",");

      for(let [fieldDbName, fieldValue] of Object.entries(contactPersonObj)) {
        const existingFieldValue = existingContactPersonObj[fieldDbName];
        const existingValueIsBlankTF = (!JSFUNC.text_or_number_is_filled_out_tf(existingFieldValue) || (typeof existingFieldValue === "string" && existingFieldValue.trim() === ""));
        const newValueIsFilledTF = (JSFUNC.text_or_number_is_filled_out_tf(fieldValue) && !(typeof fieldValue === "string" && fieldValue.trim() === ""));
        if((fieldDbName !== "bm_contact_person_type_ids_comma") && existingValueIsBlankTF && newValueIsFilledTF) {
          existingContactPersonObj[fieldDbName] = fieldValue;
        }
      }
    }

    return(uniqueContactPersonsArrayOfObjs);
  }

  a_initialize_new_contact(i_isPersonTF) {
    this.o_newContactObj = this.create_initialized_contact_obj(i_isPersonTF);
  }

  create_initialized_contact_obj(i_isPersonTF) {
    var newContactObj = {isPersonTF:i_isPersonTF};

    //initialize new contactObj with the blankValue for every field
    const contactFieldsArrayOfObjs = this.contact_fields_arrayOfObjs(i_isPersonTF);
    for(let contactExpandedFieldObj of contactFieldsArrayOfObjs) {
      newContactObj[contactExpandedFieldObj.db_name] = contactExpandedFieldObj.fieldTypeObj.blankValue;
    }

    //set a few other field initial values manually
    if(i_isPersonTF) {
      newContactObj.contact_company_id = -1;
    }
    else {
      newContactObj.org_topdiv_id = -1;
      newContactObj.tree_id = "";
      newContactObj.business_type_id = -1;
    }

    return(newContactObj);
  }

  a_update_new_contact_field_local(i_fieldDbName, i_newValue) {
    if(this.o_newContactObj !== undefined) {
      this.o_newContactObj[i_fieldDbName] = i_newValue;
      if(!this.o_newContactObj.isPersonTF && i_fieldDbName === "business_type_id") {
        const businessTypeMap = DatabaseMobx.tbl_row_map_from_id("tbl_a_business_types", i_newValue);
        if(businessTypeMap !== undefined) {
          if(businessTypeMap.get("is_sb_type_01") !== 1) {
            this.o_newContactObj.sb_certifications_bm_set_aside_ids_comma = "";
          }
        }
      }
    }
  }

  a_update_new_contact_parent_company_field_local(i_newParentContactCompanyIDSelected) {
    if(this.o_newContactObj !== undefined) {
      if(this.o_newContactObj.isPersonTF) { //selecting parent for newly created  contact person
        this.o_newContactObj.contact_company_id = i_newParentContactCompanyIDSelected;
      }
      else { //selecting parent for newly created contact company
        if(i_newParentContactCompanyIDSelected <= 0) { //selected that this should be its own new top level division (-1), thus the treeID is ""
          this.o_newContactObj.org_topdiv_id = -1;
          this.o_newContactObj.tree_id = "";
        }
        else {
          const newParentCompanyObj = this.contact_company_or_person_obj_from_id(false, i_newParentContactCompanyIDSelected);
          if(newParentCompanyObj.id > 0) { //only compute the new treeID if the company selected was a valid company found within the array of all contact companies (-1 id returned from --Company Does Not Exist-- record), this should always be found when selecting a parent
            const allCompaniesInNewOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(newParentCompanyObj.organizationID);
            const newTreeID = JSFUNC.get_new_child_tree_id_from_arrayOfObjs(allCompaniesInNewOrgArrayOfObjs, newParentCompanyObj.tree_id);

            this.o_newContactObj.org_topdiv_id = newParentCompanyObj.organizationID;
            this.o_newContactObj.tree_id = newTreeID;
          }
        }
      }
    }
  }






  //export
  a_export_contacts_to_csv_for_download(i_exportTopLevelContactCompaniesTF, i_exportSubdivisionContactCompaniesTF, i_exportContactPersonsTF, i_functionOnFinish) {
    const c_allPersonsArrayOfObjs = this.c_allPersonsArrayOfObjs;
    const c_allOrgsArrayOfObjs = this.c_allOrgsArrayOfObjs;

    var headerRowArray = [];
    var contactsArrayOfArrays = [];

    if(!i_exportTopLevelContactCompaniesTF && !i_exportSubdivisionContactCompaniesTF && i_exportContactPersonsTF) { //contact persons only
      //person only header
      headerRowArray = this.export_contacts_add_person_field_display_names_to_array(headerRowArray);
      contactsArrayOfArrays.push(headerRowArray);

      //person only data
      for(let personObj of c_allPersonsArrayOfObjs) {
        var personDataRowArray = this.export_contacts_add_person_data_values_to_array_from_person_obj([], personObj);
        contactsArrayOfArrays.push(personDataRowArray);
      }
    }
    else { //contact companies with or without persons
      //header
      headerRowArray = this.export_contacts_add_company_field_display_names_to_array(headerRowArray);
      if(i_exportContactPersonsTF) {
        headerRowArray = this.export_contacts_add_person_field_display_names_to_array(headerRowArray);
      }
      contactsArrayOfArrays.push(headerRowArray);

      //data
      for(let orgObj of c_allOrgsArrayOfObjs) {
        if(i_exportTopLevelContactCompaniesTF) {
          //top level org
          contactsArrayOfArrays.push(this.export_contacts_add_person_empty_strings_to_array(this.export_contacts_add_company_data_values_to_array_from_company_obj([], orgObj)));

          //persons under org
          if(i_exportContactPersonsTF) {
            const orgPersonsArrayOfObjs = this.all_persons_within_company_id_arrayOfObjs(orgObj.id);
            for(let personObj of orgPersonsArrayOfObjs) {
              contactsArrayOfArrays.push(this.export_contacts_add_person_data_values_to_array_from_person_obj(this.export_contacts_add_company_empty_strings_to_array([]), personObj));
            }
          }
        }

        if(i_exportSubdivisionContactCompaniesTF) {
          //all divisions under org
          const allDivisionsInOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(orgObj.id);
          for(let divisionObj of allDivisionsInOrgArrayOfObjs) {
            //subdivision
            contactsArrayOfArrays.push(this.export_contacts_add_person_empty_strings_to_array(this.export_contacts_add_company_data_values_to_array_from_company_obj([], divisionObj)));

            //persons under subdivision
            if(i_exportContactPersonsTF) {
              var divisionPersonsArrayOfObjs = this.all_persons_within_company_id_arrayOfObjs(divisionObj.id);
              for(let personObj of divisionPersonsArrayOfObjs) {
                contactsArrayOfArrays.push(this.export_contacts_add_person_data_values_to_array_from_person_obj(this.export_contacts_add_company_empty_strings_to_array([]), personObj));
              }
            }
          }
        }
      }

      //unassigned persons
      if(i_exportContactPersonsTF) {
        const unassignedCompanyObj = {id:-1,  legal_name:"Persons Not Assigned to a Company"};
        contactsArrayOfArrays.push(this.export_contacts_add_person_empty_strings_to_array(this.export_contacts_add_company_data_values_to_array_from_company_obj([], unassignedCompanyObj)));

        const unassignedPersonsArrayOfObjs = this.all_persons_within_company_id_arrayOfObjs(unassignedCompanyObj.id);
        for(let personObj of unassignedPersonsArrayOfObjs) {
          contactsArrayOfArrays.push(this.export_contacts_add_person_data_values_to_array_from_person_obj(this.export_contacts_add_company_empty_strings_to_array([]), personObj));
        }
      }

    }

    const csvString = JSFUNC.convert_data_table_to_csv_string(contactsArrayOfArrays);
    const downloadFileNameAndExt = "CaptureExec Contacts " + JSFUNC.now_dateYmd() + ".csv";
    JSFUNC.browser_offer_file_download_from_file_data_string(csvString, downloadFileNameAndExt);

    if(JSFUNC.is_function(i_functionOnFinish)) {
      i_functionOnFinish();
    }
  }

  export_contacts_add_person_field_display_names_to_array(i_array) {
    const c_personFieldsArrayOfObjs = this.c_personFieldsArrayOfObjs;
    for(let personFieldObj of c_personFieldsArrayOfObjs) {
      i_array.push(personFieldObj.display_name);
    }
    return(i_array);
  }

  export_contacts_add_company_field_display_names_to_array(i_array) {
    const c_companyFieldsArrayOfObjs = this.c_companyFieldsArrayOfObjs;
    for(let companyFieldObj of c_companyFieldsArrayOfObjs) {
      if(companyFieldObj.db_name !== "tree_id") {
        i_array.push(companyFieldObj.display_name);
      }
    }
    return(i_array);
  }

  export_contacts_add_person_data_values_to_array_from_person_obj(i_array, i_personObj) {
    const c_personFieldsArrayOfObjs = this.c_personFieldsArrayOfObjs;
    for(let personFieldObj of c_personFieldsArrayOfObjs) {
      var personValueMaskPlainText = i_personObj[personFieldObj.db_name];
      if(personFieldObj.db_name === "contact_company_id") {
        personValueMaskPlainText = i_personObj.organizationAbbrOrLegalName;
        if(!i_personObj.parentCompanyIsOrganizationTF) {
          personValueMaskPlainText += " [" + i_personObj.parentCompanyAbbrOrLegalName + "]";
        }
      }
      i_array.push(personValueMaskPlainText);
    }
    return(i_array);
  }

  export_contacts_add_company_data_values_to_array_from_company_obj(i_array, i_companyObj) {
    const c_companyFieldsArrayOfObjs = this.c_companyFieldsArrayOfObjs;
    for(let companyFieldObj of c_companyFieldsArrayOfObjs) {
      if(companyFieldObj.db_name !== "tree_id") {
        var companyValueMaskPlainText = DatabaseMobx.value_mask_plaintext_from_value_raw_and_field_type_obj(i_companyObj[companyFieldObj.db_name], companyFieldObj.fieldTypeObj);
        i_array.push(companyValueMaskPlainText);
      }
    }
    return(i_array);
  }

  export_contacts_add_person_empty_strings_to_array(i_array) {
    const c_personFieldsArrayOfObjs = this.c_personFieldsArrayOfObjs;
    for(let personFieldObj of c_personFieldsArrayOfObjs) {
      i_array.push("");
    }
    return(i_array);
  }

  export_contacts_add_company_empty_strings_to_array(i_array) {
    const c_companyFieldsArrayOfObjs = this.c_companyFieldsArrayOfObjs;
    for(let companyFieldObj of c_companyFieldsArrayOfObjs) {
      if(companyFieldObj.db_name !== "tree_id") {
        i_array.push("");
      }
    }
    return(i_array);
  }







  //advanced search
  a_open_contacts_advanced_search_left_side() {
    this.a_initialize_contacts_system();
    this.a_set_left_side_state("advancedCompanySearch");
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_selected_business_type_ids_comma(i_newValue) {
    this.o_advSearchCompanyFilterSelectedBusinessTypeIDsComma = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_selected_sb_certifications_bm_set_aside_ids_comma(i_newValue) {
    this.o_advSearchCompanyFilterSelectedSBCertificationIDsComma = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_selected_capability_ids_comma(i_newValue) {
    this.o_advSearchCompanyFilterSelectedCapabilityIDsComma = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_selected_naics_code_ids_comma(i_newValue) {
    this.o_advSearchCompanyFilterSelectedNaicsCodeIDsComma = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_selected_contact_company_extra_field_id(i_newValueInt) {
    this.o_advSearchCompanyFilterSelectedContactCompanyExtraFieldID = i_newValueInt;
    this.o_advSearchCompanyFilterContactCompanyExtraFieldSearchText = "";
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_company_filter_contact_company_extra_field_search_text(i_newValueString) {
    this.o_advSearchCompanyFilterContactCompanyExtraFieldSearchText = i_newValueString;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_capture_filter_selected_date_field_id(i_newValue) {
    this.o_advSearchCaptureFilterSelectedDateFieldID = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_capture_filter_selected_earliest_date(i_newValue) {
    this.o_advSearchCaptureFilterSelectedEarliestDate = i_newValue;
    this.a_contacts_advanced_search_set_num_results_drawn_to_initial_value();
  }

  a_contacts_advanced_search_set_num_results_drawn_to_initial_value() {
    this.o_advSearchNumResultsDrawnLimit = 50;
  }

  a_contacts_advanced_search_set_num_results_drawn_to_unlimited() {
    this.o_advSearchNumResultsDrawnLimit = JSFUNC.sort_max_mysqli_int();
  }

  a_contacts_advanced_search_set_generating_csv_of_matching_companies_data_tf(i_newValueTF) {
    this.o_advSearchGeneratingCsvOfMatchingCompaniesDataTF = i_newValueTF;
  }

  a_contacts_advanced_search_download_csv_of_matching_companies_data() {
    const c_advSearchFilteredContactCompaniesArrayOfObjs = this.c_advSearchFilteredContactCompaniesArrayOfObjs;

    this.a_contacts_advanced_search_set_generating_csv_of_matching_companies_data_tf(true);

    const advSearchMatchingCompaniesCsvColumnNamesArray = ["Company/Division Name", "Parent Organization", "[We Prime] # Wins", "[We Prime] # Active", "[We Prime] # Losses", "[We Sub] # Wins", "[We Sub] # Active", "[We Sub] # Losses"];

    var advSearchMatchingCompaniesCsvDataArrayOfArrays = [advSearchMatchingCompaniesCsvColumnNamesArray];
    for(let advSearchFilterContactCompanyObj of c_advSearchFilteredContactCompaniesArrayOfObjs) {
      var contactName = this.contact_name_plaintext_from_contact_obj(advSearchFilterContactCompanyObj);
      var isTopCompanyTF = this.contact_is_top_org_tf(advSearchFilterContactCompanyObj);
      var parentOrganizationName = "";
      if(!isTopCompanyTF) {
        parentOrganizationName = advSearchFilterContactCompanyObj.organizationAbbrOrLegalName;
      }

      var advSearchCompanyCsvDataArray = [
        contactName,
        parentOrganizationName,
        advSearchFilterContactCompanyObj.numWonCapturesWePrimeTheySub,
        advSearchFilterContactCompanyObj.numActiveCapturesWePrimeTheySub,
        advSearchFilterContactCompanyObj.numLostCapturesWePrimeTheySub,
        advSearchFilterContactCompanyObj.numWonCapturesTheyPrimeWeSub,
        advSearchFilterContactCompanyObj.numActiveCapturesTheyPrimeWeSub,
        advSearchFilterContactCompanyObj.numLostCapturesTheyPrimeWeSub
      ];

      advSearchMatchingCompaniesCsvDataArrayOfArrays.push(advSearchCompanyCsvDataArray);
    }

    const csvString = JSFUNC.convert_data_table_to_csv_string(advSearchMatchingCompaniesCsvDataArrayOfArrays);
    const downloadFileNameAndExt = JSFUNC.now_dateYmd() + " Contacts Advanced Search Matching Companies.csv";

    JSFUNC.browser_offer_file_download_from_file_data_string(csvString, downloadFileNameAndExt);

    this.a_contacts_advanced_search_set_generating_csv_of_matching_companies_data_tf(false);
  }

  a_contacts_advanced_search_open_captures_analysis(i_contactID, i_advSearchTeamingCategory, i_captureIDsArray) {
    this.o_rightSideState = "advancedSearchCapturesAnalysis";
    this.o_openContactIsPersonTF = false;
    this.o_openContactID = i_contactID;
    this.o_advSearchTeamingCategory = i_advSearchTeamingCategory;
    this.o_advSearchCaptureIDsArray = i_captureIDsArray;
  }








  //other non-action methods
  contact_company_or_person_obj_from_id(i_isPersonTF, i_contactCompanyOrPersonID) {
    if(i_isPersonTF) {
      return(this.contact_person_obj_from_id(i_contactCompanyOrPersonID));
    }
    return(this.contact_company_obj_from_id(i_contactCompanyOrPersonID, false));
  }

  contact_person_obj_from_id(i_contactPersonID) {
    const contactPersonMap = DatabaseMobx.o_tbl_g_contacts_persons.get(i_contactPersonID);
    return(this.contact_person_obj_from_tbl_g_contacts_persons_row_map(contactPersonMap, i_contactPersonID, this.c_contactCompaniesPrecomputedInfoObj));
  }

  contact_person_obj_from_tbl_g_contacts_persons_row_map(i_contactPersonMap, i_contactPersonID=undefined, i_contactCompaniesPrecomputedInfoObj=undefined) {
    const c_allCompanyIDsArray = this.c_allCompanyIDsArray;

    const contactPersonExistsTF = (i_contactPersonMap !== undefined);

    var doesNotExistString = undefined;

    var firstName = undefined;
    var middleName = undefined;
    var lastName = undefined;
    var title = undefined;
    var email = undefined;
    var phone = undefined;
    let personAddress1;
    let personAddress2;
    let personCity;
    let personState;
    let personZip;
    let personZip2;
    let personCountry;
    var contactCompanyID = undefined;
    var fullName = undefined;
    var sortName = undefined;
    var organizationID = undefined;
    var organizationAbbrOrLegalName = undefined;
    var parentCompanyAbbrOrLegalName = undefined;
    var firstNameSpaceLastNameLower = undefined;
    var lastNameLower = undefined;
    var titleLower = undefined;
    var parentCompanyAbbrOrLegalNameLower = undefined;
    var parentCompanyIsOrganizationTF = undefined;

    if(contactPersonExistsTF) {
      firstName = i_contactPersonMap.get("first_name");
      middleName = i_contactPersonMap.get("middle_name");
      lastName = i_contactPersonMap.get("last_name");
      title = i_contactPersonMap.get("title");
      email =i_contactPersonMap.get("email");
      phone = i_contactPersonMap.get("phone");
      personAddress1 = JSFUNC.prop_value(i_contactPersonMap.get("person_address1"), "");
      personAddress2 = JSFUNC.prop_value(i_contactPersonMap.get("person_address2"), "");
      personCity = JSFUNC.prop_value(i_contactPersonMap.get("person_city"), "");
      personState = JSFUNC.prop_value(i_contactPersonMap.get("person_state"), "");
      personZip = JSFUNC.prop_value(i_contactPersonMap.get("person_zip"), "");
      personZip2 = JSFUNC.prop_value(i_contactPersonMap.get("person_zip2"), "");
      personCountry = JSFUNC.prop_value(i_contactPersonMap.get("person_country"), "");
      contactCompanyID = i_contactPersonMap.get("contact_company_id");

      //check if person's contact company ID matches to an existing valid contact company, otherwise, overwrite it with a -1 to indicate that this person is unassigned
      const precomputedCompanyObj = (i_contactCompaniesPrecomputedInfoObj !== undefined) ? (i_contactCompaniesPrecomputedInfoObj.companyPrecomputedMap.get(contactCompanyID)) : (undefined);

      if(precomputedCompanyObj === undefined && !JSFUNC.in_array(contactCompanyID, c_allCompanyIDsArray)) {
        contactCompanyID = -1;
      }

      var firstNameSpaceLastName = JSFUNC.full_name_from_first_name_last_name(firstName, lastName);
      if(JSFUNC.text_or_number_is_filled_out_tf(middleName)) {
        firstNameSpaceLastName = [firstName, middleName, lastName].filter((i_namePart) => JSFUNC.text_or_number_is_filled_out_tf(i_namePart)).join(" ");
      }
      const lastNameFirstName = lastName + firstName + ((middleName === undefined) ? ("") : (middleName));

      fullName = firstNameSpaceLastName;
      sortName = JSFUNC.str2lower(lastNameFirstName);
      firstNameSpaceLastNameLower = JSFUNC.str2lower(firstNameSpaceLastName);
      lastNameLower = JSFUNC.str2lower(lastName);
      titleLower = JSFUNC.str2lower(title);

      if(precomputedCompanyObj !== undefined) {
        organizationID = precomputedCompanyObj.organizationID;
        organizationAbbrOrLegalName = precomputedCompanyObj.organizationAbbrOrLegalName;
        parentCompanyAbbrOrLegalName = precomputedCompanyObj.abbrOrLegalName;
        parentCompanyAbbrOrLegalNameLower = precomputedCompanyObj.divNameParenOrgNameLower;
        parentCompanyIsOrganizationTF = precomputedCompanyObj.isTopLevelCompanyTF;
      }
      else {
        const contactCompanyObj = this.contact_company_obj_from_id(contactCompanyID, false);

        organizationID = contactCompanyObj.organizationID;
        organizationAbbrOrLegalName = contactCompanyObj.organizationAbbrOrLegalName;
        parentCompanyAbbrOrLegalName = contactCompanyObj.abbrOrLegalName;
        parentCompanyAbbrOrLegalNameLower = contactCompanyObj.divNameParenOrgNameLower;
        parentCompanyIsOrganizationTF = contactCompanyObj.isTopLevelCompanyTF;
      }
    }
    else {
      if(JSFUNC.select_int_is_filled_out_tf(i_contactPersonID)) {
        doesNotExistString = "--Contact Person Does Not Exist (ID: " + i_contactPersonID + ")--";
      }
      else {
        doesNotExistString = "--No Contact Person Selected--";
      }

      firstName = doesNotExistString;
      middleName = doesNotExistString;
      lastName = doesNotExistString;
      title = doesNotExistString;
      email = doesNotExistString;
      phone = doesNotExistString;
      personAddress1 = doesNotExistString;
      personAddress2 = doesNotExistString;
      personCity = doesNotExistString;
      personState = doesNotExistString;
      personZip = doesNotExistString;
      personZip2 = doesNotExistString;
      personCountry = doesNotExistString;
      contactCompanyID = -1;
      fullName = doesNotExistString;
      sortName = doesNotExistString;
      firstNameSpaceLastNameLower = doesNotExistString;
      lastNameLower = doesNotExistString;
      titleLower = doesNotExistString;
      organizationID = -1;
      organizationAbbrOrLegalName = doesNotExistString;
      parentCompanyAbbrOrLegalName = doesNotExistString;
      parentCompanyAbbrOrLegalNameLower = doesNotExistString;
      parentCompanyIsOrganizationTF = true;
    }

    var contactPersonObj = {
      existsTF: contactPersonExistsTF,
      id: i_contactPersonID,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      title: title,
      email: email,
      phone: phone,
      person_address1: personAddress1,
      person_address2: personAddress2,
      person_city: personCity,
      person_state: personState,
      person_zip: personZip,
      person_zip2: personZip2,
      person_country: personCountry,
      contact_company_id: contactCompanyID,
      isPersonTF: true,
      fullName: fullName,
      sortName: sortName,
      firstNameSpaceLastNameLower: firstNameSpaceLastNameLower,
      lastNameLower: lastNameLower,
      titleLower: titleLower,
      organizationID: organizationID,
      organizationAbbrOrLegalName: organizationAbbrOrLegalName,
      parentCompanyAbbrOrLegalName: parentCompanyAbbrOrLegalName,
      parentCompanyAbbrOrLegalNameLower: parentCompanyAbbrOrLegalNameLower,
      parentCompanyIsOrganizationTF: parentCompanyIsOrganizationTF
    };

    for(let hardcodedContactPersonFieldObj of DatabaseMobx.c_hardcodedContactPersonFieldsArrayOfObjs) {
      const fieldDbName = hardcodedContactPersonFieldObj.db_name;
      if((fieldDbName !== undefined) && (fieldDbName !== "id") && (contactPersonObj[fieldDbName] === undefined)) {
        contactPersonObj[fieldDbName] = ((contactPersonExistsTF) ? (JSFUNC.prop_value(i_contactPersonMap.get(fieldDbName), "")) : (doesNotExistString));
      }
    }

    //add extra custom contact person fields created by the admin
    for(let contactsPersonsFieldObj of DatabaseMobx.c_contactsPersonsExtraFieldsArrayOfObjs) { //loop through each extra field (all are text type inputs)
      contactPersonObj[contactsPersonsFieldObj.db_name] = ((contactPersonExistsTF) ? (i_contactPersonMap.get(contactsPersonsFieldObj.db_name)) : (doesNotExistString));
    }

    return(contactPersonObj);
  }

  contact_company_obj_from_id(i_contactCompanyID, i_subdivsAndPersonsTF=false) {
    const o_tbl_g_contacts_companies = DatabaseMobx.o_tbl_g_contacts_companies;

    const contactCompanyMap = o_tbl_g_contacts_companies.get(i_contactCompanyID);
    const precomputedCompanyObj = this.c_contactCompaniesPrecomputedInfoObj.companyPrecomputedMap.get(i_contactCompanyID);
    const contactCompanyObj = this.contact_company_obj_from_tbl_g_contacts_companies_row_map(contactCompanyMap, i_subdivsAndPersonsTF, i_contactCompanyID, precomputedCompanyObj);
    return(contactCompanyObj);
  }

  contact_company_obj_from_tbl_g_contacts_companies_row_map(i_contactCompanyMap, i_subdivsAndPersonsTF=false, i_contactCompanyID=undefined, i_precomputedCompanyObj=undefined) {
    const o_tbl_g_contacts_companies = DatabaseMobx.o_tbl_g_contacts_companies;

    const contactCompanyExistsTF = (i_contactCompanyMap !== undefined);

    var doesNotExistString = undefined;

    var contactCompanyID = undefined;
    var orgTopDivID = undefined;
    var treeID = undefined;
    var legalName = undefined;
    var abbrName = undefined;
    var businessTypeID = undefined;
    var sbCertificationsBmSetAsideIDsComma = undefined;
    var capabilityIDsComma = undefined;
    var naicsCodeIDsComma = undefined;
    var samUei = undefined;
    var dunsNumber = undefined;
    var cageCode = undefined;
    var samEntityStructureID = undefined;
    var address1 = undefined;
    var address2 = undefined;
    var city = undefined;
    var state = undefined;
    var zip = undefined;
    var zip2 = undefined;
    var country = undefined;
    var webAddress = undefined;
    var isTopLevelCompanyTF = undefined;
    var organizationID = undefined;
    var organizationAbbrOrLegalName = undefined;
    var legalNameLower = undefined;
    var abbrNameLower = undefined;
    var abbrOrLegalName = undefined;
    var divNameParenOrgNameLower = undefined;
    var numOrgDivisions = undefined;
    var numPersons = undefined;
    var businessTypeIsLargeTF = undefined;
    var businessTypeIsSmallTF = undefined;
    var businessTypeIsInvalidTF = undefined;
    var teammateRatingTotalRating = undefined;
    var teammateRatingNumReviews = undefined;

    if(contactCompanyExistsTF) {
      //load existing db fields ot use in new calculated fields
      contactCompanyID = i_contactCompanyMap.get("id");
      orgTopDivID = i_contactCompanyMap.get("org_topdiv_id"); //all subdivisions of an organization have and org_topdiv_id value equal to the top organization id number, if this value is set to -1, it is a flag that this division is the top level of the organization
      treeID = i_contactCompanyMap.get("tree_id");
      legalName = i_contactCompanyMap.get("legal_name");
      abbrName = i_contactCompanyMap.get("abbreviated_name");
      businessTypeID = i_contactCompanyMap.get("business_type_id");
      sbCertificationsBmSetAsideIDsComma = i_contactCompanyMap.get("sb_certifications_bm_set_aside_ids_comma");
      capabilityIDsComma = i_contactCompanyMap.get("capability_ids_comma");
      naicsCodeIDsComma = i_contactCompanyMap.get("naics_code_ids_comma");
      samUei = i_contactCompanyMap.get("sam_unique_entity_id");
      dunsNumber = i_contactCompanyMap.get("duns_number");
      cageCode = i_contactCompanyMap.get("cage_code");
      samEntityStructureID = i_contactCompanyMap.get("sam_entity_structure_id");
      address1 = i_contactCompanyMap.get("address1");
      address2 = i_contactCompanyMap.get("address2");
      city = i_contactCompanyMap.get("city");
      state = i_contactCompanyMap.get("state");
      zip = i_contactCompanyMap.get("zip");
      zip2 = i_contactCompanyMap.get("zip2");
      country = i_contactCompanyMap.get("country");
      webAddress = i_contactCompanyMap.get("web_address");
      teammateRatingTotalRating = i_contactCompanyMap.get("tr_total");
      teammateRatingNumReviews = i_contactCompanyMap.get("tr_num_reviews");

      if(i_precomputedCompanyObj !== undefined) {
        orgTopDivID = i_precomputedCompanyObj.orgTopDivID;
        organizationID = i_precomputedCompanyObj.organizationID;
        organizationAbbrOrLegalName = i_precomputedCompanyObj.organizationAbbrOrLegalName;
        legalNameLower = i_precomputedCompanyObj.legalNameLower;
        abbrNameLower = i_precomputedCompanyObj.abbrNameLower;
        abbrOrLegalName = i_precomputedCompanyObj.abbrOrLegalName;
        isTopLevelCompanyTF = i_precomputedCompanyObj.isTopLevelCompanyTF;
        divNameParenOrgNameLower = i_precomputedCompanyObj.divNameParenOrgNameLower;
        numOrgDivisions = (i_subdivsAndPersonsTF) ? (i_precomputedCompanyObj.numOrgDivisions) : (-1);
        numPersons = (i_subdivsAndPersonsTF) ? (i_precomputedCompanyObj.numPersons) : (-1);
      }
      else {
        //get the lowercase versions of abbr/legal name for searches
        legalNameLower = JSFUNC.str2lower(legalName, "--Legal Name not a Contact Field--");
        abbrNameLower = JSFUNC.str2lower(abbrName, "--Abbr Name not a Contact Field--");

        //full name for a contact company, the abbreviated name takes priority over the legal name if filled out
        abbrOrLegalName = this.contact_company_abbr_name_or_legal_name_from_abbr_name_and_legal_name(abbrName, legalName);

        //this company is a top level company (an "organization") where org_topdiv_id is -1
        isTopLevelCompanyTF = (orgTopDivID <= 0);

        //if this is a subdivision of an org, check that the parent company id exists, otherwise force it to be an org so that it's visible
        if(!isTopLevelCompanyTF) {
          if(!o_tbl_g_contacts_companies.has(orgTopDivID)) {
            const parentCompanyDoesNotExistMessage = " {--Parent Company [ID: " + orgTopDivID + "] Does Not Exist--}";
            abbrOrLegalName = abbrOrLegalName + parentCompanyDoesNotExistMessage;
            orgTopDivID = -1;
            isTopLevelCompanyTF = true;
          }
        }

        organizationID = ((isTopLevelCompanyTF) ? (contactCompanyID) : (orgTopDivID));

        //get name of this div's top level organization (if this is an org, the name is the same as its own name)
        var divNameParenOrgName = JSFUNC.full_name_from_first_name_last_name(abbrName, legalName); //"Org Abbr Name Org Legal Name" or "Div Abbr Name Div Legal Name (Org Abbr Name Org Legal Name)"
        if(!isTopLevelCompanyTF) {
          const organizationMap = DatabaseMobx.tbl_row_map_from_id("tbl_g_contacts_companies", organizationID, "o");
          const orgLegalName = organizationMap.get("legal_name");
          const orgAbbrName = organizationMap.get("abbreviated_name");
          organizationAbbrOrLegalName = this.contact_company_abbr_name_or_legal_name_from_abbr_name_and_legal_name(orgAbbrName, orgLegalName);
          divNameParenOrgName += " (" + JSFUNC.full_name_from_first_name_last_name(orgAbbrName, orgLegalName) + ")";
        }
        else {
          organizationAbbrOrLegalName = abbrOrLegalName;
        }
        divNameParenOrgNameLower = divNameParenOrgName.toLowerCase();

        //person/division counts only calculated for top organization records
        numOrgDivisions = -1;
        numPersons = -1;
        if(i_subdivsAndPersonsTF) {
          if(isTopLevelCompanyTF) { //top org calculated only
            numOrgDivisions = 0;
            var orgCompanyIDsArray = [contactCompanyID]; //all divisionIDs under the org, also include the orgID
            for(let contactCompanyMap of DatabaseMobx.o_tbl_g_contacts_companies.values()) {
              if(contactCompanyMap.get("org_topdiv_id") === contactCompanyID) {
                numOrgDivisions++;
                orgCompanyIDsArray.push(contactCompanyMap.get("id"));
              }
            }

            numPersons = 0;
            for(let contactPersonMap of DatabaseMobx.o_tbl_g_contacts_persons.values()) {
              if(JSFUNC.in_array(contactPersonMap.get("contact_company_id"), orgCompanyIDsArray)) {
                numPersons++;
              }
            }
          }
        }
      }

      //determine if business type is a small business type from business_type_id
      var smallLargeInvalidBusinessTypeTFObj = DatabaseMobx.determine_small_large_invalid_business_type_tf_obj_from_business_type_id(businessTypeID);
      businessTypeIsLargeTF = smallLargeInvalidBusinessTypeTFObj.businessTypeIsLargeTF;
      businessTypeIsSmallTF = smallLargeInvalidBusinessTypeTFObj.businessTypeIsSmallTF;
      businessTypeIsInvalidTF = smallLargeInvalidBusinessTypeTFObj.businessTypeIsInvalidTF;
    }
    else {
      if(JSFUNC.select_int_is_filled_out_tf(i_contactCompanyID)) {
        doesNotExistString = "--Contact Company Does Not Exist (ID: " + i_contactCompanyID + ")--";
      }
      else {
        doesNotExistString = "--No Contact Company Selected--";
      }

      orgTopDivID = -1;
      treeID = "00";
      legalName = doesNotExistString;
      abbrName = doesNotExistString;
      businessTypeID = -1;
      sbCertificationsBmSetAsideIDsComma = "";
      capabilityIDsComma = "";
      naicsCodeIDsComma = "";
      samUei = "";
      dunsNumber = "";
      cageCode = "";
      samEntityStructureID = -1;
      address1 = doesNotExistString;
      address2 = doesNotExistString;
      city = doesNotExistString;
      state = doesNotExistString;
      zip = doesNotExistString;
      zip2 = doesNotExistString;
      country = doesNotExistString;
      webAddress = doesNotExistString;
      isTopLevelCompanyTF = true;
      organizationID = -1;
      organizationAbbrOrLegalName = doesNotExistString;
      legalNameLower = doesNotExistString;
      abbrNameLower = doesNotExistString;
      abbrOrLegalName = doesNotExistString;
      divNameParenOrgNameLower = doesNotExistString;
      numOrgDivisions = -1;
      numPersons = -1;
      businessTypeIsLargeTF = false;
      businessTypeIsSmallTF = false;
      businessTypeIsInvalidTF = true;
      teammateRatingTotalRating = -1;
      teammateRatingNumReviews = 0;
    }

    var contactCompanyObj = {
      existsTF: contactCompanyExistsTF,
      id: contactCompanyID,
      org_topdiv_id: orgTopDivID,
      tree_id: treeID,
      legal_name: legalName,
      abbreviated_name: abbrName,
      business_type_id: businessTypeID,
      sb_certifications_bm_set_aside_ids_comma: sbCertificationsBmSetAsideIDsComma,
      capability_ids_comma: capabilityIDsComma,
      naics_code_ids_comma: naicsCodeIDsComma,
      sam_unique_entity_id: samUei,
      duns_number: dunsNumber,
      cage_code: cageCode,
      sam_entity_structure_id: samEntityStructureID,
      address1: address1,
      address2: address2,
      city: city,
      state: state,
      zip: zip,
      zip2: zip2,
      country: country,
      web_address: webAddress,
      isPersonTF: false,
      isTopLevelCompanyTF: isTopLevelCompanyTF,
      organizationID: organizationID,
      organizationAbbrOrLegalName: organizationAbbrOrLegalName,
      legalNameLower: legalNameLower,
      abbrNameLower: abbrNameLower,
      abbrOrLegalName: abbrOrLegalName,
      divNameParenOrgNameLower: divNameParenOrgNameLower,
      numOrgDivisions: numOrgDivisions,
      numPersons: numPersons,
      businessTypeIsLargeTF: businessTypeIsLargeTF,
      businessTypeIsSmallTF: businessTypeIsSmallTF,
      businessTypeIsInvalidTF: businessTypeIsInvalidTF,
      tr_total: teammateRatingTotalRating,
      tr_num_reviews: teammateRatingNumReviews
    };

    //add individual teammate rating question average ratings
    for(let teammatesRatingsQuestionObj of DatabaseMobx.c_teammatesRatingsQuestionsArrayOfObjs) {
      var questionRatingColumnDbName = "tr_q" + teammatesRatingsQuestionObj.id;
      contactCompanyObj[questionRatingColumnDbName] = ((contactCompanyExistsTF) ? (i_contactCompanyMap.get(questionRatingColumnDbName)) : (undefined));
    }

    //add extra custom contact person fields created by the admin
    for(let contactsCompaniesFieldObj of DatabaseMobx.c_contactsCompaniesExtraFieldsArrayOfObjs) { //loop through each extra field (all are text type inputs)
      contactCompanyObj[contactsCompaniesFieldObj.db_name] = ((contactCompanyExistsTF) ? (i_contactCompanyMap.get(contactsCompaniesFieldObj.db_name)) : (doesNotExistString));
    }

    return(contactCompanyObj);
  }

  contact_name_from_id(i_isPersonTF, i_contactCompanyOrPersonID) {
    const contactObj = this.contact_company_or_person_obj_from_id(i_isPersonTF, i_contactCompanyOrPersonID);
    return(this.contact_name_plaintext_from_contact_obj(contactObj));
  }

  contact_name_plaintext_from_contact_obj(i_contactObj) {
    if(i_contactObj === undefined) {
      return("--Invalid Contact Obj--");
    }
    const contactNameField = this.contact_name_field(i_contactObj.isPersonTF);
    return(i_contactObj[contactNameField]);
  }

  contact_name_field(i_isPersonTF) { //contact name is constructed in DatabaseMobx when building the extra fields into the contacts raw data maps
    return((i_isPersonTF) ? ("fullName") : ("abbrOrLegalName"));
  }

  contact_company_has_valid_business_type_tf_from_contact_company_id(i_contactCompanyID) {
    const subdivsAndPersonsTF = false;
    const contactCompanyObj = this.contact_company_obj_from_id(i_contactCompanyID, subdivsAndPersonsTF);
    return(!contactCompanyObj.businessTypeIsInvalidTF);
  }

  contact_person_email_from_contact_person_obj(i_contactPersonObj) {
    if((i_contactPersonObj === undefined) || (!i_contactPersonObj.isPersonTF)) {
      return("--Invalid Contact Person Obj--");
    }
    return(i_contactPersonObj.email);
  }

  parent_company_field_db_name(i_isPersonTF) {
    return((i_isPersonTF) ? ("contact_company_id") : ("org_topdiv_id"));
  }

  is_person_and_is_multiselect_tfu_from_select_mode(i_selectMode) { //returns [isSelectingPersonsTFU, isMultiSelectTFU]
    if(i_selectMode === "select_contact_person") { return([true, false]); }
    if(i_selectMode === "select_contact_company") { return([false, false]); }
    if(i_selectMode === "selectmulti_contact_persons") { return([true, true]); }
    if(i_selectMode === "selectmulti_contact_companies") { return([false, true]); }
    return([undefined, undefined]); //no_select or invalid options
  }

  person_or_company_string_from_is_person_and_is_multi_tf(i_isPersonTF, i_isMultiSelectTF) {
    if(i_isPersonTF === undefined || i_isMultiSelectTF === undefined) {
      return("NoSelect");
    }

    if(i_isMultiSelectTF) {
      return((i_isPersonTF) ? ("Persons") : ("Companies"));
    }
    return((i_isPersonTF) ? ("Person") : ("Company"));
  }

  contacts_tbl_name_from_is_person_tf(i_isPersonTF) {
    if(i_isPersonTF === undefined) {
      return("no_select");
    }
    return((i_isPersonTF) ? ("tbl_g_contacts_persons") : ("tbl_g_contacts_companies"));
  }

  contact_fields_arrayOfObjs(i_isPersonTF, i_fieldDbNamesToNotIncludeArray=undefined) {
    if(i_isPersonTF === undefined) {
      return([]);
    }

    const contactFieldsArrayOfObjs = (i_isPersonTF) ? (this.c_personFieldsArrayOfObjs) : (this.c_companyFieldsArrayOfObjs);
    if(!JSFUNC.is_array(i_fieldDbNamesToNotIncludeArray)) {
      return(contactFieldsArrayOfObjs); //all fields (from persons or companies)
    }

    //don't include specified fields that match db_name in i_fieldDbNamesToNotIncludeArray
    var modifiedContactFieldsArrayOfObjs = [];
    for(let contactFieldObj of contactFieldsArrayOfObjs) {
      if(!JSFUNC.in_array(contactFieldObj.db_name, i_fieldDbNamesToNotIncludeArray)) {
        modifiedContactFieldsArrayOfObjs.push(contactFieldObj);
      }
    }
    return(modifiedContactFieldsArrayOfObjs);
  }

  contact_company_abbr_name_or_legal_name_from_abbr_name_and_legal_name(i_abbrName, i_legalName) {
    if(JSFUNC.text_or_number_is_filled_out_tf(i_abbrName)) {
      return(i_abbrName);
    }
    else if(JSFUNC.text_or_number_is_filled_out_tf(i_legalName)) {
      return(i_legalName);
    }
    return("--Company With No Legal or Abbrivated Names Given--");
  }

  contact_company_legal_name_paren_abbr_name_from_abbr_name_and_legal_name(i_abbrName, i_legalName) {
    const abbrNameIsFilledOutTF = JSFUNC.text_or_number_is_filled_out_tf(i_abbrName);
    const legalNameIsFilledOutTF = JSFUNC.text_or_number_is_filled_out_tf(i_legalName);
    if(abbrNameIsFilledOutTF && legalNameIsFilledOutTF) {
      if(i_abbrName === i_legalName) { //abbr and legal names are identical, only show 1
        return(i_legalName);
      }
      return(i_legalName + " (" + i_abbrName + ")");
    }
    else if(abbrNameIsFilledOutTF) {
      return(i_abbrName);
    }
    else if(legalNameIsFilledOutTF) {
      return(i_legalName);
    }
    return("--Company With No Legal or Abbrivated Names Given--");
  }

  all_companies_within_org_id_arrayOfObjs(i_orgCompanyID) {
    if(i_orgCompanyID <= 0) {
      return([]);
    }

    const filterFunction = (i_companyObj) => { return(i_companyObj.org_topdiv_id === i_orgCompanyID); };
    const sortFunction = JSFUNC.sort_by_asc("tree_id");
    return(this.c_allCompaniesArrayOfObjs.filter(filterFunction).sort(sortFunction));
  }

  num_divisions_under_contact_company_obj(i_contactCompanyObj) {
    const allDivisionsInOrgArrayOfObjs = this.all_companies_within_org_id_arrayOfObjs(i_contactCompanyObj.organizationID);
    if(i_contactCompanyObj.isTopLevelCompanyTF) {
      return(allDivisionsInOrgArrayOfObjs.length);
    }
    return(allDivisionsInOrgArrayOfObjs.filter((i_divisionObj) => JSFUNC.tree_id_is_child_of_parent_tree_id_tf(i_contactCompanyObj.tree_id, i_divisionObj.tree_id, false)).length);
  }

  all_persons_within_company_id_arrayOfObjs(i_companyID) {
    if(JSFUNC.is_number(i_companyID)) {
      if(i_companyID > 0) {
        const filterFunction = (i_personObj) => { return(i_personObj.contact_company_id === i_companyID); };
        const sortFunction = JSFUNC.sort_by_asc("sortName");
        return(this.c_allPersonsArrayOfObjs.filter(filterFunction).sort(sortFunction));
      }

      if(i_companyID === -2) { //Persons Created from GCSS Import special company grouping
        return(this.c_createdByGcssImportContactPersonsArrayOfObjs)
      }
    }

    //Unassigned Persons special company grouping (unassigned persons usually have a parent companyID of -1 or 0)
    return(this.c_unassignedPersonsArrayOfObjs);
  }

  all_expanded_companies_within_org_tree_id_array(i_orgCompanyID) {
    if(i_orgCompanyID <= 0) {
      return([]);
    }

    const filterFunction = (i_companyObj) => { return((i_companyObj.org_topdiv_id === i_orgCompanyID) && this.company_item_is_expanded_tf(i_companyObj.id)); };
    const allExpandedCompaniesWithinOrgArrayOfObjs = this.c_allCompaniesArrayOfObjs.filter(filterFunction);
    return(JSFUNC.get_column_vector_from_arrayOfObjs(allExpandedCompaniesWithinOrgArrayOfObjs, "tree_id"));
  }

  parent_company_id_from_company_obj(i_childCompanyObj) {
    if(i_childCompanyObj.org_topdiv_id <= 0) { //org_topdiv_id of -1 is a flag that this division is a top organization, so its parent companyID is -1
      return(-1);
    }

    const childTreeID = i_childCompanyObj.tree_id;
    const childTreeIDLength = childTreeID.length;
    const childOrganizationID = i_childCompanyObj.org_topdiv_id;
    if(childTreeIDLength <= 2) { //treeID length 2 like "01" or "02", the parent division is the organization it is a part of, which has already been precomputed as the organizationID
      return(childOrganizationID);
    }

    const parentTreeID = childTreeID.substring(0, childTreeIDLength - 2);
    for(let companyObj of this.c_allCompaniesArrayOfObjs) {
      if(companyObj.organizationID === childOrganizationID && companyObj.tree_id === parentTreeID) {
        return(companyObj.id);
      }
    }
    return(0); //child does not have a direct parent flag
  }

  contact_is_selected_tf(i_contactID) {
    return(JSFUNC.in_array(i_contactID, this.o_currentSelectedContactIDsArray));
  }

  company_item_is_expanded_tf(i_companyID) {
    return(JSFUNC.in_array(i_companyID, this.o_orgsExpandedCompanyIDsArray));
  }

  contact_is_top_org_tf(i_contactObj) {
    if(i_contactObj === undefined) {
      return(false);
    }
    return(!i_contactObj.isPersonTF && (i_contactObj.org_topdiv_id <= 0));
  }

  get_changed_output_selected_value_or_unchanged_undefined(i_selectMode) {
    //current selected contacts are the same as they were initially, return undefined to signal a cancel operation without saving new data
    if(JSFUNC.array1_equals_array2_tf(this.o_initialSelectedContactIDsArray, this.o_currentSelectedContactIDsArray)) {
      return(undefined);
    }

    //convert the selectedContactIDsArray into the output format required based on the selectMode (single int for select, comma list string for selectmulti)
    const [isSelectingPersonsTFU, isMultiSelectTFU] = this.is_person_and_is_multiselect_tfu_from_select_mode(i_selectMode);

    //string comma list of contactIDs for multiselect
    if(isMultiSelectTFU) {
      return(JSFUNC.convert_array_to_comma_list(this.o_currentSelectedContactIDsArray));
    }

    //single int contactID for single selects
    const numSelectedContacts = this.o_currentSelectedContactIDsArray.length;
    if(numSelectedContacts >= 1) { //if somehow more than 1 contact was selected (should be impossible), only return the first contactID selected, otherwise return the single contact selected as intended
      return(this.o_currentSelectedContactIDsArray[0]);
    }
    return(-1); //if no items were selected, return -1 as a flag for input selects that no items were selected
  }



  //searching the contacts system for a matching abbr/legal name
  find_first_lowecase_exact_match_contact_company_id_or_undefined_from_name_string(i_legalOrAbbrNameToSearch) {
    //if the input is not a string, return no match
    if(!JSFUNC.is_string(i_legalOrAbbrNameToSearch)) {
      return(undefined);
    }

    //if the input is an empty string, return no match
    if(i_legalOrAbbrNameToSearch === "") {
      return(undefined);
    }

    //convert the input name to lowercase
    const legalOrAbbrNameToSearchLower = i_legalOrAbbrNameToSearch.toLowerCase();

    //compare the input name against every contact legal and abbr name (lowercase)
    for(let companyObj of this.c_allCompaniesArrayOfObjs) {
      if((legalOrAbbrNameToSearchLower === companyObj.legalNameLower) || (legalOrAbbrNameToSearchLower === companyObj.abbrNameLower)) {
        return(companyObj.id);
      }
    }

    //search did not match any contact company names in the contacts system, return undefined
    return(undefined);
  }

  find_multiple_lowercase_exact_match_contact_company_ids_comma_or_undefined_from_comma_list_of_names_string(i_legalOrAbbrNamesToSearchComma) {
    //if the input is not a string, return no match
    if(!JSFUNC.is_string(i_legalOrAbbrNamesToSearchComma)) {
      return(undefined);
    }

    //if the input string is blank, return no match
    if(i_legalOrAbbrNamesToSearchComma === "") {
      return(undefined);
    }

    //convert the input name to lowercase
    var legalOrAbbrNamesToSearchCommaLower = i_legalOrAbbrNamesToSearchComma.toLowerCase();

    //replace any ", inc" or ", llc" with ZZZZZ so that the comma is not mistaken for commas separating company names
    legalOrAbbrNamesToSearchCommaLower = legalOrAbbrNamesToSearchCommaLower.replace(", inc", "ZZZZZ inc");
    legalOrAbbrNamesToSearchCommaLower = legalOrAbbrNamesToSearchCommaLower.replace(",inc", "ZZZZZinc");
    legalOrAbbrNamesToSearchCommaLower = legalOrAbbrNamesToSearchCommaLower.replace(", llc", "ZZZZZ llc");
    legalOrAbbrNamesToSearchCommaLower = legalOrAbbrNamesToSearchCommaLower.replace(",llc", "ZZZZZllc");

    //break the comma list into multiple company names
    var companyNamesWithZsArray = JSFUNC.convert_comma_list_with_or_without_spaces_to_array_blank_entries_removed(legalOrAbbrNamesToSearchCommaLower);

    //for each company name identified in the input comma list, search for it within the contact company records to return an id number
    var matchingContactCompanyIDsComma = "";
    for(let companyNameWithZs of companyNamesWithZsArray) {
      var companyName = companyNameWithZs.replace("ZZZZZ", ","); //convert back any ZZZZZ into a single comma
      var matchingContactCompanyID = this.find_first_lowecase_exact_match_contact_company_id_or_undefined_from_name_string(companyName);
      if(matchingContactCompanyID !== undefined) {
        if(matchingContactCompanyIDsComma !== "") { matchingContactCompanyIDsComma += ","; }
        matchingContactCompanyIDsComma += matchingContactCompanyID;
      }
    }

    if(matchingContactCompanyIDsComma === "") {
      return(undefined);
    }

    return(matchingContactCompanyIDsComma);
  }

}
export default new ContactsMobx();
